import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Drawer,
  Empty,
  Input,
  InputNumber,
  List,
  Modal,
  message,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import dayjs from "dayjs";
import {
  BankOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  SyncOutlined,
  TeamOutlined,
  PrinterOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import { CareShell } from "components/care/CareLayout";
import PaystubDetailsCard from "components/care/payroll/PaystubDetailsCard";
import { printPaystubDocument } from "components/care/payroll/paystubPrint";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import { careService } from "services/careService";
import olreadiaService from "services/olreadiaService";
import paymentMethodsService from "services/paymentMethodsService";
import userPayrollService from "services/userPayrollService";
import workspacePayrollService from "services/workspacePayrollService";
import { useFileUpload } from "hooks/useFileUpload";
import { readable } from "workspaces/workspaceUtils";
import { OLIVEEB_COLORS, workspaceStyles } from "workspaces/colors";

const { Text } = Typography;
const { RangePicker } = DatePicker;
const PAYROLL_TAB_KEYS = ["overview", "approved-timesheets", "issued-payments", "pay-staff", "payment-schedules", "wa-lni", "paysturbs", "earnings", "deductions", "tax-remittances", "attention"];
const DEFAULT_PAYROLL_TAB = "overview";
const ALL_STAFF_ID = "__all_staff__";
const ALL_TAX_REMITTANCES_CODE = "__all_tax_remittances__";
const MANUAL_PAYROLL_PAYMENT_METHODS = [
  { value: "CASH", label: "Cash" },
  { value: "CHEQUE", label: "Cheque" },
  { value: "WIRE", label: "Wire" },
  { value: "OTHER", label: "Other" },
];
const PAYROLL_PROOF_MAX_FILE_SIZE_MB = 10;
const PAYROLL_PROOF_MAX_FILE_SIZE_BYTES = PAYROLL_PROOF_MAX_FILE_SIZE_MB * 1024 * 1024;
const PAYROLL_TAX_REMITTANCE_TYPES = [
  {
    code: "FEDERAL_INCOME_TAX",
    label: "Federal income tax",
    agency: "IRS",
    due: "Deposit schedule depends on IRS monthly, semiweekly, or next-day rules.",
    matches: ["FEDERAL INCOME TAX", "FEDERAL_INCOME_TAX", "FIT"],
  },
  {
    code: "SOCIAL_SECURITY",
    label: "Social Security",
    agency: "IRS",
    due: "Deposited with federal payroll taxes.",
    matches: ["SOCIAL SECURITY", "SOCIAL_SECURITY", "FICA"],
  },
  {
    code: "MEDICARE",
    label: "Medicare",
    agency: "IRS",
    due: "Deposited with federal payroll taxes.",
    matches: ["MEDICARE"],
  },
  {
    code: "WA_PFML_EE",
    label: "WA Paid Family and Medical Leave EE",
    agency: "Washington ESD",
    due: "Reported and paid quarterly.",
    matches: ["WA PAID FAMILY", "MEDICAL LEAVE", "PFML", "WA_PFML_EE"],
  },
  {
    code: "WA_WORKERS_COMP_EE",
    label: "WA Workers' Compensation Tax",
    agency: "Washington L&I",
    due: "Reported and paid quarterly with L&I reporting.",
    matches: ["WORKERS' COMPENSATION", "WORKERS COMPENSATION", "WORKERS", "COMPENSATION", "L&I", "LNI", "WA_WORKERS_COMP_EE"],
  },
  {
    code: "WA_CARES_FUND",
    label: "WA Cares Fund",
    agency: "Washington ESD",
    due: "Reported and paid quarterly.",
    matches: ["WA CARES", "WA_CARES"],
  },
];
const payrollScheduleOptions = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "BIWEEKLY", label: "Bi-Weekly" },
  { value: "SEMIMONTHLY", label: "Semi-Monthly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "QUARTERLY", label: "Quarterly" },
  { value: "SEMIANNUALLY", label: "Semi-Annually" },
  { value: "ANNUALLY", label: "Annually" },
];

const FieldInfoLabel = ({ children, info }) => (
  <span className="payroll-field-info-label">
    <span>{children}</span>
    <Tooltip title={info} placement="topLeft">
      <InfoCircleOutlined aria-label={`${children} help`} className="payroll-field-info-icon" />
    </Tooltip>
  </span>
);

const fileTypeForPayrollUpload = (file = {}) =>
  String(file.type || "").toLowerCase().startsWith("image/") ? "PHOTO" : file.type || "DOCUMENT";

const dataUrlToFile = async (dataUrl, filename) => {
  const response = await fetch(dataUrl);
  const blob = await response.blob();
  return new File([blob], filename, {
    type: blob.type || "image/jpeg",
    lastModified: Date.now(),
  });
};

const isWeekend = (date) => [0, 6].includes(date.day());

const previousBusinessDay = (date) => {
  let nextDate = date;
  while (isWeekend(nextDate)) {
    nextDate = nextDate.subtract(1, "day");
  }
  return nextDate;
};

const nextBusinessDay = (date) => {
  let nextDate = date;
  while (isWeekend(nextDate)) {
    nextDate = nextDate.add(1, "day");
  }
  return nextDate;
};

const addBusinessDaysIncludingStart = (date, days) => {
  let nextDate = date;
  let countedDays = isWeekend(nextDate) ? 0 : 1;
  while (countedDays < days) {
    nextDate = nextDate.add(1, "day");
    if (!isWeekend(nextDate)) countedDays += 1;
  }
  return nextDate;
};

const computedFirstPayDate = (frequency, startAt) => {
  if (!startAt) return null;
  const start = dayjs(startAt);
  if (!start.isValid()) return null;
  const midMonthPayDate = start.date(15);
  const monthEndPayDate = start.endOf("month");
  let computedDate;
  switch (frequency) {
    case "DAILY":
      computedDate = start.add(1, "day");
      break;
    case "WEEKLY":
      computedDate = addBusinessDaysIncludingStart(start, 5);
      break;
    case "BIWEEKLY":
      computedDate = addBusinessDaysIncludingStart(start, 10);
      break;
    case "SEMIMONTHLY":
      if (!start.isAfter(midMonthPayDate, "day")) computedDate = midMonthPayDate;
      else if (!start.isAfter(monthEndPayDate, "day")) computedDate = monthEndPayDate;
      else computedDate = start.add(1, "month").date(15);
      break;
    case "MONTHLY":
      computedDate = start.add(1, "month");
      break;
    case "QUARTERLY":
      computedDate = start.add(3, "month");
      break;
    case "SEMIANNUALLY":
      computedDate = start.add(6, "month");
      break;
    case "ANNUALLY":
      computedDate = start.add(1, "year");
      break;
    default:
      computedDate = addBusinessDaysIncludingStart(start, 10);
  }
  return previousBusinessDay(computedDate);
};

const previousScheduledPayDate = (payDate, frequency) => {
  const date = dayjs(payDate);
  if (!date.isValid()) return null;
  let previousDate;
  switch (frequency) {
    case "DAILY":
      previousDate = date.subtract(1, "day");
      break;
    case "WEEKLY":
      previousDate = date.subtract(1, "week");
      break;
    case "BIWEEKLY":
      previousDate = date.subtract(2, "week");
      break;
    case "SEMIMONTHLY":
      previousDate = date.date() <= 15 ? date.subtract(1, "month").endOf("month") : date.date(15);
      break;
    case "MONTHLY":
      previousDate = date.subtract(1, "month");
      break;
    case "QUARTERLY":
      previousDate = date.subtract(3, "month");
      break;
    case "SEMIANNUALLY":
      previousDate = date.subtract(6, "month");
      break;
    case "ANNUALLY":
      previousDate = date.subtract(1, "year");
      break;
    default:
      previousDate = date.subtract(2, "week");
  }
  return previousBusinessDay(previousDate);
};

const nextScheduledPayDate = (payDate, frequency) => {
  const date = dayjs(payDate);
  if (!date.isValid()) return null;
  let nextDate;
  switch (frequency) {
    case "DAILY":
      nextDate = nextBusinessDay(date.add(1, "day"));
      break;
    case "WEEKLY":
      nextDate = date.add(1, "week");
      break;
    case "BIWEEKLY":
      nextDate = date.add(2, "week");
      break;
    case "SEMIMONTHLY":
      nextDate = date.date() <= 15 ? date.endOf("month") : date.add(1, "month").date(15);
      break;
    case "MONTHLY":
      nextDate = date.add(1, "month");
      break;
    case "QUARTERLY":
      nextDate = date.add(3, "month");
      break;
    case "SEMIANNUALLY":
      nextDate = date.add(6, "month");
      break;
    case "ANNUALLY":
      nextDate = date.add(1, "year");
      break;
    default:
      nextDate = date.add(2, "week");
  }
  return previousBusinessDay(nextDate);
};

const payrollPeriodOptionsFromSchedules = (schedules = []) => {
  const optionsByKey = new Map();
  schedules.forEach((schedule) => {
    const frequency = String(schedule.frequency || "BIWEEKLY").toUpperCase();
    let payDate = dayjs(schedule.firstPayDate || computedFirstPayDate(frequency, schedule.startAt));
    if (!payDate.isValid()) return;
    while (payDate.isBefore(dayjs().subtract(12, "month"), "day")) {
      payDate = nextScheduledPayDate(payDate, frequency);
      if (!payDate) return;
    }
    while (payDate.isBefore(dayjs(), "day")) {
      payDate = nextScheduledPayDate(payDate, frequency);
      if (!payDate) return;
    }
    for (let index = 0; index < 10; index += 1) {
      const previousPayDate = previousScheduledPayDate(payDate, frequency);
      if (!previousPayDate) break;
      const periodStart = nextBusinessDay(previousPayDate.add(1, "day")).startOf("day");
      const periodEnd = payDate.endOf("day");
      const key = `${frequency}-${periodStart.format("YYYY-MM-DD")}-${periodEnd.format("YYYY-MM-DD")}`;
      if (!optionsByKey.has(key)) {
        optionsByKey.set(key, {
          value: key,
          label: `${shortDateText(periodStart)} - ${shortDateText(periodEnd)} | Pay date ${shortDateText(payDate)}`,
          frequency,
          start: periodStart,
          end: periodEnd,
          payDate,
          schedule,
        });
      }
      payDate = previousPayDate;
    }
  });
  return [...optionsByKey.values()].sort((a, b) => b.payDate.valueOf() - a.payDate.valueOf());
};

const pageContent = (data) => (Array.isArray(data) ? data : data?.content || data?.items || data?.data || []);
const clean = (value) => String(value || "").trim();
const firstText = (...values) => values.map(clean).find(Boolean) || "";
const objectValue = (value) => (value && typeof value === "object" && !Array.isArray(value) ? value : {});
const arrayValue = (value) => (Array.isArray(value) ? value : []);

const userFor = (profile = {}) => profile.userProfile || profile.user || {};
const baseProfileFor = (profile = {}) => profile.profile || profile.profileData || {};
const profileIdFor = (profile = {}) => profile.id || profile.careProfileId || "";
const userIdFor = (profile = {}) => {
  const user = userFor(profile);
  return user.id || user.userId || profile.userProfileId || profile.userId || profile.caregiverUserId || "";
};
const staffNameFor = (profile = {}) => {
  const user = userFor(profile);
  const base = baseProfileFor(profile);
  return (
    firstText(user.name, profile.name, profile.fullName, profile.displayName) ||
    [base.firstName || profile.firstName || user.firstName, base.middleName || profile.middleName, base.lastName || profile.lastName || user.lastName]
      .map(clean)
      .filter(Boolean)
      .join(" ") ||
    "Care staff"
  );
};
const staffEmailFor = (profile = {}) =>
  firstText(profile.email, profile.caregiverEmail, userFor(profile).email, baseProfileFor(profile).email);

const rowEntries = (row = {}) => row.entries || row.timeEntries || [];
const entryStart = (entry = {}) => entry.startTimestamp || entry.startAt || entry.startDateTime || entry.startTime || "";
const entryEnd = (entry = {}) => entry.endTimestamp || entry.endAt || entry.endDateTime || entry.endTime || "";
const timestampMillis = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
};
const hoursBetween = (start, end) => {
  const startMs = timestampMillis(start);
  const endMs = timestampMillis(end);
  if (startMs === null || endMs === null) return 0;
  return Math.max(0, endMs - startMs) / 3600000;
};
const timesheetHours = (row = {}) => {
  const entries = rowEntries(row);
  if (row.totalHours !== undefined && row.totalHours !== null) return Number(row.totalHours || 0);
  if (row.hours !== undefined && row.hours !== null) return Number(row.hours || 0);
  if (!entries.length) return hoursBetween(row.startTimestamp || row.startAt, row.endTimestamp || row.endAt);
  return entries.reduce((sum, entry) => sum + Number(entry.hours ?? hoursBetween(entryStart(entry), entryEnd(entry))), 0);
};
const formatHours = (value) => {
  const hours = Number(value || 0);
  return `${hours.toFixed(2)} hr${Math.abs(hours - 1) < 0.005 ? "" : "s"}`;
};
const money = (value, currency = "USD") => {
  if (value === undefined || value === null || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD" }).format(number);
};
const dateText = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};
const shortDateText = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "-";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};
const scheduleStartLabel = (value) => (value && dayjs(value).isAfter(dayjs(), "day") ? "Starts" : "Started");
const timesheetDate = (row = {}) => {
  const entries = rowEntries(row);
  return firstText(row.workDate, row.date, row.startTimestamp, row.startAt, row.createdAt, entryStart(entries[0] || {}));
};
const timesheetStart = (row = {}) => {
  const entries = rowEntries(row);
  return firstText(row.startTimestamp, row.startAt, row.startDateTime, row.startTime, entryStart(entries[0] || {}));
};
const timesheetEnd = (row = {}) => {
  const entries = rowEntries(row);
  const lastEntry = entries[entries.length - 1] || {};
  return firstText(row.endTimestamp, row.endAt, row.endDateTime, row.endTime, entryEnd(lastEntry));
};
const timesheetPayCodes = (row = {}) => {
  const codes = [
    row.payCode,
    row.shiftType,
    ...rowEntries(row).flatMap((entry) => [entry.payCode, entry.shiftType]),
  ].map(clean).filter(Boolean);
  return [...new Set(codes)].map((code) => readable(code, "Regular"));
};
const timesheetNotes = (row = {}) =>
  firstText(row.notes, row.description, ...rowEntries(row).map((entry) => entry.notes));
const timesheetReviewReason = (row = {}) =>
  firstText(row.statusReason, row.reason, row.reviewReason, row.reviewNotes);
const paymentAmount = (payment = {}) =>
  payment.netPay ?? payment.payeeAmount ?? payment.chargeableAmount ?? payment.amount ?? payment.netAmount ?? payment.grossPay;
const paymentTitle = (payment = {}) =>
  firstText(
    payment.staffName,
    payment.description,
    payment.paymentDescription,
    payment.chargeItemName,
    readable(payment.chargeItemCode, "Payroll payment"),
  );
const paymentStatusText = (record = {}) =>
  firstText(record.paymentStatus, record.paymentDetails?.paymentStatus, record.paymentDetails?.workspaceChargeStatus, record.status, "Status");
const paymentRecordStatusText = (record = {}) =>
  firstText(record.paymentStatus, record.paymentDetails?.paymentStatus, record.paymentDetails?.workspaceChargeStatus, record.status, "Status");
const latestStripePayoutFor = (record = {}) => arrayValue(record.stripePayouts)[0] || objectValue(record.stripePayout);
const stripeTransferFor = (record = {}) => objectValue(record.stripeTransfer);
const payoutStatusText = (record = {}) =>
  firstText(latestStripePayoutFor(record).status, record.payoutStatus, record.paymentDetails?.payoutStatus, "Pending");
const hasPayoutStatus = (record = {}) =>
  Boolean(firstText(latestStripePayoutFor(record).status, record.payoutStatus, record.paymentDetails?.payoutStatus, stripeTransferFor(record).status));
const payoutTypeText = (record = {}) => {
  const details = objectValue(record.paymentDetails);
  const payout = latestStripePayoutFor(record);
  const payoutMethod = objectValue(record.payoutMethod || details.payoutMethod || record.payeePaymentMethod || details.payeePaymentMethod);
  return firstText(
    payout.source,
    record.payoutType,
    details.payoutType,
    payoutMethod.type,
    payoutMethod.paymentMethodType,
    payoutMethod.accountType,
    payoutMethod.externalAccountType,
    payoutMethod.label,
  );
};
const payoutReferenceText = (record = {}) => {
  const details = objectValue(record.paymentDetails);
  const payout = latestStripePayoutFor(record);
  const transfer = stripeTransferFor(record);
  return firstText(
    payout.stripePayoutId,
    payout.id,
    record.payoutReference,
    details.payoutReference,
    transfer.stripeTransferId,
    transfer.id,
    record.stripeTransferId,
  );
};
const paymentStatusColor = (statusValue) => {
  const status = String(statusValue || "").toUpperCase();
  if (["PAID", "PAYOUT_PAID", "PAYOUT_SUCCEEDED", "SUCCEEDED", "SUCCESS", "COMPLETED"].includes(status)) return "green";
  if (["SCHEDULED", "PAYOUT_SCHEDULED", "PROCESSING", "PAYOUT_PROCESSING", "PENDING", "IN_TRANSIT"].includes(status)) return "gold";
  if (["FAILED", "PAYOUT_FAILED", "CANCELLED", "CANCELED", "REQUIRES_ACTION"].includes(status)) return "red";
  return "blue";
};
const isPaidPaymentStatus = (statusValue) =>
  ["PAID", "SUCCESS", "SUCCESSFUL", "SUCCEEDED", "COMPLETED", "CAPTURED"].includes(String(statusValue || "").toUpperCase());
const isPaidPayoutStatus = (statusValue) =>
  ["PAID", "PAYOUT_PAID", "PAYOUT_SUCCEEDED", "SUCCESS", "SUCCESSFUL", "SUCCEEDED", "COMPLETED"].includes(String(statusValue || "").toUpperCase());
const hasWorkspaceCharge = (record = {}) => {
  const details = objectValue(record.paymentDetails);
  return Boolean(firstText(record.stripePaymentChargeId, details.workspaceChargeId));
};
const isWorkspacePaymentPaid = (record = {}) =>
  isPaidPaymentStatus(paymentStatusText(record)) || hasWorkspaceCharge(record);
const canRetryPayrollPayment = (record = {}) => {
  if (String(record.paymentType || "").toUpperCase() !== "PAY_NOW") return false;
  const payoutStatus = payoutStatusText(record);
  return !isWorkspacePaymentPaid(record) || (hasPayoutStatus(record) && !isPaidPayoutStatus(payoutStatus));
};
const payrollPayoutMessage = (result = {}) => {
  const status = String(payoutStatusText(result)).toUpperCase();
  if (["PAID", "PAYOUT_PAID", "PAYOUT_SUCCEEDED", "SUCCEEDED", "SUCCESS", "COMPLETED"].includes(status)) {
    return "Workspace funds were deducted, timesheets were marked paid, and the employee payout was initiated successfully.";
  }
  if (["SCHEDULED", "PAYOUT_SCHEDULED", "PROCESSING", "PAYOUT_PROCESSING", "PENDING", "IN_TRANSIT"].includes(status)) {
    return "Workspace funds were deducted and timesheets were marked paid. The employee payout is scheduled or processing.";
  }
  if (["FAILED", "PAYOUT_FAILED", "CANCELLED", "CANCELED", "REQUIRES_ACTION"].includes(status)) {
    return "Workspace funds were deducted and timesheets were marked paid, but the employee payout needs attention.";
  }
  return "Workspace funds were deducted, timesheets were marked paid, and the employee payout was initiated.";
};
const payrollDocumentFileId = (document = {}) =>
  firstText(document.fileId, document.storageFileId, document.documentFileId, document.file?.fileId, document.file?.id);
const payrollDocumentUrl = (document = {}) =>
  firstText(document.signedUrl, document.downloadUrl, document.previewUrl, document.url, document.fileUrl, document.file?.signedUrl, document.file?.downloadUrl, document.file?.url);
const payrollDocumentName = (document = {}, fallback = "Document") =>
  firstText(document.fileName, document.filename, document.name, document.originalFileName, document.title, document.file?.fileName, document.file?.name, fallback);
const payrollDocumentType = (document = {}) =>
  firstText(document.contentType, document.mimeType, document.type, document.fileType, document.file?.contentType, document.file?.mimeType);
const isPayrollImageDocument = (document = {}, url = "") => {
  const type = payrollDocumentType(document).toLowerCase();
  const name = payrollDocumentName(document, "").toLowerCase();
  return type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)(\?|$)/i.test(url || name);
};
const issuedPaymentDocumentsFor = (record = {}) => {
  const details = objectValue(record.paymentDetails);
  const proofDocuments = arrayValue(record.proofDocuments || details.proofDocuments)
    .map((document, index) => ({
      ...(typeof document === "string" ? { fileId: document } : objectValue(document)),
      kind: "Proof",
      fallbackName: `Proof document ${index + 1}`,
    }));
  const signatureFileId = firstText(
    record.acknowledgementSignatureFileId,
    details.acknowledgementSignatureFileId,
    details.signatureDocument?.fileId,
    details.signature?.fileId,
  );
  const signatureDocument = signatureFileId
    ? {
        ...objectValue(details.signatureDocument || details.signature),
        fileId: signatureFileId,
        kind: "Signature",
        fallbackName: "Employee signature",
        contentType: firstText(details.signatureDocument?.contentType, details.signature?.contentType, "image/png"),
      }
    : null;
  const unique = new Map();
  [...proofDocuments, signatureDocument].filter(Boolean).forEach((document, index) => {
    const key = payrollDocumentFileId(document) || payrollDocumentUrl(document) || `${document.kind}-${index}`;
    if (!unique.has(key)) unique.set(key, document);
  });
  return [...unique.values()];
};
const payrollDocumentResolvedUrl = (document = {}, signedUrls = {}) =>
  payrollDocumentUrl(document) || signedUrls[payrollDocumentFileId(document)] || "";
const payRateAmount = (rate) => Number((rate || {}).amount ?? (rate || {}).payRate ?? (rate || {}).hourlyRate ?? 0);
const payRateKind = (rate = {}) => String((rate || {}).rateKind || (rate || {}).kind || "REGULAR").toUpperCase();
const payRateType = (rate = {}) => String((rate || {}).type || (rate || {}).payRateType || "HOURLY").toUpperCase();
const isSalaryPayRate = (rate = {}) => payRateType(rate) === "SALARY";
const payRateLabel = (rate = {}) => {
  const amount = payRateAmount(rate);
  if (!amount) return "Not set";
  const kindLabel = readable(payRateKind(rate), "Regular");
  const amountLabel = isSalaryPayRate(rate) ? `${money(amount)} salary` : `${money(amount)} / hr`;
  return `${kindLabel} - ${amountLabel}`;
};
const activePayRateFor = (rates = [], kind = "REGULAR") =>
  [...rates]
    .filter((rate) => String(rate.status || "").toUpperCase() === "ACTIVE")
    .filter((rate) => !kind || payRateKind(rate) === String(kind).toUpperCase())
    .sort((a, b) => timestampMillis(b.startAt || b.createdAt) - timestampMillis(a.startAt || a.createdAt))[0] || null;
const activePayRatesForDisplay = (rates = []) =>
  ["REGULAR", "OVERTIME"]
    .map((kind) => activePayRateFor(rates, kind))
    .filter(Boolean);
const payRateDisplayParts = (row = {}) => {
  const rates = activePayRatesForDisplay(row.payRates || []);
  if (rates.length) return rates.map(payRateLabel);
  if (row.payRate) return [payRateLabel(row.payRate)];
  return [row.hourlyRate ? `${money(row.hourlyRate)} / hr` : "Not set"];
};
const payRateDisplay = (row = {}) => payRateDisplayParts(row).join(" / ");
const hasAnyPayRate = (row = {}) =>
  Boolean(payRateAmount(row.payRate) || payRateAmount(row.overtimePayRate) || Number(row.hourlyRate || 0) || Number(row.overtimeRate || 0));
const regularPayAmountFor = (row = {}) =>
  String(row.payRateType || payRateType(row.payRate)).toUpperCase() === "SALARY"
    ? Number(row.hourlyRate || 0)
    : Number(row.regularHours || 0) * Number(row.hourlyRate || 0);
const overtimePayAmountFor = (row = {}) => Number(row.overtimeHours || 0) * Number(row.overtimeRate || 0);
const paystubEmployeeAddressFor = (row = {}) =>
  firstText(row.employeeAddress, employeePaystubAddressFor(row.profile || {}), addressFor(row.profile || {}));
const paystubEmployerNameFor = (record = {}, details = {}) =>
  firstText(details.employer?.name, record.workspaceName, "Organization");
const paystubEmployerAddressFor = (record = {}, details = {}) =>
  firstText(details.employer?.address, record.workspaceAddress);
const payrollPaymentActualPayDateFor = (record = {}) => {
  const details = objectValue(record.paymentDetails);
  const workspaceCharge = objectValue(record.workspaceCharge || details.workspaceCharge || details.charge || record.charge);
  const stripeCharge = objectValue(record.stripeCharge || details.stripeCharge || workspaceCharge.stripeCharge);
  return firstText(
    record.paidAt,
    record.paymentPaidAt,
    record.paidDate,
    record.paymentDate,
    record.paymentConfirmedAt,
    record.paymentCompletedAt,
    record.chargePaidAt,
    record.chargedAt,
    record.capturedAt,
    record.completedAt,
    record.succeededAt,
    record.processedAt,
    record.confirmedAt,
    details.paidAt,
    details.paymentPaidAt,
    details.paidDate,
    details.paymentDate,
    details.paymentConfirmedAt,
    details.paymentCompletedAt,
    details.chargePaidAt,
    details.chargedAt,
    details.capturedAt,
    details.completedAt,
    details.succeededAt,
    details.processedAt,
    details.confirmedAt,
    workspaceCharge.paidAt,
    workspaceCharge.paymentDate,
    workspaceCharge.completedAt,
    workspaceCharge.succeededAt,
    workspaceCharge.processedAt,
    workspaceCharge.confirmedAt,
    workspaceCharge.chargedAt,
    workspaceCharge.capturedAt,
    stripeCharge.paidAt,
    stripeCharge.createdAt,
    stripeCharge.capturedAt,
  );
};
const payrollPaymentDisplayPayDateFor = (record = {}) => {
  return payrollPaymentActualPayDateFor(record);
};
const payrollPaymentPeriodFor = (record = {}) => {
  const details = objectValue(record.paymentDetails);
  const period = objectValue(details.payPeriod || record.payPeriod || record.paymentSchedule);
  return {
    start: firstText(period.periodStart, period.start, period.startsAt, record.periodStart),
    end: firstText(period.periodEnd, period.end, period.endsAt, record.periodEnd),
    payDate: payrollPaymentDisplayPayDateFor(record),
    frequency: firstText(period.frequency, record.paymentSchedule?.frequency),
  };
};
const isPaymentInWindow = (record = {}, [start, end] = []) => {
  if (!start || !end) return true;
  const windowStart = start.startOf("day").valueOf();
  const windowEnd = end.endOf("day").valueOf();
  const period = payrollPaymentPeriodFor(record);
  const periodStart = timestampMillis(period.start);
  const periodEnd = timestampMillis(period.end);
  if (periodStart !== null && periodEnd !== null) {
    return periodStart <= windowEnd && periodEnd >= windowStart;
  }
  const paymentDate = timestampMillis(period.payDate || record.confirmedAt || record.initiatedAt || record.createdAt || record.updatedAt);
  return paymentDate !== null && paymentDate >= windowStart && paymentDate <= windowEnd;
};
const payrollPaymentYtdTotalsFor = (record = {}) =>
  objectValue(record.yearToDateTotals || objectValue(record.paymentDetails).yearToDateTotals);
const payrollPaymentToPaystubRow = (record = {}) => {
  const details = objectValue(record.paymentDetails);
  const employee = objectValue(details.employee);
  const hours = objectValue(details.hours);
  const totals = objectValue(details.totals);
  const payRates = objectValue(details.payRates);
  const deductions = arrayValue(details.deductions);
  const earnings = arrayValue(details.earnings);
  const regularRate = Number(payRates.regular ?? 0);
  const overtimeRate = Number(payRates.overtime ?? regularRate ?? 0);
  const payRateTypeValue = String(payRates.type || "HOURLY").toUpperCase();
  const regularHours = Number(hours.regular ?? 0);
  const overtimeHours = Number(hours.overtime ?? 0);
  const grossPay = Number(record.grossPay ?? totals.grossPay ?? 0);
  const netPay = Number(record.netPay ?? totals.netPay ?? paymentAmount(record) ?? 0);
  const federalTaxLine = deductions.find((item) => payrollItemName(item).includes("FEDERAL"));
  const computedTaxLines = deductions.filter((item) => !payrollItemName(item).includes("FEDERAL") && isStatutoryPayrollTax(item));
  const fallbackWashingtonTaxLines = computeWashingtonPayrollTaxes({
    grossPay,
    approvedHours: Number(hours.approved ?? regularHours + overtimeHours),
    location: {
      employerAddress: firstText(details.employer?.address, record.workspaceAddress),
      workspaceAddress: firstText(details.employer?.address, record.workspaceAddress),
    },
  }).filter((item) => !computedTaxLines.some((line) => {
    const lineName = payrollItemName(line);
    const itemName = payrollItemName(item);
    return lineName === itemName || lineName.includes(itemName) || itemName.includes(lineName);
  }));
  const allComputedTaxLines = [...computedTaxLines, ...fallbackWashingtonTaxLines];
  const nonFederalDeductions = deductions.filter((item) => !payrollItemName(item).includes("FEDERAL") && !isStatutoryPayrollTax(item));
  const extraEarnings = earnings.filter((item) => !["REGULAR PAY", "OVERTIME PAY"].includes(payrollItemName(item)));
  return {
    paymentRecord: record,
    profile: {
      id: record.careProfileId,
      name: firstText(employee.name, record.staffName),
      email: firstText(employee.email, record.staffEmail, record.userEmail),
      phone: employee.phone,
      address: employee.address,
      userProfile: {
        id: record.userId,
        name: firstText(employee.name, record.staffName),
        email: firstText(employee.email, record.staffEmail, record.userEmail),
        phone: employee.phone,
      },
    },
    staffId: record.careProfileId,
    userId: record.userId,
    employeeAddress: employee.address,
    employer: objectValue(details.employer),
    payRate: regularRate ? { amount: regularRate, rateKind: "REGULAR", type: payRateTypeValue, status: "ACTIVE" } : null,
    overtimePayRate: overtimeRate ? { amount: overtimeRate, rateKind: "OVERTIME", type: "HOURLY", status: "ACTIVE" } : null,
    payRates: [
      regularRate ? { amount: regularRate, rateKind: "REGULAR", type: payRateTypeValue, status: "ACTIVE" } : null,
      overtimeRate ? { amount: overtimeRate, rateKind: "OVERTIME", type: "HOURLY", status: "ACTIVE" } : null,
    ].filter(Boolean),
    timesheets: arrayValue(details.timesheets),
    approvedHours: Number(hours.approved ?? regularHours + overtimeHours),
    regularHours,
    overtimeHours,
    hourlyRate: regularRate,
    overtimeRate,
    payRateType: payRateTypeValue,
    basePay: Number(totals.basePay ?? totals.regularPay ?? regularPayAmountFor({ regularHours, hourlyRate: regularRate, payRateType: payRateTypeValue })),
    activeEarnings: extraEarnings.map((item) => ({ ...item, amountType: "FIXED", scope: item.scope || "WORKSPACE" })),
    activeDeductions: nonFederalDeductions.map((item) => ({ ...item, amountType: "FIXED", deductionTaxTreatment: item.taxTreatment || item.deductionTaxTreatment, scope: item.scope || "WORKSPACE" })),
    computedPayrollTaxes: allComputedTaxLines.map((item) => ({
      ...item,
      amountType: "FIXED",
      deductionTaxTreatment: item.taxTreatment || item.deductionTaxTreatment || "POST_TAX",
      scope: item.scope || "WORKSPACE",
    })),
    grossPay,
    taxablePay: Number(totals.taxablePay ?? Math.max(0, grossPay - Number(totals.preTaxDeductions || 0))),
    federalTax: {
      amount: Number(totals.federalTax ?? federalTaxLine?.amount ?? 0),
      method: federalTaxLine?.method || "",
    },
    preTaxDeductionsAmount: Number(totals.preTaxDeductions || 0),
    postTaxDeductionsAmount: Number(totals.postTaxDeductions ?? Math.max(0, Number(record.totalDeductions || totals.totalDeductions || 0) - Number(totals.federalTax ?? federalTaxLine?.amount ?? 0))),
    estimatedPay: netPay,
  };
};
const splitRegularOvertimeHours = (hours = 0) => {
  const totalHours = Number(hours || 0);
  return {
    regularHours: Math.min(totalHours, 40),
    overtimeHours: Math.max(totalHours - 40, 0),
  };
};
const computeEstimatedPay = ({ regularHours = 0, overtimeHours = 0, regularRate = 0, overtimeRate = 0, payRateType: type = "HOURLY" }) => {
  const salary = String(type || "").toUpperCase() === "SALARY";
  const regularPay = salary ? Number(regularRate || 0) : Number(regularHours || 0) * Number(regularRate || 0);
  return regularPay + (Number(overtimeHours || 0) * Number(overtimeRate || 0));
};
const federalPayPeriods = {
  DAILY: 260,
  WEEKLY: 52,
  BIWEEKLY: 26,
  SEMIMONTHLY: 24,
  MONTHLY: 12,
  QUARTERLY: 4,
  SEMIANNUALLY: 2,
  ANNUALLY: 1,
};
const federalTaxTables2026 = {
  MARRIED_FILING_JOINTLY: {
    standardDeduction: 12900,
    STANDARD: [[0, 19300, 0, 0], [19300, 44100, 0, 0.10], [44100, 120100, 2480, 0.12], [120100, 230700, 11600, 0.22], [230700, 422850, 35932, 0.24], [422850, 531750, 82048, 0.32], [531750, 788000, 116896, 0.35], [788000, Infinity, 206583.5, 0.37]],
    STEP_2: [[0, 16100, 0, 0], [16100, 28500, 0, 0.10], [28500, 66500, 1240, 0.12], [66500, 121800, 5800, 0.22], [121800, 217875, 17966, 0.24], [217875, 272325, 41024, 0.32], [272325, 400450, 58448, 0.35], [400450, Infinity, 103291.75, 0.37]],
  },
  SINGLE_OR_MARRIED_SEPARATELY: {
    standardDeduction: 8600,
    STANDARD: [[0, 7500, 0, 0], [7500, 19900, 0, 0.10], [19900, 57900, 1240, 0.12], [57900, 113200, 5800, 0.22], [113200, 209275, 17966, 0.24], [209275, 263725, 41024, 0.32], [263725, 648100, 58448, 0.35], [648100, Infinity, 192979.25, 0.37]],
    STEP_2: [[0, 8050, 0, 0], [8050, 14250, 0, 0.10], [14250, 33250, 620, 0.12], [33250, 60900, 2900, 0.22], [60900, 108938, 8983, 0.24], [108938, 136163, 20512, 0.32], [136163, 328350, 29224, 0.35], [328350, Infinity, 96489.63, 0.37]],
  },
  HEAD_OF_HOUSEHOLD: {
    standardDeduction: 8600,
    STANDARD: [[0, 15550, 0, 0], [15550, 33250, 0, 0.10], [33250, 83000, 1770, 0.12], [83000, 121250, 7740, 0.22], [121250, 217300, 16155, 0.24], [217300, 271750, 39207, 0.32], [271750, 656150, 56631, 0.35], [656150, Infinity, 191171, 0.37]],
    STEP_2: [[0, 12075, 0, 0], [12075, 20925, 0, 0.10], [20925, 45800, 885, 0.12], [45800, 64925, 3870, 0.22], [64925, 112950, 8077.5, 0.24], [112950, 140175, 19603.5, 0.32], [140175, 332375, 28315.5, 0.35], [332375, Infinity, 95585.5, 0.37]],
  },
};
const normalizeFilingStatus = (value) => {
  const status = String(value || "").toUpperCase();
  if (status.includes("HEAD")) return "HEAD_OF_HOUSEHOLD";
  if (status.includes("MARRIED") && (status.includes("JOINT") || !status.includes("SEPARATE"))) return "MARRIED_FILING_JOINTLY";
  return "SINGLE_OR_MARRIED_SEPARATELY";
};
const w4ForProfile = (profile = {}) => {
  const base = baseProfileFor(profile);
  const user = userFor(profile);
  const source = profile.w4 || profile.formW4 || profile.federalW4 || base.w4 || user.w4 || user.federalW4 || {};
  return {
    filingStatus: normalizeFilingStatus(source.filingStatus || source.federalFilingStatus || profile.filingStatus),
    multipleJobsChecked: Boolean(source.multipleJobsChecked || source.step2Checked || source.w4Step2Checked),
    dependentCredits: Number(source.dependentCredits ?? source.step3Amount ?? source.w4Step3Amount ?? 0),
    otherIncome: Number(source.otherIncome ?? source.step4aOtherIncome ?? source.w4Step4a ?? 0),
    deductions: Number(source.deductions ?? source.step4bDeductions ?? source.w4Step4b ?? 0),
    extraWithholding: Number(source.extraWithholding ?? source.step4cExtraWithholding ?? source.w4Step4c ?? 0),
    exemptFromWithholding: Boolean(source.exemptFromWithholding || source.exempt),
    taxYear: Number(source.taxYear || 2026),
  };
};
const computeFederalIncomeTax = ({ taxablePay = 0, frequency = "BIWEEKLY", hoursWorked = 0, fullTimeWeeklyHours = 40, payRateType: type = "HOURLY", w4 = {} } = {}) => {
  const payPeriods = federalPayPeriods[String(frequency || "BIWEEKLY").toUpperCase()] || 26;
  const filingStatus = normalizeFilingStatus(w4.filingStatus);
  const tableConfig = federalTaxTables2026[filingStatus] || federalTaxTables2026.SINGLE_OR_MARRIED_SEPARATELY;
  const tableKind = w4.multipleJobsChecked ? "STEP_2" : "STANDARD";
  const taxable = Math.max(0, Number(taxablePay || 0));
  const workedHours = Number(hoursWorked || 0);
  const useHourlyProration = String(type || "").toUpperCase() !== "SALARY" && workedHours > 0 && Number(fullTimeWeeklyHours || 0) > 0;
  if (w4.exemptFromWithholding || taxable <= 0) {
    return {
      amount: Number(w4.extraWithholding || 0),
      taxablePay: taxable,
      annualizedWages: useHourlyProration ? (taxable / workedHours) * Number(fullTimeWeeklyHours || 40) * 52 : taxable * payPeriods,
      adjustedAnnualWages: 0,
      effectiveTaxRate: 0,
      filingStatus,
      tableKind,
      payPeriods,
      method: useHourlyProration ? "IRS Pub. 15-T Worksheet 1A, 40-hour prorated rate" : "IRS Pub. 15-T Worksheet 1A",
      tableVersion: "2026",
    };
  }
  const annualizedWages = useHourlyProration ? (taxable / workedHours) * Number(fullTimeWeeklyHours || 40) * 52 : taxable * payPeriods;
  const standardAdjustment = w4.multipleJobsChecked ? 0 : tableConfig.standardDeduction;
  const adjustedAnnualWages = Math.max(
    0,
    annualizedWages + Number(w4.otherIncome || 0) - Number(w4.deductions || 0) - standardAdjustment,
  );
  const selectedTable = tableConfig[tableKind] || tableConfig.STANDARD;
  const bracket = selectedTable.find(([lower, upper]) => adjustedAnnualWages >= lower && adjustedAnnualWages < upper) || selectedTable[selectedTable.length - 1];
  const [lowerLimit, , baseTax, rate] = bracket;
  const annualTax = baseTax + ((adjustedAnnualWages - lowerLimit) * rate);
  const tentativePerPeriod = annualTax / payPeriods;
  const creditPerPeriod = Number(w4.dependentCredits || 0) / payPeriods;
  const effectiveTaxRate = annualizedWages > 0 ? Math.max(0, annualTax - Number(w4.dependentCredits || 0)) / annualizedWages : 0;
  const amount = (
    useHourlyProration
      ? Math.max(0, taxable * effectiveTaxRate)
      : Math.max(0, tentativePerPeriod - creditPerPeriod)
  ) + Number(w4.extraWithholding || 0);
  return {
    amount: Math.round(amount * 100) / 100,
    taxablePay: taxable,
    annualizedWages,
    adjustedAnnualWages,
    tentativeAnnualTax: annualTax,
    tentativePerPeriod,
    creditPerPeriod,
    effectiveTaxRate,
    filingStatus,
    tableKind,
    payPeriods,
    method: useHourlyProration ? "IRS Pub. 15-T Worksheet 1A, 40-hour prorated rate" : "IRS Pub. 15-T Worksheet 1A",
    tableVersion: "2026",
  };
};
const WA_PFML_2026_TOTAL_PREMIUM_RATE = 0.0113;
const WA_PFML_2026_EMPLOYEE_SHARE = 0.7143;
const WA_PFML_2026_EMPLOYEE_RATE = WA_PFML_2026_TOTAL_PREMIUM_RATE * WA_PFML_2026_EMPLOYEE_SHARE;
const WA_PFML_2026_WAGE_BASE = 184500;
const WA_CARES_FUND_RATE = 0.0058;
const nextMonthEndBusinessDay = (date = dayjs()) => previousBusinessDay(dayjs(date).endOf("month"));
const nextQuarterEndBusinessDay = (date = dayjs()) => {
  const current = dayjs(date);
  const quarterEndMonth = Math.floor(current.month() / 3) * 3 + 2;
  return previousBusinessDay(current.month(quarterEndMonth).endOf("month"));
};
const nextExpectedRemitDateForTax = (tax = {}, date = dayjs()) => {
  const code = clean(tax.code || tax.taxCode).toUpperCase();
  if (code.startsWith("WA_")) {
    return nextQuarterEndBusinessDay(date);
  }
  return nextMonthEndBusinessDay(date);
};
const roundMoney = (value = 0) => Math.round(Number(value || 0) * 100) / 100;
const firstNumber = (...values) => {
  for (const value of values) {
    if (clean(value) === "") continue;
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
  }
  return null;
};
const addressStateFor = (source = {}) => firstText(
  source.state,
  source.stateCode,
  source.region,
  source.address?.state,
  source.address?.stateCode,
  source.address?.region,
  source.mailingAddress?.state,
  source.mailingAddress?.stateCode,
  source.permanentAddress?.state,
  source.permanentAddress?.stateCode,
  source.primaryAddress?.state,
  source.primaryAddress?.stateCode,
);
const addressTextFor = (source = {}) => firstText(
  typeof source.address === "string" ? source.address : "",
  source.addressText,
  source.formattedAddressText,
  source.employeeAddress,
  source.careStaffAddress,
  source.staffAddress,
  source.workspaceAddress,
  source.organizationAddress,
  source.employerAddress,
  source.fullAddress,
  source.formattedAddress,
  typeof source.mailingAddress === "string" ? source.mailingAddress : "",
  typeof source.permanentAddress === "string" ? source.permanentAddress : "",
  typeof source.primaryAddress === "string" ? source.primaryAddress : "",
);
const isWashingtonPayrollLocation = (value) => {
  const text = clean(value).toUpperCase();
  return text === "WA"
    || text === "WASHINGTON"
    || /\bWA\b/.test(text)
    || /\bWASHINGTON\b/.test(text);
};
const washingtonPayrollStateEvaluation = (...sources) => {
  const candidates = sources.flatMap((source) => {
    const base = baseProfileFor(source || {});
    const user = userFor(source || {});
    return [
      { label: "state", value: addressStateFor(source || {}) },
      { label: "base state", value: addressStateFor(base || {}) },
      { label: "user state", value: addressStateFor(user || {}) },
      { label: "address", value: addressTextFor(source || {}) },
      { label: "base address", value: addressTextFor(base || {}) },
      { label: "user address", value: addressTextFor(user || {}) },
    ].filter((item) => clean(item.value));
  });
  const matched = candidates.find((item) => isWashingtonPayrollLocation(item.value)) || null;
  return {
    isWashington: Boolean(matched),
    matched,
    candidates,
  };
};
const isWashingtonPayroll = (...sources) => washingtonPayrollStateEvaluation(...sources).isWashington;
const washingtonLiEmployeeShareRateFromFunds = (...sources) => {
  for (const source of sources) {
    if (!source) continue;
    const base = baseProfileFor(source || {});
    const user = userFor(source || {});
    const medicalAid = firstNumber(source.medicalAidRate, base.medicalAidRate, user.medicalAidRate);
    const stayAtWork = firstNumber(source.stayAtWorkRate, base.stayAtWorkRate, user.stayAtWorkRate);
    const supplementalPension = firstNumber(
      source.supplementalPensionRate,
      base.supplementalPensionRate,
      user.supplementalPensionRate,
    );
    const enteredRates = [medicalAid, stayAtWork, supplementalPension].filter((value) => value !== null && value !== undefined);
    if (enteredRates.length) {
      return enteredRates.reduce((total, value) => total + Number(value || 0), 0) / 2;
    }
  }
  return null;
};
const washingtonWorkersCompEmployeeHourlyRate = (...sources) => firstNumber(
  ...sources.flatMap((source) => {
    if (!source) return [];
    const base = baseProfileFor(source || {});
    const user = userFor(source || {});
    return [
      source.waWorkersCompEmployeeHourlyRate,
      source.employeeHourlyRate,
      source.employeeShareHourlyRate,
      source.waWorkersCompHourlyRate,
      source.workersCompEmployeeHourlyRate,
      source.workersCompHourlyRate,
      source.lniEmployeeHourlyRate,
      source.lniHourlyRate,
      source.lniWorkerRate,
      source.workerCompRate,
      source.workersCompRate,
      base.waWorkersCompEmployeeHourlyRate,
      base.employeeHourlyRate,
      base.employeeShareHourlyRate,
      base.waWorkersCompHourlyRate,
      base.workersCompEmployeeHourlyRate,
      base.workersCompHourlyRate,
      base.lniEmployeeHourlyRate,
      base.lniHourlyRate,
      base.lniWorkerRate,
      user.waWorkersCompEmployeeHourlyRate,
      user.employeeHourlyRate,
      user.employeeShareHourlyRate,
      user.waWorkersCompHourlyRate,
      user.workersCompEmployeeHourlyRate,
      user.workersCompHourlyRate,
      user.lniEmployeeHourlyRate,
      user.lniHourlyRate,
      user.lniWorkerRate,
    ];
  }),
) ?? washingtonLiEmployeeShareRateFromFunds(...sources);
const computedPayrollTaxItem = ({ code, name, amount, method, basis, rate }) => ({
  id: code,
  code,
  name,
  scope: "WORKSPACE",
  deductionTaxTreatment: "POST_TAX",
  taxTreatment: "PAYROLL_TAX",
  amountType: "FIXED",
  amount: roundMoney(amount),
  computed: true,
  taxable: false,
  method,
  basis,
  rate,
});
const computeWashingtonPayrollTaxes = ({ grossPay = 0, approvedHours = 0, profile = {}, workspace = {}, organization = {}, lniConfig = {}, location = {} } = {}) => {
  if (!isWashingtonPayroll(profile, workspace, organization, location)) return [];
  const pay = Math.max(0, Number(grossPay || 0));
  const hours = Math.max(0, Number(approvedHours || 0));
  const workersCompHourlyRate = washingtonWorkersCompEmployeeHourlyRate(lniConfig, profile, workspace, organization) || 0;
  return [
    computedPayrollTaxItem({
      code: "WA_PFML_EE",
      name: "WA Paid Family and Medical Leave EE",
      amount: Math.min(pay, WA_PFML_2026_WAGE_BASE) * WA_PFML_2026_EMPLOYEE_RATE,
      method: "2026 WA PFML employee share: 71.43% of 1.13%, capped at the Social Security wage base.",
      basis: pay,
      rate: WA_PFML_2026_EMPLOYEE_RATE,
    }),
    computedPayrollTaxItem({
      code: "WA_WORKERS_COMP_EE",
      name: "WA Workers' Compensation Tax",
      amount: hours * workersCompHourlyRate,
      method: workersCompHourlyRate > 0
        ? `Configured WA L&I employee-share rate ${money(workersCompHourlyRate)} x ${hours.toFixed(2)} hrs.`
        : "WA L&I employee-share rate is not configured for this workspace or staff member.",
      basis: hours,
      rate: workersCompHourlyRate,
    }),
    computedPayrollTaxItem({
      code: "WA_CARES_FUND",
      name: "WA Cares Fund",
      amount: pay * WA_CARES_FUND_RATE,
      method: "WA Cares Fund employee premium: 0.58% of gross wages.",
      basis: pay,
      rate: WA_CARES_FUND_RATE,
    }),
  ];
};
const computedPayrollTaxLinesFor = (row = {}) => arrayValue(row.computedPayrollTaxes || row.washingtonPayrollTaxes);
const computedPayrollTaxAmountFor = (row = {}) =>
  sumPayrollItems(computedPayrollTaxLinesFor(row), Number(row?.grossPay || 0));
const payrollTaxSearchText = (item = {}) => [
  item.code,
  item.taxCode,
  item.name,
  item.description,
  item.type,
  item.reference,
].map((value) => clean(value).toUpperCase()).join(" ");
const payrollTaxDefinitionForLine = (item = {}) =>
  PAYROLL_TAX_REMITTANCE_TYPES.find((type) =>
    type.matches.some((match) => payrollTaxSearchText(item).includes(match)),
  ) || null;
const payrollTaxDefinitionForRecord = (record = {}) =>
  PAYROLL_TAX_REMITTANCE_TYPES.find((type) => {
    const code = clean(record.taxCode || record.code || record.taxType || record.type).toUpperCase();
    return code === type.code || type.matches.some((match) => code.includes(match));
  }) || payrollTaxDefinitionForLine(record);
const payrollPaymentTaxLinesFor = (payment = {}) => {
  const details = objectValue(payment.paymentDetails);
  return [
    ...arrayValue(details.deductions),
    ...arrayValue(details.taxes),
    ...arrayValue(details.payrollTaxes),
    ...arrayValue(payment.deductions),
    ...arrayValue(payment.taxes),
    ...arrayValue(payment.payrollTaxes),
  ].filter((item) => {
    const amount = Number(item.amount ?? item.total ?? item.value ?? 0);
    return amount > 0 && (isStatutoryPayrollTax(item) || payrollTaxDefinitionForLine(item));
  });
};
const taxRemittanceCodeForRecord = (record = {}) =>
  payrollTaxDefinitionForRecord(record)?.code || clean(record.taxCode || record.code || record.taxType || record.type);
const taxRemittanceNameForRecord = (record = {}) =>
  firstText(record.taxName, record.name, payrollTaxDefinitionForRecord(record)?.label, readable(taxRemittanceCodeForRecord(record), "Tax"));
const taxRemittanceAmountForRecord = (record = {}) => Number(record.amount || record.remittedAmount || record.paymentAmount || 0);
const payrollItemAppliesToStaff = (item = {}, row = {}) => {
  const scope = String(item.scope || "").toUpperCase();
  if (scope === "WORKSPACE") return true;
  const itemUserId = clean(item.userId || item.user?.id);
  const itemEmail = clean(item.userEmail || item.email).toLowerCase();
  return Boolean(
    itemUserId && itemUserId === row.userId
  ) || Boolean(itemEmail && itemEmail === clean(staffEmailFor(row.profile)).toLowerCase());
};
const payrollItemIsActive = (item = {}, payWindow = []) => {
  if (String(item.status || "").toUpperCase() !== "ACTIVE") return false;
  const windowEnd = payWindow?.[1] || dayjs();
  const windowStart = payWindow?.[0] || windowEnd;
  const start = item.startAt ? dayjs(item.startAt) : null;
  const end = item.endAt ? dayjs(item.endAt) : null;
  return (!start || !start.isAfter(windowEnd, "day")) && (!end || !end.isBefore(windowStart, "day"));
};
const payrollItemName = (item = {}) => String(item.name || item.code || item.itemCode || item.type || "").toUpperCase();
const payrollLineCodeFor = (item = {}, fallback = "") =>
  firstText(item.code, item.itemCode, item.payrollCode, item.id, fallback);
const payrollEarningLineTypeFor = (item = {}) => {
  const text = payrollItemName(item);
  if (text.includes("BENEFIT")) return "BENEFIT";
  if (text.includes("BONUS")) return "BONUS";
  if (text.includes("GIFT")) return "GIFT";
  return "OTHER";
};
const payrollDeductionLineTypeFor = (item = {}) => {
  const text = payrollItemName(item);
  if (isStatutoryPayrollTax(item) || text.includes("TAX")) return "TAX";
  if (text.includes("CHARGE") || text.includes("FEE")) return "CHARGE";
  if (text.includes("EXPENSE")) return "EXPENSE";
  return "OTHER";
};
const isStatutoryPayrollTax = (item = {}) => {
  const name = payrollItemName(item);
  return name.includes("MEDICARE")
    || name.includes("SOCIAL_SECURITY")
    || name.includes("SOCIAL SECURITY")
    || name.includes("FICA")
    || name.includes("WA CARES")
    || name.includes("WA_CARES")
    || name.includes("PFML")
    || name.includes("PAID FAMILY")
    || name.includes("MEDICAL LEAVE")
    || name.includes("WORKERS")
    || name.includes("WORKER")
    || name.includes("COMPENSATION")
    || name.includes("L&I")
    || name.includes("LNI");
};
const payrollDeductionTreatmentLabel = (item = {}) => {
  if (isStatutoryPayrollTax(item)) return "Payroll tax";
  return item.deductionTaxTreatment === "PRE_TAX" ? "Before tax" : "After tax";
};
const activePayrollItemsForStaff = (items = [], row = {}, payWindow = []) =>
  items.filter((item) => payrollItemIsActive(item, payWindow) && payrollItemAppliesToStaff(item, row));
const payrollItemAmount = (item = {}, basis = 0) => {
  const amount = Number(item.amount || 0);
  if (String(item.amountType || "").toUpperCase() === "PERCENT") return (Number(basis || 0) * amount) / 100;
  return amount;
};
const sumPayrollItems = (items = [], basis = 0) => items.reduce((sum, item) => sum + payrollItemAmount(item, basis), 0);
const methodId = (method = {}) => method.id || method.externalPayoutAccountId || method.externalAccountId || "";
const methodLabel = (method = {}) => {
  const name = firstText(method.bankName, method.bank, method.institutionName, method.brand, method.accountHolderName, method.type, "Payment method");
  const last4 = firstText(method.last4, method.lastDigits, method.last4Digits);
  return last4 ? `${name} ending ${last4}` : name;
};
const isDefaultPaymentMethod = (method = {}) =>
  Boolean(
    method.default ||
    method.isDefault ||
    method.defaultPaymentMethod ||
    method.defaultPaymentAccount ||
    method.isDefaultPaymentAccount ||
    method.defaultOutgoingPaymentMethod ||
    method.isDefaultOutgoingPaymentMethod ||
    method.defaultIncomingPaymentMethod ||
    method.isDefaultIncomingPaymentMethod,
  );
const selectedMethodId = (method = {}) =>
  methodId(method) || method.paymentMethodId || method.stripePaymentMethodId || method.externalAccountId || method.id || "";
const defaultOnlyPaymentMethod = (methods = []) => methods.find(isDefaultPaymentMethod) || null;
const defaultPaymentMethod = (methods = []) => methods.find(isDefaultPaymentMethod) || methods[0] || null;
const paymentMethodSummary = (method = {}) => {
  if (!method) return null;
  return {
    id: selectedMethodId(method),
    label: methodLabel(method),
    type: firstText(method.type, method.paymentMethodType, method.accountType, method.externalAccountType),
    brand: firstText(method.brand, method.cardBrand, method.bankName, method.bank),
    last4: firstText(method.last4, method.lastDigits, method.last4Digits),
    status: firstText(method.status, method.accountStatus),
    isDefault: isDefaultPaymentMethod(method),
    workspaceId: firstText(method.workspaceId, method.workspace?.id) || undefined,
    payeeWorkspaceId: firstText(method.payeeWorkspaceId, method.workspaceId, method.workspace?.id) || undefined,
  };
};
const manualPaymentMethodDetails = (method, details) => ({
  type: method || "",
  label: readable(method, "Payment method"),
  details: clean(details),
});
const payrollStripeChargeEstimate = ({ grossPay = 0, netPay = 0 } = {}) => {
  const gross = Number(grossPay || 0);
  const principal = Number(netPay || 0);
  const paymentProcessorFeeRate = 2.9;
  const paymentFeeConstant = 0.3;
  const oliveebProcessingFeeRate = 1;
  const paymentProcessorFee = Number(((principal * paymentProcessorFeeRate) / 100).toFixed(2));
  const totalPaymentProcessorFee = Number((paymentProcessorFee + paymentFeeConstant).toFixed(2));
  const oliveebProcessingFee = Number(((principal * oliveebProcessingFeeRate) / 100).toFixed(2));
  return {
    principalAmount: principal,
    paymentProcessorFeeRate,
    paymentProcessorFee,
    paymentFeeConstant,
    totalPaymentProcessorFee,
    oliveebProcessingFeeRate,
    oliveebProcessingFee,
    stripeAdditionalCharges: totalPaymentProcessorFee,
    totalCharge: Number((principal + totalPaymentProcessorFee + oliveebProcessingFee).toFixed(2)),
    grossPay: gross,
    netPay: principal,
    currency: "USD",
  };
};
const formatAddress = (address = {}) => {
  if (!address) return "";
  if (typeof address === "string") return clean(address);
  return [
    address.formattedAddress,
    address.addressLine1 || address.line1 || address.street || address.streetAddress || address.address,
    address.addressLine2 || address.line2,
    address.city,
    address.county,
    address.state || address.stateCode,
    address.country || address.countryCode,
    address.zipCode || address.zip || address.postalCode,
  ].map(clean).filter(Boolean).join(", ");
};
const hourlyRateFor = (profile = {}) => {
  const base = baseProfileFor(profile);
  const user = userFor(profile);
  return Number(
    profile.hourlyRate ??
    profile.payRate ??
    profile.rate ??
    profile.defaultHourlyRate ??
    base.hourlyRate ??
    base.payRate ??
    user.hourlyRate ??
    0,
  );
};
const addressFor = (profile = {}) => {
  const base = baseProfileFor(profile);
  const user = userFor(profile);
  const care = profile.care || profile.careData || {};
  const userProfileDetails = user.profile || user.profileData || user.details || {};
  const connectedProfile = profile.connectedProfile || profile.connectedUserProfile || profile.careGiverProfile || profile.caregiverProfile || {};
  return firstText(
    formatAddress(care.permanentAddress),
    formatAddress(care.residentialAddress),
    formatAddress(profile.permanentAddress),
    formatAddress(profile.residentialAddress),
    formatAddress(connectedProfile.address),
    formatAddress(connectedProfile.addressDetails),
    formatAddress(connectedProfile.location),
    formatAddress(connectedProfile.contactAddress),
    formatAddress(connectedProfile.mailingAddress),
    formatAddress(connectedProfile.residentialAddress),
    formatAddress(connectedProfile.profile?.address),
    formatAddress(connectedProfile.profileData?.address),
    formatAddress(connectedProfile.details?.address),
    formatAddress(user.address),
    formatAddress(user.addressDetails),
    formatAddress(user.location),
    formatAddress(user.contactAddress),
    formatAddress(user.mailingAddress),
    formatAddress(user.residentialAddress),
    formatAddress(userProfileDetails.address),
    formatAddress(userProfileDetails.addressDetails),
    formatAddress(userProfileDetails.location),
    formatAddress(userProfileDetails.contactAddress),
    formatAddress(userProfileDetails.mailingAddress),
    formatAddress(userProfileDetails.residentialAddress),
    formatAddress({
      addressLine1: connectedProfile.addressLine1 || connectedProfile.street || connectedProfile.streetAddress,
      addressLine2: connectedProfile.addressLine2,
      city: connectedProfile.city,
      county: connectedProfile.county,
      state: connectedProfile.state,
      country: connectedProfile.country,
      zipCode: connectedProfile.zipCode || connectedProfile.zip || connectedProfile.postalCode,
    }),
    formatAddress({
      addressLine1: user.addressLine1 || user.street || user.streetAddress,
      addressLine2: user.addressLine2,
      city: user.city,
      county: user.county,
      state: user.state,
      country: user.country,
      zipCode: user.zipCode || user.zip || user.postalCode,
    }),
    formatAddress({
      addressLine1: userProfileDetails.addressLine1 || userProfileDetails.street || userProfileDetails.streetAddress,
      addressLine2: userProfileDetails.addressLine2,
      city: userProfileDetails.city,
      county: userProfileDetails.county,
      state: userProfileDetails.state,
      country: userProfileDetails.country,
      zipCode: userProfileDetails.zipCode || userProfileDetails.zip || userProfileDetails.postalCode,
    }),
    formatAddress(base.address),
    formatAddress(profile.address),
    formatAddress(profile.location),
  );
};
const cityStateZipForAddress = (address = {}) => {
  if (!address || typeof address === "string") return "";
  const city = clean(address.city);
  const state = clean(address.state || address.stateCode);
  const zip = clean(address.zipCode || address.zip || address.postalCode);
  const stateZip = [state, zip].filter(Boolean).join(" ");
  return [city, stateZip].filter(Boolean).join(", ");
};
const employeePaystubAddressFor = (profile = {}) => {
  const base = baseProfileFor(profile);
  const user = userFor(profile);
  const care = profile.care || profile.careData || {};
  const userProfileDetails = user.profile || user.profileData || user.details || {};
  const connectedProfile = profile.connectedProfile || profile.connectedUserProfile || profile.careGiverProfile || profile.caregiverProfile || {};
  return firstText(
    cityStateZipForAddress(care.permanentAddress),
    cityStateZipForAddress(care.residentialAddress),
    cityStateZipForAddress(profile.permanentAddress),
    cityStateZipForAddress(profile.residentialAddress),
    cityStateZipForAddress(connectedProfile.address),
    cityStateZipForAddress(connectedProfile.addressDetails),
    cityStateZipForAddress(connectedProfile.location),
    cityStateZipForAddress(connectedProfile.contactAddress),
    cityStateZipForAddress(connectedProfile.mailingAddress),
    cityStateZipForAddress(connectedProfile.residentialAddress),
    cityStateZipForAddress(user.address),
    cityStateZipForAddress(user.addressDetails),
    cityStateZipForAddress(user.location),
    cityStateZipForAddress(user.contactAddress),
    cityStateZipForAddress(user.mailingAddress),
    cityStateZipForAddress(user.residentialAddress),
    cityStateZipForAddress(userProfileDetails.address),
    cityStateZipForAddress(userProfileDetails.addressDetails),
    cityStateZipForAddress(userProfileDetails.location),
    cityStateZipForAddress({
      city: connectedProfile.city,
      state: connectedProfile.state,
      zipCode: connectedProfile.zipCode || connectedProfile.zip || connectedProfile.postalCode,
    }),
    cityStateZipForAddress({
      city: user.city,
      state: user.state,
      zipCode: user.zipCode || user.zip || user.postalCode,
    }),
    cityStateZipForAddress({
      city: userProfileDetails.city,
      state: userProfileDetails.state,
      zipCode: userProfileDetails.zipCode || userProfileDetails.zip || userProfileDetails.postalCode,
    }),
    cityStateZipForAddress(base.address),
    cityStateZipForAddress(profile.address),
    cityStateZipForAddress(profile.location),
  );
};
const phoneFor = (profile = {}) => {
  const base = baseProfileFor(profile);
  const user = userFor(profile);
  const userProfileDetails = user.profile || user.profileData || user.details || {};
  return firstText(
    user.phone,
    user.phoneNumber,
    user.mobilePhone,
    user.mobile,
    user.cellPhone,
    user.primaryPhone,
    userProfileDetails.phone,
    userProfileDetails.phoneNumber,
    userProfileDetails.mobilePhone,
    userProfileDetails.mobile,
    userProfileDetails.primaryPhone,
    base.phone,
    base.phoneNumber,
    profile.phone,
    profile.phoneNumber,
  );
};
const workspaceNameFor = (workspace = {}) =>
  firstText(
    workspace.name,
    workspace.organizationName,
    workspace.businessName,
    workspace.companyName,
    workspace.legalName,
    workspace.workspaceName,
    "Organization",
  );
const workspaceEmailFor = (workspace = {}) =>
  firstText(
    workspace.contactInformation?.primaryEmail,
    workspace.contactInformation?.secondaryEmail,
    workspace.contactDetails?.email,
    workspace.email,
    workspace.primaryEmail,
    workspace.contactEmail,
    workspace.businessEmail,
    workspace.organizationEmail,
    workspace.ownerEmail,
  );
const workspacePhoneFor = (workspace = {}) =>
  firstText(
    workspace.contactInformation?.primaryPhone,
    workspace.contactInformation?.secondaryPhone,
    workspace.contactDetails?.phone,
    workspace.phone,
    workspace.primaryPhone,
    workspace.phoneNumber,
    workspace.contactPhone,
    workspace.businessPhone,
    workspace.organizationPhone,
  );
const workspaceAddressFor = (workspace = {}) =>
  firstText(
    formatAddress(workspace.locationInformation),
    formatAddress(workspace.address),
    formatAddress(workspace.addressDetails),
    formatAddress(workspace.billingAddress),
    formatAddress(workspace.location),
    formatAddress(workspace.organizationLocations?.[0]?.locationInformation),
    formatAddress(workspace.organizationLocations?.[0]),
    formatAddress(workspace.businessLocations?.[0]?.locationInformation),
    formatAddress(workspace.businessLocations?.[0]),
    formatAddress({
      addressLine1: workspace.addressLine1 || workspace.street || workspace.streetAddress,
      addressLine2: workspace.addressLine2,
      city: workspace.city,
      county: workspace.county,
      state: workspace.state,
      country: workspace.country,
      zipCode: workspace.zipCode || workspace.zip || workspace.postalCode,
    }),
  );
const rowStaffKeys = (row = {}) => [row.careProfileId, row.profileId, row.caregiverUserId].map(clean).filter(Boolean);
const isTimesheetInWindow = (row = {}, [start, end] = []) => {
  if (!start || !end) return true;
  const value = timestampMillis(timesheetStart(row) || timesheetDate(row));
  if (value === null) return false;
  return value >= start.startOf("day").valueOf() && value <= end.endOf("day").valueOf();
};

export default function PayrollPage() {
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspace, workspaceListReady } = useWorkspace();
  const { uploadFile } = useFileUpload();
  const [searchParams, setSearchParams] = useSearchParams();
  const actorId = user?.id || user?.userId || "";
  const tabFromUrl = searchParams.get("tab");
  const selectedTab = PAYROLL_TAB_KEYS.includes(tabFromUrl) ? tabFromUrl : DEFAULT_PAYROLL_TAB;
  const [activeTab, setActiveTab] = useState(selectedTab);
  const [staff, setStaff] = useState([]);
  const [selectedStaffId, setSelectedStaffId] = useState("");
  const [paystubStaffUserId, setPaystubStaffUserId] = useState("");
  const [paystubPaymentId, setPaystubPaymentId] = useState("");
  const [paystubDetailPayment, setPaystubDetailPayment] = useState(null);
  const [paystubDrawerOpen, setPaystubDrawerOpen] = useState(false);
  const [approvedTimesheets, setApprovedTimesheets] = useState([]);
  const [issuedPayments, setIssuedPayments] = useState([]);
  const [workspaceOrganization, setWorkspaceOrganization] = useState(null);
  const [loadingWorkspaceOrganization, setLoadingWorkspaceOrganization] = useState(false);
  const [payWindow, setPayWindow] = useState([dayjs().startOf("month"), dayjs().endOf("month")]);
  const [payStaffRows, setPayStaffRows] = useState([]);
  const [includedPayStaffIds, setIncludedPayStaffIds] = useState([]);
  const [payDrawer, setPayDrawer] = useState(null);
  const [issuedPaymentDrawer, setIssuedPaymentDrawer] = useState(null);
  const [payrollDocumentDialog, setPayrollDocumentDialog] = useState(null);
  const [manualPaymentDrawerOpen, setManualPaymentDrawerOpen] = useState(false);
  const payrollSignatureCanvasRef = useRef(null);
  const payrollSignatureDrawingRef = useRef(false);
  const payrollSignatureLastPointRef = useRef(null);
  const [payrollSignatureHasInk, setPayrollSignatureHasInk] = useState(false);
  const [manualPaymentForm, setManualPaymentForm] = useState({
    method: "CASH",
    details: "",
    acknowledgementSignerName: "",
    acknowledgementSignature: "",
    proofFiles: [],
  });
  const [payNowDialog, setPayNowDialog] = useState({
    open: false,
    row: null,
    payerPaymentMethods: [],
    loadingMethods: false,
  });
  const [payRateDrawer, setPayRateDrawer] = useState(null);
  const [payRateRows, setPayRateRows] = useState([]);
  const [payRateForm, setPayRateForm] = useState({
    amount: null,
    type: "HOURLY",
    rateKind: "REGULAR",
    status: "ACTIVE",
    startAt: dayjs(),
  });
  const [payrollItems, setPayrollItems] = useState({ EARNING: [], DEDUCTION: [] });
  const [payrollSchedules, setPayrollSchedules] = useState([]);
  const [washingtonLiConfigs, setWashingtonLiConfigs] = useState([]);
  const [taxRemittances, setTaxRemittances] = useState([]);
  const [loadingTaxRemittances, setLoadingTaxRemittances] = useState(false);
  const [selectedTaxRemittanceCode, setSelectedTaxRemittanceCode] = useState(ALL_TAX_REMITTANCES_CODE);
  const [taxRemittanceDrawerOpen, setTaxRemittanceDrawerOpen] = useState(false);
  const [savingTaxRemittance, setSavingTaxRemittance] = useState(false);
  const [taxRemittanceSummary, setTaxRemittanceSummary] = useState([]);
  const [taxRemittanceForm, setTaxRemittanceForm] = useState({
    amount: null,
    remittedAt: dayjs(),
    paymentMethod: "EFT",
    reference: "",
    notes: "",
    proofFiles: [],
  });
  const [taxRemittanceHistoryRange, setTaxRemittanceHistoryRange] = useState([dayjs().startOf("year"), dayjs().endOf("day")]);
  const activePayrollSchedule = payrollSchedules.find((schedule) => String(schedule.status || "").toUpperCase() === "ACTIVE") || null;
  const activeWashingtonLiConfig = washingtonLiConfigs.find((config) => String(config.status || "").toUpperCase() === "ACTIVE") || null;
  const taxRemittanceRows = useMemo(() => {
    const totals = new Map(PAYROLL_TAX_REMITTANCE_TYPES.map((type) => [type.code, {
      ...type,
      accumulated: 0,
      remitted: 0,
      paymentCount: 0,
      lineCount: 0,
    }]));
    taxRemittanceSummary.forEach((summary) => {
      const definition = payrollTaxDefinitionForRecord(summary);
      if (!definition || !totals.has(definition.code)) return;
      const row = totals.get(definition.code);
      row.accumulated += Number(summary.accumulated || 0);
      row.remitted += Number(summary.remitted || 0);
      row.lineCount += Number(summary.lineCount || 0);
      row.paymentCount += Number(summary.paymentCount || 0);
    });
    taxRemittances.forEach((record) => {
      const code = taxRemittanceCodeForRecord(record);
      if (!totals.has(code)) return;
      if (!taxRemittanceSummary.length) {
        totals.get(code).remitted += taxRemittanceAmountForRecord(record);
      }
    });
    const rows = [...totals.values()].map((row) => ({
      ...row,
      accumulated: roundMoney(row.accumulated),
      remitted: roundMoney(row.remitted),
      outstanding: roundMoney(Math.max(0, row.accumulated - row.remitted)),
      nextExpectedRemitDate: nextExpectedRemitDateForTax(row).toISOString(),
    }));
    const allRow = {
      code: ALL_TAX_REMITTANCES_CODE,
      label: "All Remittances",
      agency: "All agencies",
      due: "Combined payroll tax remittance totals and history.",
      accumulated: roundMoney(rows.reduce((sum, row) => sum + Number(row.accumulated || 0), 0)),
      remitted: roundMoney(rows.reduce((sum, row) => sum + Number(row.remitted || 0), 0)),
      outstanding: roundMoney(rows.reduce((sum, row) => sum + Number(row.outstanding || 0), 0)),
      paymentCount: rows.reduce((sum, row) => sum + Number(row.paymentCount || 0), 0),
      lineCount: rows.reduce((sum, row) => sum + Number(row.lineCount || 0), 0),
      nextExpectedRemitDate: rows
        .map((row) => row.nextExpectedRemitDate)
        .filter(Boolean)
        .sort((a, b) => timestampMillis(a) - timestampMillis(b))[0],
      isAllRemittances: true,
    };
    return [allRow, ...rows];
  }, [taxRemittanceSummary, taxRemittances]);
  const selectedTaxRemittance = taxRemittanceRows.find((row) => row.code === selectedTaxRemittanceCode) || taxRemittanceRows[0];
  const selectedTaxRemittanceHistory = taxRemittances
    .filter((record) => selectedTaxRemittance?.isAllRemittances || taxRemittanceCodeForRecord(record) === selectedTaxRemittance?.code)
    .sort((a, b) => timestampMillis(b.remittedAt || b.paymentDate || b.createdAt) - timestampMillis(a.remittedAt || a.paymentDate || a.createdAt));
  const filteredTaxRemittanceHistory = selectedTaxRemittanceHistory.filter((record) => {
    if (!taxRemittanceHistoryRange?.[0] || !taxRemittanceHistoryRange?.[1]) return true;
    const value = timestampMillis(record.remittedAt || record.paymentDate || record.createdAt);
    if (value === null) return false;
    return value >= taxRemittanceHistoryRange[0].startOf("day").valueOf()
      && value <= taxRemittanceHistoryRange[1].endOf("day").valueOf();
  });
  const payrollAttentionItems = useMemo(() => {
    const activeDeductions = (payrollItems.DEDUCTION || []).filter((item) => String(item.status || "ACTIVE").toUpperCase() === "ACTIVE");
    const hasActiveDeductionMatching = (matches) =>
      activeDeductions.some((item) => {
        const searchable = [
          item.name,
          item.code,
          item.description,
          item.reference,
          item.metric,
          item.type,
        ].map((value) => clean(value).toUpperCase()).join(" ");
        return matches.some((match) => searchable.includes(match));
      });
    const hasMedicareSetting = hasActiveDeductionMatching(["MEDICARE"]);
    const hasSocialSecuritySetting = hasActiveDeductionMatching(["SOCIAL SECURITY", "SOCIAL_SECURITY", "FICA"]);
    const missingPayRateItems = payStaffRows
      .map((row) => {
        const missingKinds = ["REGULAR", "OVERTIME"].filter((kind) => !activePayRateFor(row.payRates || [], kind));
        if (!missingKinds.length) return null;
        const staffName = staffNameFor(row.profile);
        const staffEmail = staffEmailFor(row.profile);
        return {
          key: `pay-rate-${row.staffId || row.userId || staffEmail || staffName}`,
          title: `${staffName} is missing ${missingKinds.map((kind) => readable(kind, kind)).join(" and ")} pay rate`,
          description: `${staffEmail ? `${staffEmail}. ` : ""}Add active regular and overtime pay rates before preparing this employee's payroll.`,
          actionLabel: "Set pay rate",
          tab: "pay-staff",
          ready: false,
        };
      })
      .filter(Boolean);

    return [
      {
        key: "pay-schedule",
        title: "Pay schedule is missing",
        description: "Add an active pay schedule so OCare can place approved hours into the correct pay periods.",
        actionLabel: "Set pay schedule",
        tab: "payment-schedules",
        ready: Boolean(activePayrollSchedule),
      },
      {
        key: "wa-lni",
        title: "Washington L&I is not configured",
        description: "Add the workspace risk class and employee-share rates used for WA workers' compensation calculations.",
        actionLabel: "Configure WA L&I",
        tab: "wa-lni",
        ready: Boolean(activeWashingtonLiConfig),
      },
      {
        key: "medicare",
        title: "Medicare payroll tax setting is missing",
        description: "Add the Medicare payroll deduction/tax setting so payroll calculations stay complete for this workspace.",
        actionLabel: "Add deduction",
        tab: "deductions",
        ready: hasMedicareSetting,
      },
      {
        key: "social-security",
        title: "Social Security payroll tax setting is missing",
        description: "Add the Social Security payroll deduction/tax setting used when preparing staff payments.",
        actionLabel: "Add deduction",
        tab: "deductions",
        ready: hasSocialSecuritySetting,
      },
      ...missingPayRateItems,
    ].filter((item) => !item.ready);
  }, [activePayrollSchedule, activeWashingtonLiConfig, payStaffRows, payrollItems]);
  const selectedPayNowPayerMethod = defaultOnlyPaymentMethod(payNowDialog.payerPaymentMethods);
  const selectedPayNowPayeeMethod = defaultPaymentMethod(payNowDialog.row?.paymentMethods || []);
  const payNowChargeEstimate = payrollStripeChargeEstimate({
    grossPay: payNowDialog.row?.grossPay || 0,
    netPay: payNowDialog.row?.estimatedPay || 0,
  });
  const [payrollScheduleForm, setPayrollScheduleForm] = useState({
    frequency: "BIWEEKLY",
    status: "ACTIVE",
    startAt: dayjs(),
    firstPayDate: computedFirstPayDate("BIWEEKLY", dayjs()),
    description: "",
  });
  const [payrollScheduleDrawerOpen, setPayrollScheduleDrawerOpen] = useState(false);
  const [washingtonLiDrawerOpen, setWashingtonLiDrawerOpen] = useState(false);
  const [washingtonLiInfoOpen, setWashingtonLiInfoOpen] = useState(false);
  const [washingtonLiForm, setWashingtonLiForm] = useState({
    riskClassCode: "",
    riskClassDescription: "",
    employeeHourlyRate: null,
    medicalAidRate: null,
    stayAtWorkRate: null,
    supplementalPensionRate: null,
    status: "ACTIVE",
    startAt: dayjs(),
    endAt: null,
    notes: "",
  });
  const [payrollItemForms, setPayrollItemForms] = useState({
    EARNING: {
      scope: "WORKSPACE",
      userId: "",
      name: "",
      description: "",
      amount: null,
      amountType: "FIXED",
      status: "ACTIVE",
      taxable: true,
      deductionTaxTreatment: "POST_TAX",
      startAt: dayjs(),
      endAt: null,
    },
    DEDUCTION: {
      scope: "WORKSPACE",
      userId: "",
      name: "",
      description: "",
      amount: null,
      amountType: "FIXED",
      status: "ACTIVE",
      taxable: true,
      deductionTaxTreatment: "POST_TAX",
      startAt: dayjs(),
      endAt: null,
    },
  });
  const [payrollItemDrawerKind, setPayrollItemDrawerKind] = useState("");
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingPayStaff, setLoadingPayStaff] = useState(false);
  const [loadingPayRates, setLoadingPayRates] = useState(false);
  const [loadingPayrollItems, setLoadingPayrollItems] = useState(false);
  const [loadingPayrollSchedules, setLoadingPayrollSchedules] = useState(false);
  const [loadingPayrollPaymentDetail, setLoadingPayrollPaymentDetail] = useState("");
  const [retryingPayrollPaymentId, setRetryingPayrollPaymentId] = useState("");
  const [savingPayRate, setSavingPayRate] = useState(false);
  const [savingPayrollItemKind, setSavingPayrollItemKind] = useState("");
  const [savingPayrollSchedule, setSavingPayrollSchedule] = useState(false);
  const [loadingWashingtonLiConfigs, setLoadingWashingtonLiConfigs] = useState(false);
  const [savingWashingtonLiConfig, setSavingWashingtonLiConfig] = useState(false);
  const [savingManualPayment, setSavingManualPayment] = useState(false);
  const [initiatingPayment, setInitiatingPayment] = useState(false);
  const [error, setError] = useState("");

  const selectedStaff = useMemo(
    () => staff.find((item) => profileIdFor(item) === selectedStaffId) || null,
    [selectedStaffId, staff],
  );
  const isAllStaffSelected = selectedStaffId === ALL_STAFF_ID;
  const selectedStaffUserId = userIdFor(selectedStaff || {});
  const issuedPaymentDocuments = useMemo(
    () => issuedPaymentDocumentsFor(issuedPaymentDrawer || {}),
    [issuedPaymentDrawer],
  );
  const workspaceOrganizationId = selectedWorkspace?.organizationId || selectedWorkspace?.organization?.id || selectedWorkspace?.businessEntityId || "";
  const paystubEmployer = workspaceOrganizationId
    ? (workspaceOrganization || selectedWorkspace?.organization || {})
    : (selectedWorkspace?.organization || selectedWorkspace || {});
  const staffOptions = useMemo(
    () => [
      { value: ALL_STAFF_ID, label: "All Staff" },
      ...staff.map((profile) => ({
        value: profileIdFor(profile),
        label: firstText(staffEmailFor(profile))
          ? `${staffNameFor(profile)} - ${staffEmailFor(profile)}`
          : staffNameFor(profile),
      })),
    ],
    [staff],
  );
  const staffById = useMemo(() => {
    const lookup = {};
    staff.forEach((profile) => {
      const profileId = profileIdFor(profile);
      const userId = userIdFor(profile);
      if (profileId) lookup[profileId] = profile;
      if (userId) lookup[userId] = profile;
    });
    return lookup;
  }, [staff]);
  const timesheetStaffName = useCallback(
    (row = {}) => {
      const matched = staffById[row.careProfileId] || staffById[row.profileId] || staffById[row.caregiverUserId] || {};
      return firstText(
        row.caregiverName,
        row.staffName,
        row.userProfile?.name,
        row.caregiver?.name,
        staffNameFor(matched),
        row.caregiverEmail,
        row.caregiverUserId,
        "Care staff",
      );
    },
    [staffById],
  );
  const timesheetStaffEmail = useCallback(
    (row = {}) => {
      const matched = staffById[row.careProfileId] || staffById[row.profileId] || staffById[row.caregiverUserId] || {};
      return firstText(
        row.caregiverEmail,
        row.userProfile?.email,
        row.caregiver?.email,
        staffEmailFor(matched),
      );
    },
    [staffById],
  );
  const approvedHours = useMemo(
    () => approvedTimesheets.filter((row) => isTimesheetInWindow(row, payWindow)).reduce((sum, item) => sum + timesheetHours(item), 0),
    [approvedTimesheets, payWindow],
  );
  const issuedTotal = useMemo(
    () => issuedPayments.reduce((sum, item) => sum + Number(paymentAmount(item) || 0), 0),
    [issuedPayments],
  );
  const includedPayTotal = useMemo(
    () =>
      payStaffRows
        .filter((row) => includedPayStaffIds.includes(row.staffId))
        .reduce((sum, row) => sum + row.estimatedPay, 0),
    [includedPayStaffIds, payStaffRows],
  );

  const staffUserOptions = useMemo(
    () =>
      [
        { value: ALL_STAFF_ID, label: "All Staff" },
        ...staff
        .filter((profile) => userIdFor(profile))
        .map((profile) => ({
          value: userIdFor(profile),
          label: firstText(staffEmailFor(profile))
            ? `${staffNameFor(profile)} - ${staffEmailFor(profile)}`
            : staffNameFor(profile),
        })),
      ],
    [staff],
  );
  const paystubPaymentRows = useMemo(
    () => issuedPayments
      .filter((payment) => isPaymentInWindow(payment, payWindow))
      .filter((payment) => !paystubStaffUserId || paystubStaffUserId === ALL_STAFF_ID || payment.userId === paystubStaffUserId),
    [issuedPayments, payWindow, paystubStaffUserId],
  );
  const activePayrollPeriodOptions = useMemo(
    () => activePayrollSchedule ? payrollPeriodOptionsFromSchedules([activePayrollSchedule]) : [],
    [activePayrollSchedule],
  );
  const currentActivePayPeriod = useMemo(() => {
    if (!activePayrollSchedule) return null;
    const today = dayjs();
    return activePayrollPeriodOptions.find((option) => !today.isBefore(option.start, "day") && !today.isAfter(option.end, "day")) || activePayrollPeriodOptions[0] || null;
  }, [activePayrollPeriodOptions, activePayrollSchedule]);
  const effectivePayWindow = useMemo(
    () => payWindow,
    [payWindow],
  );
  const selectedPayWindowPeriod = useMemo(() => {
    const selectedStart = payWindow?.[0]?.startOf?.("day");
    const selectedEnd = payWindow?.[1]?.endOf?.("day");
    const matchedPeriod = activePayrollPeriodOptions.find((option) =>
      selectedStart?.isSame(option.start, "day") && selectedEnd?.isSame(option.end, "day"),
    );
    if (matchedPeriod) return matchedPeriod;
    const fallbackEnd = selectedEnd || currentActivePayPeriod?.end || dayjs().endOf("day");
    const schedule = activePayrollSchedule || currentActivePayPeriod?.schedule || null;
    return {
      value: "selected-pay-window",
      label: `${shortDateText(selectedStart)} - ${shortDateText(selectedEnd)}`,
      frequency: schedule?.frequency || currentActivePayPeriod?.frequency || "",
      start: selectedStart || currentActivePayPeriod?.start || dayjs().startOf("day"),
      end: selectedEnd || currentActivePayPeriod?.end || dayjs().endOf("day"),
      payDate: previousBusinessDay(fallbackEnd),
      schedule,
    };
  }, [activePayrollPeriodOptions, activePayrollSchedule, currentActivePayPeriod, payWindow]);
  const selectedActivePayrollPeriodValue = useMemo(() => {
    const selectedStart = payWindow?.[0]?.startOf?.("day");
    const selectedEnd = payWindow?.[1]?.endOf?.("day");
    return activePayrollPeriodOptions.find((option) =>
      selectedStart?.isSame(option.start, "day") && selectedEnd?.isSame(option.end, "day"),
    )?.value;
  }, [activePayrollPeriodOptions, payWindow]);
  const approvedTimesheetsInPayWindow = useMemo(
    () => approvedTimesheets.filter((row) => isTimesheetInWindow(row, effectivePayWindow)),
    [approvedTimesheets, effectivePayWindow],
  );
  const issuedPaymentsInPayWindow = useMemo(
    () => issuedPayments.filter((payment) => isPaymentInWindow(payment, effectivePayWindow)),
    [effectivePayWindow, issuedPayments],
  );
  const issuedPayWindowTotal = useMemo(
    () => issuedPaymentsInPayWindow.reduce((sum, item) => sum + Number(paymentAmount(item) || 0), 0),
    [issuedPaymentsInPayWindow],
  );
  const payDrawerPeriod = selectedPayWindowPeriod;
  const payDrawerTimesheets = useMemo(
    () => (payDrawer?.timesheets || []).filter((row) => isTimesheetInWindow(row, effectivePayWindow)),
    [effectivePayWindow, payDrawer],
  );
  const selectedPaystubPayment = useMemo(
    () => {
      if (paystubDetailPayment?.id && paystubDetailPayment.id === paystubPaymentId) {
        return paystubDetailPayment;
      }
      return paystubPaymentRows.find((payment) => payment.id === paystubPaymentId) || null;
    },
    [paystubDetailPayment, paystubPaymentId, paystubPaymentRows],
  );
  const selectedPaystubPeriod = useMemo(
    () => selectedPaystubPayment ? payrollPaymentPeriodFor(selectedPaystubPayment) : null,
    [selectedPaystubPayment],
  );
  const selectedPaystubRow = useMemo(
    () => selectedPaystubPayment ? payrollPaymentToPaystubRow(selectedPaystubPayment) : null,
    [selectedPaystubPayment],
  );
  const paystubApprovedHours = selectedPaystubRow?.approvedHours || 0;
  const paystubTimesheetsForSelected = selectedPaystubRow?.timesheets || [];
  const selectedPaystubEmployer = selectedPaystubRow?.employer || {};
  const selectedPaystubYtdTotals = payrollPaymentYtdTotalsFor(selectedPaystubPayment || {});

  const loadStaff = useCallback(async () => {
    if (!actorId || !workspaceListReady || !selectedWorkspaceId) {
      setStaff([]);
      setSelectedStaffId("");
      return;
    }
    try {
      setLoadingStaff(true);
      setError("");
      const result = await careService.careProfiles.list({
        actorId,
        workspaceId: selectedWorkspaceId,
        wid: selectedWorkspaceId,
        size: 200,
      });
      const rows = pageContent(result).filter((profile) => profileIdFor(profile));
      setStaff(rows);
      setSelectedStaffId((current) =>
        current === ALL_STAFF_ID || rows.some((profile) => profileIdFor(profile) === current)
          ? current
          : ALL_STAFF_ID,
      );
    } catch (nextError) {
      setStaff([]);
      setSelectedStaffId("");
      setError(nextError?.message || "Unable to load staff payroll.");
    } finally {
      setLoadingStaff(false);
    }
  }, [actorId, selectedWorkspaceId, workspaceListReady]);

  useEffect(() => {
    if (!workspaceOrganizationId) {
      setWorkspaceOrganization(null);
      setLoadingWorkspaceOrganization(false);
      return;
    }
    let cancelled = false;
    setLoadingWorkspaceOrganization(true);
    olreadiaService.organizations.get(workspaceOrganizationId, selectedWorkspaceId ? { wid: selectedWorkspaceId } : {})
      .then((organization) => {
        if (!cancelled) setWorkspaceOrganization(organization || null);
      })
      .catch(() => {
        if (!cancelled) setWorkspaceOrganization(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingWorkspaceOrganization(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceOrganizationId, selectedWorkspaceId]);

  const loadIssuedPayments = useCallback(async (userId = null) => {
    if (!actorId || !selectedWorkspaceId) {
      setIssuedPayments([]);
      return [];
    }
    const paymentsResult = await workspacePayrollService.listPayments({
      actorId,
      workspaceId: selectedWorkspaceId,
      userId,
    });
    const rows = pageContent(paymentsResult);
    setIssuedPayments(rows);
    return rows;
  }, [actorId, selectedWorkspaceId]);

  const loadStaffDetails = useCallback(async () => {
    if (!actorId || !selectedWorkspaceId || !selectedStaffId) {
      setApprovedTimesheets([]);
      if (activeTab === "overview") loadIssuedPayments().catch(() => setIssuedPayments([]));
      else setIssuedPayments([]);
      return;
    }
    try {
      setLoadingDetails(true);
      setError("");
      if (isAllStaffSelected || activeTab === "overview" || activeTab === "paysturbs" || activeTab === "tax-remittances") {
        const timesheetsResult = await careService.timesheets.list({
          actorId,
          workspaceId: selectedWorkspaceId,
          wid: selectedWorkspaceId,
          status: "APPROVED",
          size: 300,
        });
        setApprovedTimesheets(pageContent(timesheetsResult));

        await loadIssuedPayments().catch(() => setIssuedPayments([]));
        return;
      }

      if (!selectedStaffUserId) {
        setApprovedTimesheets([]);
        setIssuedPayments([]);
        return;
      }

      const timesheetsResult = await careService.timesheets.listForCareProfile(selectedStaffId, {
        actorId,
        workspaceId: selectedWorkspaceId,
        wid: selectedWorkspaceId,
        status: "APPROVED",
      });
      setApprovedTimesheets(pageContent(timesheetsResult));

      await loadIssuedPayments(selectedStaffUserId).catch(() => setIssuedPayments([]));
    } catch (nextError) {
      setApprovedTimesheets([]);
      setIssuedPayments([]);
      setError(nextError?.message || "Unable to load staff payroll details.");
    } finally {
      setLoadingDetails(false);
    }
  }, [actorId, activeTab, isAllStaffSelected, loadIssuedPayments, selectedStaffId, selectedStaffUserId, selectedWorkspaceId]);

  const loadPayStaff = useCallback(async () => {
    if (!actorId || !selectedWorkspaceId || !staff.length) {
      setPayStaffRows([]);
      setIncludedPayStaffIds([]);
      return;
    }
    try {
      setLoadingPayStaff(true);
      setError("");
      const timesheetsResult = await careService.timesheets.list({
        actorId,
        workspaceId: selectedWorkspaceId,
        wid: selectedWorkspaceId,
        status: "APPROVED",
        size: 500,
      });
      const approvedRows = pageContent(timesheetsResult).filter((row) => isTimesheetInWindow(row, effectivePayWindow));
      const staffWithUsers = staff.filter((profile) => userIdFor(profile));
      const payrollLinks = await Promise.all(
        staffWithUsers.map((profile) =>
          userPayrollService.getForUser({ actorId, userId: userIdFor(profile) }).catch(() => null),
        ),
      );
      const methodResults = await Promise.all(
        staffWithUsers.map((profile, index) => {
          const linkedWorkspaceId = payrollLinks[index]?.workspaceId;
          if (!linkedWorkspaceId) return Promise.resolve([]);
          return paymentMethodsService.incoming.list({
            actorId,
            userId: userIdFor(profile),
            workspaceId: linkedWorkspaceId,
          }).catch(() => []);
        }),
      );
      const payRateResults = await Promise.all(
        staffWithUsers.map((profile) =>
          userPayrollService.listPayRates({ actorId, userId: userIdFor(profile), workspaceId: selectedWorkspaceId }).catch(() => []),
        ),
      );
      const rows = staffWithUsers.map((profile, index) => {
        const staffId = profileIdFor(profile);
        const userId = userIdFor(profile);
        const timesheets = approvedRows.filter((row) => rowStaffKeys(row).includes(staffId) || rowStaffKeys(row).includes(userId));
        const approvedStaffHours = timesheets.reduce((sum, row) => sum + timesheetHours(row), 0);
        const payRates = Array.isArray(payRateResults[index]) ? payRateResults[index] : pageContent(payRateResults[index]);
        const currentPayRate = activePayRateFor(payRates, "REGULAR") || null;
        const currentOvertimeRate = activePayRateFor(payRates, "OVERTIME") || null;
        const hourlyRate = payRateAmount(currentPayRate) || hourlyRateFor(profile);
        const overtimeRate = payRateAmount(currentOvertimeRate) || hourlyRate;
        const regularPayRateType = payRateType(currentPayRate);
        const { regularHours, overtimeHours } = splitRegularOvertimeHours(approvedStaffHours);
        const basePay = computeEstimatedPay({
          regularHours,
          overtimeHours,
          regularRate: hourlyRate,
          overtimeRate,
          payRateType: regularPayRateType,
        });
        const draftRow = { profile, staffId, userId };
        const activeEarnings = activePayrollItemsForStaff(payrollItems.EARNING, draftRow, effectivePayWindow);
        const configuredDeductions = activePayrollItemsForStaff(payrollItems.DEDUCTION, draftRow, effectivePayWindow);
        const taxableEarnings = activeEarnings.filter((item) => item.taxable !== false);
        const earningsAmount = sumPayrollItems(activeEarnings, basePay);
        const taxableEarningsAmount = sumPayrollItems(taxableEarnings, basePay);
        const grossPay = basePay + earningsAmount;
        const washingtonTaxLocation = {
          employerAddress: workspaceAddressFor(paystubEmployer) || workspaceAddressFor(selectedWorkspace),
          workspaceAddress: workspaceAddressFor(selectedWorkspace),
          organizationAddress: workspaceAddressFor(paystubEmployer),
        };
        const washingtonTaxEvaluation = washingtonPayrollStateEvaluation(selectedWorkspace, paystubEmployer, washingtonTaxLocation);
        const washingtonPayrollTaxes = computeWashingtonPayrollTaxes({
          grossPay,
          approvedHours: approvedStaffHours,
          workspace: selectedWorkspace,
          organization: paystubEmployer,
          lniConfig: activeWashingtonLiConfig,
          location: washingtonTaxLocation,
        });
        const activeDeductions = configuredDeductions;
        const preTaxDeductions = activeDeductions.filter((item) => String(item.deductionTaxTreatment || "").toUpperCase() === "PRE_TAX");
        const taxablePayPreTaxDeductions = preTaxDeductions.filter((item) => !isStatutoryPayrollTax(item));
        const postTaxDeductions = activeDeductions.filter((item) => String(item.deductionTaxTreatment || "").toUpperCase() !== "PRE_TAX");
        const preTaxDeductionsAmount = sumPayrollItems(preTaxDeductions, basePay + taxableEarningsAmount);
        const taxablePayPreTaxDeductionsAmount = sumPayrollItems(taxablePayPreTaxDeductions, basePay + taxableEarningsAmount);
        const postTaxDeductionsAmount = sumPayrollItems(postTaxDeductions, grossPay);
        const taxablePay = Math.max(0, basePay + taxableEarningsAmount - taxablePayPreTaxDeductionsAmount);
        const profileW4 = w4ForProfile(profile);
        const w4 = {
          ...profileW4,
          filingStatus: profileW4.filingStatus || "SINGLE_OR_MARRIED_SEPARATELY",
          multipleJobsChecked: false,
        };
        const federalTax = computeFederalIncomeTax({
          taxablePay,
          frequency: selectedPayWindowPeriod?.frequency || activePayrollSchedule?.frequency || "BIWEEKLY",
          hoursWorked: approvedStaffHours,
          fullTimeWeeklyHours: 40,
          payRateType: regularPayRateType,
          w4,
        });
        return {
          profile,
          staffId,
          userId,
          payroll: payrollLinks[index] || null,
          payPeriod: selectedPayWindowPeriod,
          paymentSchedule: selectedPayWindowPeriod?.schedule || activePayrollSchedule || null,
          payRate: currentPayRate,
          overtimePayRate: currentOvertimeRate,
          payRates,
          paymentMethods: Array.isArray(methodResults[index]) ? methodResults[index] : pageContent(methodResults[index]),
          timesheets,
          approvedHours: approvedStaffHours,
          regularHours,
          overtimeHours,
          hourlyRate,
          overtimeRate,
          payRateType: regularPayRateType,
          basePay,
          activeEarnings,
          activeDeductions,
          computedPayrollTaxes: washingtonPayrollTaxes,
          washingtonTaxEvaluation: {
            ...washingtonTaxEvaluation,
            computedLineCount: washingtonPayrollTaxes.length,
          },
          earningsAmount,
          taxableEarningsAmount,
          preTaxDeductionsAmount,
          postTaxDeductionsAmount,
          grossPay,
          taxablePay,
          federalTax,
          estimatedPay: Math.max(0, grossPay - preTaxDeductionsAmount - federalTax.amount - postTaxDeductionsAmount - sumPayrollItems(washingtonPayrollTaxes, grossPay)),
        };
      });
      setPayStaffRows(rows);
      setIncludedPayStaffIds((current) => current.filter((staffId) => rows.some((row) => row.staffId === staffId)));
    } catch (nextError) {
      setPayStaffRows([]);
      setIncludedPayStaffIds([]);
      setError(nextError?.message || "Unable to load staff ready for payment.");
    } finally {
      setLoadingPayStaff(false);
    }
  }, [actorId, activePayrollSchedule, activeWashingtonLiConfig, effectivePayWindow, payrollItems, paystubEmployer, selectedPayWindowPeriod, selectedWorkspace, selectedWorkspaceId, staff]);

  const loadPayrollItems = useCallback(async () => {
    if (!actorId || !selectedWorkspaceId) {
      setPayrollItems({ EARNING: [], DEDUCTION: [] });
      return;
    }
    try {
      setLoadingPayrollItems(true);
      setError("");
      const [earningRows, deductionRows] = await Promise.all([
        workspacePayrollService.listItems({ actorId, workspaceId: selectedWorkspaceId, kind: "EARNING" }).catch(() => []),
        workspacePayrollService.listItems({ actorId, workspaceId: selectedWorkspaceId, kind: "DEDUCTION" }).catch(() => []),
      ]);
      setPayrollItems({
        EARNING: Array.isArray(earningRows) ? earningRows : pageContent(earningRows),
        DEDUCTION: Array.isArray(deductionRows) ? deductionRows : pageContent(deductionRows),
      });
    } catch (nextError) {
      setPayrollItems({ EARNING: [], DEDUCTION: [] });
      setError(nextError?.message || "Unable to load payroll earnings and deductions.");
    } finally {
      setLoadingPayrollItems(false);
    }
  }, [actorId, selectedWorkspaceId]);

  const loadPayrollSchedules = useCallback(async () => {
    if (!actorId || !selectedWorkspaceId) {
      setPayrollSchedules([]);
      return;
    }
    try {
      setLoadingPayrollSchedules(true);
      setError("");
      const rows = await workspacePayrollService.listSchedules({ actorId, workspaceId: selectedWorkspaceId });
      setPayrollSchedules(Array.isArray(rows) ? rows : pageContent(rows));
    } catch (nextError) {
      setPayrollSchedules([]);
      setError(nextError?.message || "Unable to load payroll payment schedules.");
    } finally {
      setLoadingPayrollSchedules(false);
    }
  }, [actorId, selectedWorkspaceId]);

  const loadWashingtonLiConfigs = useCallback(async () => {
    if (!actorId || !selectedWorkspaceId) {
      setWashingtonLiConfigs([]);
      return;
    }
    try {
      setLoadingWashingtonLiConfigs(true);
      setError("");
      const rows = await workspacePayrollService.listWashingtonLiConfigs({ actorId, workspaceId: selectedWorkspaceId });
      setWashingtonLiConfigs(Array.isArray(rows) ? rows : pageContent(rows));
    } catch (nextError) {
      setWashingtonLiConfigs([]);
      setError(nextError?.message || "Unable to load WA L&I configuration.");
    } finally {
      setLoadingWashingtonLiConfigs(false);
    }
  }, [actorId, selectedWorkspaceId]);

  const loadTaxRemittances = useCallback(async () => {
    if (!actorId || !selectedWorkspaceId) {
      setTaxRemittances([]);
      setTaxRemittanceSummary([]);
      return;
    }
    try {
      setLoadingTaxRemittances(true);
      setError("");
      const [rows, summaryRows] = await Promise.all([
        workspacePayrollService.listTaxRemittances({ actorId, workspaceId: selectedWorkspaceId }),
        workspacePayrollService.listTaxRemittanceSummary({ actorId, workspaceId: selectedWorkspaceId }),
      ]);
      setTaxRemittances(Array.isArray(rows) ? rows : pageContent(rows));
      setTaxRemittanceSummary(Array.isArray(summaryRows) ? summaryRows : pageContent(summaryRows));
    } catch (nextError) {
      setTaxRemittances([]);
      setTaxRemittanceSummary([]);
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to load tax remittances.");
    } finally {
      setLoadingTaxRemittances(false);
    }
  }, [actorId, selectedWorkspaceId]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    loadStaffDetails();
  }, [loadStaffDetails]);

  useEffect(() => {
    if (["overview", "attention", "pay-staff"].includes(activeTab)) {
      loadPayrollItems();
      loadPayrollSchedules();
      loadWashingtonLiConfigs();
    }
    if (["approved-timesheets", "issued-payments"].includes(activeTab)) {
      loadPayrollSchedules();
    }
  }, [activeTab, loadPayrollItems, loadPayrollSchedules, loadWashingtonLiConfigs]);

  useEffect(() => {
    if (["overview", "attention", "pay-staff"].includes(activeTab)) {
      loadPayStaff();
    }
  }, [activeTab, loadPayStaff]);

  useEffect(() => {
    if (["earnings", "deductions"].includes(activeTab)) {
      loadPayrollItems();
    }
  }, [activeTab, loadPayrollItems]);

  useEffect(() => {
    if (["payment-schedules", "paysturbs"].includes(activeTab)) {
      loadPayrollSchedules();
    }
  }, [activeTab, loadPayrollSchedules]);

  useEffect(() => {
    if (activeTab === "wa-lni") {
      loadWashingtonLiConfigs();
    }
  }, [activeTab, loadWashingtonLiConfigs]);

  useEffect(() => {
    if (activeTab === "paysturbs") {
      loadPayrollItems();
    }
  }, [activeTab, loadPayrollItems]);

  useEffect(() => {
    if (activeTab === "tax-remittances") {
      loadTaxRemittances();
      loadIssuedPayments().catch(() => setIssuedPayments([]));
    }
  }, [activeTab, loadIssuedPayments, loadTaxRemittances]);

  useEffect(() => {
    if (staffUserOptions.length && !staffUserOptions.some((option) => option.value === paystubStaffUserId)) {
      setPaystubStaffUserId(staffUserOptions[0].value);
    }
  }, [paystubStaffUserId, staffUserOptions]);

  useEffect(() => {
    if (paystubPaymentId && !paystubPaymentRows.some((payment) => payment.id === paystubPaymentId)) {
      setPaystubPaymentId("");
      setPaystubDetailPayment(null);
      setPaystubDrawerOpen(false);
    }
  }, [paystubPaymentId, paystubPaymentRows]);

  useEffect(() => {
    if (!payDrawer) return;
    const updatedRow = payStaffRows.find((row) => row.staffId === payDrawer.staffId || row.userId === payDrawer.userId);
    if (updatedRow && updatedRow !== payDrawer) {
      setPayDrawer(updatedRow);
    }
  }, [payDrawer, payStaffRows]);

  useEffect(() => {
    if (activeTab !== selectedTab) {
      setActiveTab(selectedTab);
    }
  }, [activeTab, selectedTab]);

  const refresh = () => {
    loadStaff();
    loadStaffDetails();
    if (["overview", "attention", "pay-staff"].includes(activeTab)) {
      loadPayrollItems();
      loadPayrollSchedules();
      loadWashingtonLiConfigs();
    }
    if (["overview", "attention", "pay-staff"].includes(activeTab)) {
      loadPayStaff();
    }
    if (["approved-timesheets", "issued-payments"].includes(activeTab)) loadPayrollSchedules();
    if (["earnings", "deductions"].includes(activeTab)) loadPayrollItems();
    if (["payment-schedules", "paysturbs"].includes(activeTab)) loadPayrollSchedules();
    if (activeTab === "wa-lni") loadWashingtonLiConfigs();
    if (activeTab === "tax-remittances") {
      loadIssuedPayments().catch(() => setIssuedPayments([]));
      loadTaxRemittances();
    }
    if (activeTab === "paysturbs") {
      loadStaffDetails();
    }
  };

  const loadPayrollPaymentDetail = async (payment) => {
    const paymentId = payment?.id || payment?.paymentId || payment?.payrollPaymentDetailsId;
    if (!paymentId || !selectedWorkspaceId) {
      message.error("Unable to find the selected payroll payment.");
      return null;
    }
    try {
      setLoadingPayrollPaymentDetail(paymentId);
      setError("");
      return await workspacePayrollService.getPayment({
        actorId,
        workspaceId: selectedWorkspaceId,
        paymentId,
      });
    } catch (nextError) {
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to load payroll payment details.");
      return null;
    } finally {
      setLoadingPayrollPaymentDetail("");
    }
  };

  const openTaxRemittanceDrawer = () => {
    if (selectedTaxRemittance?.isAllRemittances) return;
    setTaxRemittanceForm({
      amount: selectedTaxRemittance?.outstanding || null,
      remittedAt: dayjs(),
      paymentMethod: "EFT",
      reference: "",
      notes: "",
      proofFiles: [],
    });
    setTaxRemittanceDrawerOpen(true);
  };

  const submitTaxRemittance = async () => {
    if (!selectedTaxRemittance || !selectedWorkspaceId) return;
    const amount = Number(selectedTaxRemittance.outstanding || 0);
    if (amount <= 0) {
      message.error("No remittance amount is currently due.");
      return;
    }
    try {
      setSavingTaxRemittance(true);
      setError("");
      const proofFiles = (taxRemittanceForm.proofFiles || []).map((file) => file.originFileObj || file).filter(Boolean);
      const proofDocuments = await uploadManualPaymentProofDocuments(proofFiles, "PAYROLL_PAYMENT_PROOF");
      await workspacePayrollService.createTaxRemittance({
        actorId,
        workspaceId: selectedWorkspaceId,
        remittance: {
          taxCode: selectedTaxRemittance.code,
          taxName: selectedTaxRemittance.label,
          agency: selectedTaxRemittance.agency,
          amount,
          remittedAt: taxRemittanceForm.remittedAt?.toISOString?.() || dayjs().toISOString(),
          paymentMethod: taxRemittanceForm.paymentMethod,
          reference: taxRemittanceForm.reference,
          notes: taxRemittanceForm.notes,
          currency: "USD",
          paymentDetails: {
            accumulatedAmount: selectedTaxRemittance.accumulated,
            previouslyRemittedAmount: selectedTaxRemittance.remitted,
            outstandingAmount: selectedTaxRemittance.outstanding,
          },
          proofDocuments,
        },
      });
      message.success("Tax remittance recorded");
      setTaxRemittanceDrawerOpen(false);
      await loadTaxRemittances();
    } catch (nextError) {
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to record tax remittance.");
    } finally {
      setSavingTaxRemittance(false);
    }
  };

  const openIssuedPaymentDetails = async (payment) => {
    const detailedPayment = await loadPayrollPaymentDetail(payment);
    if (detailedPayment) {
      setIssuedPaymentDrawer(detailedPayment);
    }
  };

  const openPaystubDetails = async (payment) => {
    const detailedPayment = await loadPayrollPaymentDetail(payment);
    if (detailedPayment) {
      setPaystubPaymentId(detailedPayment.id || payment.id);
      setPaystubDetailPayment(detailedPayment);
      setPaystubDrawerOpen(true);
    }
  };

  const printPaystub = () => {
    if (!selectedPaystubRow || !selectedPaystubPeriod) {
      message.error("No paystub is ready to print.");
      return;
    }
    if (loadingWorkspaceOrganization) {
      message.info("Organization details are still loading.");
      return;
    }
    const printed = printPaystubDocument({
      payment: selectedPaystubPayment,
      row: selectedPaystubRow,
      period: {
        start: shortDateText(selectedPaystubPeriod.start),
        end: shortDateText(selectedPaystubPeriod.end),
        payDate: selectedPaystubPeriod.payDate ? shortDateText(selectedPaystubPeriod.payDate) : "",
        frequency: selectedPaystubPeriod.frequency,
      },
      employer: selectedPaystubEmployer,
      ytdTotals: selectedPaystubYtdTotals,
      staffName: staffNameFor(selectedPaystubRow.profile),
      staffEmail: staffEmailFor(selectedPaystubRow.profile),
      staffPhone: phoneFor(selectedPaystubRow.profile),
      employeeAddress: paystubEmployeeAddressFor(selectedPaystubRow),
      employerName: paystubEmployerNameFor(selectedPaystubPayment, { employer: selectedPaystubEmployer }),
      employerAddress: paystubEmployerAddressFor(selectedPaystubPayment, { employer: selectedPaystubEmployer }),
      employerContact: [selectedPaystubEmployer.email, selectedPaystubEmployer.phone].map(clean).filter(Boolean).join(" | "),
      timesheetCount: paystubTimesheetsForSelected.length,
      money,
      formatHours,
      readable,
      payRateDisplayParts,
      computedPayrollTaxLinesFor,
      computedPayrollTaxAmountFor,
      hasAnyPayRate,
      regularPayAmountFor,
      overtimePayAmountFor,
      payrollItemAmount,
    });
    if (!printed) {
      message.error("Allow pop-ups to print this paystub.");
    }
  };

  const updateManualPaymentForm = (updates = {}) => {
    setManualPaymentForm((current) => ({ ...current, ...updates }));
  };

  const preparePayrollSignatureCanvas = useCallback(() => {
    const canvas = payrollSignatureCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.floor(rect.width * ratio));
    canvas.height = Math.max(1, Math.floor(rect.height * ratio));
    const context = canvas.getContext("2d");
    if (!context) return;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, rect.width, rect.height);
    context.lineCap = "round";
    context.lineJoin = "round";
    context.lineWidth = 4;
    context.strokeStyle = "#0b63ce";
  }, []);

  const clearPayrollSignatureInk = useCallback(() => {
    preparePayrollSignatureCanvas();
    payrollSignatureDrawingRef.current = false;
    payrollSignatureLastPointRef.current = null;
    setPayrollSignatureHasInk(false);
    setManualPaymentForm((current) => ({ ...current, acknowledgementSignature: "" }));
  }, [preparePayrollSignatureCanvas]);

  const payrollSignaturePointFromEvent = (event) => {
    const canvas = payrollSignatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    };
  };

  const startPayrollSignatureStroke = (event) => {
    const point = payrollSignaturePointFromEvent(event);
    const canvas = payrollSignatureCanvasRef.current;
    const context = canvas?.getContext("2d");
    if (!point || !context) return;
    event.preventDefault();
    canvas.setPointerCapture?.(event.pointerId);
    payrollSignatureDrawingRef.current = true;
    payrollSignatureLastPointRef.current = point;
    context.beginPath();
    context.arc(point.x, point.y, 1.8, 0, Math.PI * 2);
    context.fillStyle = "#0b63ce";
    context.fill();
    setPayrollSignatureHasInk(true);
  };

  const movePayrollSignatureStroke = (event) => {
    if (!payrollSignatureDrawingRef.current) return;
    const point = payrollSignaturePointFromEvent(event);
    const previousPoint = payrollSignatureLastPointRef.current;
    const context = payrollSignatureCanvasRef.current?.getContext("2d");
    if (!point || !previousPoint || !context) return;
    event.preventDefault();
    context.beginPath();
    context.moveTo(previousPoint.x, previousPoint.y);
    context.lineTo(point.x, point.y);
    context.stroke();
    payrollSignatureLastPointRef.current = point;
  };

  const endPayrollSignatureStroke = (event) => {
    payrollSignatureDrawingRef.current = false;
    payrollSignatureLastPointRef.current = null;
    payrollSignatureCanvasRef.current?.releasePointerCapture?.(event.pointerId);
  };

  const capturePayrollSignatureImage = () => {
    const canvas = payrollSignatureCanvasRef.current;
    if (!canvas || !payrollSignatureHasInk) return "";
    const context = canvas.getContext("2d");
    if (!context) return "";
    const { width, height } = canvas;
    const imageData = context.getImageData(0, 0, width, height);
    const pixels = imageData.data;
    let minX = width;
    let minY = height;
    let maxX = 0;
    let maxY = 0;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = (y * width + x) * 4;
        if (pixels[index + 3] > 0) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }
    if (minX > maxX || minY > maxY) return "";
    const padding = Math.round(12 * (window.devicePixelRatio || 1));
    const cropX = Math.max(0, minX - padding);
    const cropY = Math.max(0, minY - padding);
    const cropWidth = Math.min(width - cropX, maxX - minX + padding * 2);
    const cropHeight = Math.min(height - cropY, maxY - minY + padding * 2);
    const normalizedCanvas = document.createElement("canvas");
    normalizedCanvas.width = 360;
    normalizedCanvas.height = 96;
    const normalizedContext = normalizedCanvas.getContext("2d");
    if (!normalizedContext) return canvas.toDataURL("image/png");
    normalizedContext.fillStyle = "#fff";
    normalizedContext.fillRect(0, 0, normalizedCanvas.width, normalizedCanvas.height);
    const targetWidth = normalizedCanvas.width * 0.84;
    const targetHeight = normalizedCanvas.height * 0.68;
    const scale = Math.min(targetWidth / cropWidth, targetHeight / cropHeight);
    const drawWidth = cropWidth * scale;
    const drawHeight = cropHeight * scale;
    const drawX = normalizedCanvas.width * 0.06;
    const drawY = (normalizedCanvas.height - drawHeight) / 2;
    normalizedContext.imageSmoothingEnabled = true;
    normalizedContext.imageSmoothingQuality = "high";
    normalizedContext.drawImage(canvas, cropX, cropY, cropWidth, cropHeight, drawX, drawY, drawWidth, drawHeight);
    return normalizedCanvas.toDataURL("image/jpeg", 0.72);
  };

  const resetManualPaymentForm = () => {
    setManualPaymentForm({
      method: "CASH",
      details: "",
      acknowledgementSignerName: payDrawer ? staffNameFor(payDrawer.profile) : "",
      acknowledgementSignature: "",
      proofFiles: [],
    });
    setPayrollSignatureHasInk(false);
    payrollSignatureDrawingRef.current = false;
    payrollSignatureLastPointRef.current = null;
    window.setTimeout(preparePayrollSignatureCanvas, 0);
  };

  const paymentPayloadFor = (row, extraDetails = {}) => {
    const period = row?.payPeriod || selectedPayWindowPeriod || payDrawerPeriod;
    const schedule = period?.schedule || row?.paymentSchedule || activePayrollSchedule || null;
    const {
      proofDocuments = [],
      acknowledgementSignerName,
      acknowledgementSignature,
      acknowledgementSignatureFileId,
      acknowledgementSignatureType,
      acknowledgedAt,
      acknowledgementAudit,
      ...details
    } = extraDetails;
    const timesheets = (row?.timesheets || payDrawerTimesheets || []);
    const earnings = row?.activeEarnings || [];
    const deductions = row?.activeDeductions || [];
    const computedPayrollTaxes = computedPayrollTaxLinesFor(row);
    const regularPay = regularPayAmountFor(row);
    const overtimePay = Number(row?.overtimeHours || 0) * Number(row?.overtimeRate || 0);
    const totalEarnings = Number(row?.grossPay || 0);
    const federalTaxAmount = Number(row?.federalTax?.amount || 0);
    const totalDeductions = Number(row?.preTaxDeductionsAmount || 0)
      + Number(row?.postTaxDeductionsAmount || 0)
      + computedPayrollTaxAmountFor(row)
      + federalTaxAmount;
    const paymentSchedule = {
      id: schedule?.id || schedule?.scheduleId || null,
      frequency: period?.frequency || schedule?.frequency || "",
      status: schedule?.status || "",
      startsAt: schedule?.startsAt || schedule?.startAt || schedule?.startDate || null,
      firstPayDate: schedule?.firstPayDate || null,
      nextPayDate: period?.payDate ? period.payDate.toISOString() : schedule?.nextPayDate || null,
      periodStart: period?.start ? period.start.toISOString() : payWindow?.[0]?.toISOString?.(),
      periodEnd: period?.end ? period.end.toISOString() : payWindow?.[1]?.toISOString?.(),
    };
    const paystubDetails = {
      employee: {
        userId: row?.userId || userIdFor(row?.profile || {}),
        careProfileId: row?.staffId || profileIdFor(row?.profile || {}),
        name: staffNameFor(row?.profile || {}),
        email: staffEmailFor(row?.profile || {}),
        phone: phoneFor(row?.profile || {}),
        address: employeePaystubAddressFor(row?.profile || {}),
      },
      employer: {
        workspaceId: selectedWorkspaceId,
        name: workspaceNameFor(paystubEmployer),
        address: workspaceAddressFor(paystubEmployer),
        email: workspaceEmailFor(paystubEmployer),
        phone: workspacePhoneFor(paystubEmployer),
      },
      payPeriod: paymentSchedule,
      hours: {
        approved: Number(row?.approvedHours || 0),
        regular: Number(row?.regularHours || 0),
        overtime: Number(row?.overtimeHours || 0),
      },
      payRates: {
        type: row?.payRateType || "",
        regular: Number(row?.hourlyRate || 0),
        overtime: Number(row?.overtimeRate || 0),
      },
      earnings: [
        {
          code: "REGULAR_PAY",
          type: "OTHER",
          name: "Regular pay",
          hours: Number(row?.regularHours || 0),
          rate: Number(row?.hourlyRate || 0),
          amount: regularPay,
        },
        ...(Number(row?.overtimeHours || 0) > 0 ? [{
          code: "OVERTIME_PAY",
          type: "OTHER",
          name: "Overtime pay",
          hours: Number(row?.overtimeHours || 0),
          rate: Number(row?.overtimeRate || 0),
          amount: overtimePay,
        }] : []),
        ...earnings.map((item) => ({
          id: item.id || null,
          code: payrollLineCodeFor(item, item.name),
          type: payrollEarningLineTypeFor(item),
          name: item.name,
          scope: item.scope,
          taxable: item.taxable !== false,
          amountType: item.amountType || item.type || "",
          amount: payrollItemAmount(item, row?.basePay || 0),
        })),
      ],
      deductions: [
        ...deductions.map((item) => ({
          id: item.id || null,
          code: payrollLineCodeFor(item, item.name),
          type: payrollDeductionLineTypeFor(item),
          name: item.name,
          scope: item.scope,
          taxTreatment: item.deductionTaxTreatment || "",
          amountType: item.amountType || item.type || "",
          amount: payrollItemAmount(item, row?.grossPay || 0),
        })),
        ...computedPayrollTaxes.map((item) => ({
          id: item.id || item.code || null,
          code: item.code || null,
          type: "TAX",
          name: item.name,
          scope: item.scope || "WORKSPACE",
          taxTreatment: item.deductionTaxTreatment || item.taxTreatment || "POST_TAX",
          amountType: item.amountType || "FIXED",
          amount: payrollItemAmount(item, row?.grossPay || 0),
          method: item.method || "",
          basis: item.basis,
          rate: item.rate,
          computed: true,
        })),
        {
          code: "FEDERAL_INCOME_TAX",
          type: "TAX",
          name: "Federal income tax",
          amount: federalTaxAmount,
          method: row?.federalTax?.method || "",
        },
      ],
      totals: {
        regularPay,
        overtimePay,
        basePay: Number(row?.basePay || 0),
        totalEarnings,
        grossPay: Number(row?.grossPay || 0),
        preTaxDeductions: Number(row?.preTaxDeductionsAmount || 0),
        postTaxDeductions: Number(row?.postTaxDeductionsAmount || 0),
        federalTax: federalTaxAmount,
        totalDeductions,
        netPay: Number(row?.estimatedPay || 0),
      },
      timesheets: timesheets.map((item) => ({
        id: item.id,
        date: timesheetDate(item),
        start: timesheetStart(item),
        end: timesheetEnd(item),
        hours: timesheetHours(item),
        payCodes: timesheetPayCodes(item),
        reviewReason: timesheetReviewReason(item),
      })).filter((item) => item.id),
    };
    return {
      workspaceId: selectedWorkspaceId,
      userId: row?.userId || userIdFor(row?.profile || {}),
      careProfileId: row?.staffId || profileIdFor(row?.profile || {}),
      staffName: staffNameFor(row?.profile || {}),
      staffEmail: staffEmailFor(row?.profile || {}),
      grossPay: Number(row?.grossPay || 0),
      netPay: Number(row?.estimatedPay || 0),
      grossPayNetPay: {
        grossPay: Number(row?.grossPay || 0),
        netPay: Number(row?.estimatedPay || 0),
      },
      totalDeductions,
      totalDedudctions: totalDeductions,
      totalEarnings,
      paymentSchedule,
      currency: "USD",
      payPeriod: {
        start: paymentSchedule.periodStart,
        end: paymentSchedule.periodEnd,
        payDate: paymentSchedule.nextPayDate,
        frequency: paymentSchedule.frequency,
      },
      approvedHours: Number(row?.approvedHours || 0),
      regularHours: Number(row?.regularHours || 0),
      overtimeHours: Number(row?.overtimeHours || 0),
      timesheetIds: timesheets.map((item) => item.id).filter(Boolean),
      acknowledgementSignerName,
      acknowledgementSignature,
      acknowledgementSignatureFileId,
      acknowledgementSignatureType,
      acknowledgedAt,
      acknowledgementAudit,
      proofDocuments: proofDocuments.length ? proofDocuments : [],
      paymentDetails: {
        ...paystubDetails,
        ...details,
      },
    };
  };

  const paymentRecordIdFor = (record) =>
    record?.id || record?.paymentId || record?.payrollPaymentId || record?.payrollPaymentDetailsId || record?.reference || "";

  const paymentChargeIdsFor = (record = {}) => {
    const details = objectValue(record.paymentDetails);
    const chargeDetails = objectValue(record.paymentChargeDetails || details.paymentChargeDetails);
    const payrollChargeId = firstText(record.stripePaymentChargeId, details.workspaceChargeId, chargeDetails.id);
    if (payrollChargeId) return [payrollChargeId];
    const chargeCandidates = [
      record.chargeId,
      record.paymentChargeId,
      record.paymentChargesId,
      record.stripePaymentChargeId,
      details.chargeId,
      details.paymentChargeId,
      details.workspaceChargeId,
      chargeDetails.chargeId,
      chargeDetails.id,
      chargeDetails.paymentChargeId,
      ...arrayValue(record.chargeIds),
      ...arrayValue(record.paymentChargeIds),
      ...arrayValue(details.chargeIds),
      ...arrayValue(details.paymentChargeIds),
      ...arrayValue(record.charges).map((charge) => charge?.id || charge?.chargeId || charge?.paymentChargeId),
      ...arrayValue(details.charges).map((charge) => charge?.id || charge?.chargeId || charge?.paymentChargeId),
    ];
    return [...new Set(chargeCandidates.map(clean).filter(Boolean))];
  };

  const payrollPayoutDetailsFor = (record = {}) => {
    const details = objectValue(record.paymentDetails);
    const totals = objectValue(details.totals);
    const employee = objectValue(details.employee);
    const payoutMethod = objectValue(record.payoutMethod || details.payoutMethod || record.payeePaymentMethod || details.payeePaymentMethod);
    const payeePaymentMethod = objectValue(record.payeePaymentMethod || details.payeePaymentMethod || payoutMethod);
    const paymentRecordId = paymentRecordIdFor(record);
    const netPay = Number(record.netPay ?? totals.netPay ?? details.netPay ?? paymentAmount(record) ?? 0);
    const grossPay = Number(record.grossPay ?? totals.grossPay ?? details.grossPay ?? netPay);
    return {
      payrollPaymentDetailsId: paymentRecordId,
      paymentId: paymentRecordId,
      chargeIds: paymentChargeIdsFor(record),
      currency: record.currency || details.currency || "USD",
      amount: netPay,
      grossPay,
      netPay,
      payeeReceivableAmount: netPay,
      payeeUserId: record.userId || employee.userId || details.payeeUserId,
      payeeCareProfileId: record.careProfileId || employee.careProfileId || details.payeeCareProfileId,
      payeeWorkspaceId: firstText(record.payeeWorkspaceId, details.payeeWorkspaceId, payoutMethod.payeeWorkspaceId, payoutMethod.workspaceId) || undefined,
      payeePaymentMethod,
      payoutMethod,
      paymentChannel: firstText(details.paymentChannel, "OLIVEEB_PAYMENTS"),
      source: "WORKSPACE_PAYROLL",
    };
  };

  const retryPayrollPayment = async (item = {}) => {
    if (!selectedWorkspaceId) return;
    const paymentId = paymentRecordIdFor(item);
    if (!paymentId) {
      message.error("Unable to retry this payroll payment because the payment record id is missing.");
      return;
    }
    try {
      setRetryingPayrollPaymentId(paymentId);
      setError("");
      let paymentRecord = await workspacePayrollService.getPayment({
        actorId,
        workspaceId: selectedWorkspaceId,
        paymentId,
      });
      if (isWorkspacePaymentPaid(paymentRecord) && isPaidPayoutStatus(payoutStatusText(paymentRecord))) {
        message.info("This payroll payment and payout are already paid.");
        return;
      }
      if (!isWorkspacePaymentPaid(paymentRecord)) {
        paymentRecord = await workspacePayrollService.retryPaymentCharge({
          actorId,
          workspaceId: selectedWorkspaceId,
          paymentId,
        });
      }
      const payoutDetails = payrollPayoutDetailsFor(paymentRecord);
      if (!payoutDetails.chargeIds.length) {
        throw new Error("Payroll payout retry requires a successful workspace charge id.");
      }
      const payoutResult = await workspacePayrollService.initiatePayrollPayout({
        actorId,
        workspaceId: selectedWorkspaceId,
        paymentId,
        payoutDetails,
      });
      const payoutStatus = payoutStatusText(payoutResult || paymentRecord);
      if (paymentStatusColor(payoutStatus) === "red") {
        message.warning(payrollPayoutMessage(payoutResult || paymentRecord));
      } else {
        message.success(payrollPayoutMessage(payoutResult || paymentRecord));
      }
      await loadStaffDetails();
    } catch (nextError) {
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to retry this payroll payment.");
    } finally {
      setRetryingPayrollPaymentId("");
    }
  };

  const markIncludedTimesheetsPaid = async (row, paymentRecord) => {
    const timesheets = (row?.timesheets || payDrawerTimesheets || []).filter((item) => item?.id);
    if (!timesheets.length) return;
    const paymentRecordId = paymentRecordIdFor(paymentRecord);
    await Promise.all(timesheets.map((item) => careService.timesheets.update(item.id, {
      workspaceId: selectedWorkspaceId,
      status: "PAID",
      statusReason: "Paid through payroll.",
      reason: "Paid through payroll.",
      notes: item.notes,
      payrollPaymentDetailsId: paymentRecordId || undefined,
      payrollPaymentReference: paymentRecordId || undefined,
    }, {
      actorId,
      wid: selectedWorkspaceId,
      workspaceId: selectedWorkspaceId,
    })));
  };

  const openManualPaymentDrawer = () => {
    resetManualPaymentForm();
    setManualPaymentDrawerOpen(true);
  };

  const closeManualPaymentDrawer = () => {
    setManualPaymentDrawerOpen(false);
    resetManualPaymentForm();
  };

  const uploadPayrollSignature = async (signatureDataUrl, row) => {
    const signatureFile = await dataUrlToFile(
      signatureDataUrl,
      `payroll-signature-${row?.staffId || row?.userId || Date.now()}.jpg`,
    );
    const upload = await uploadFile(null, signatureFile, {
      confirmUpload: false,
      fileType: "PHOTO",
      includeUserId: false,
      preserveOriginal: true,
      purpose: "PAYROLL_PAYMENT_SIGNATURE",
      skipUserValidation: true,
    });
    return {
      fileId: upload?.fileId || upload?.file?.id || null,
      name: signatureFile.name,
    };
  };

  const uploadManualPaymentProofDocuments = async (proofFiles = [], purpose = "PAYROLL_PAYMENT_PROOF") => {
    const uploadedDocuments = [];
    for (const file of proofFiles) {
      const upload = await uploadFile(null, file, {
        confirmUpload: false,
        fileType: fileTypeForPayrollUpload(file),
        includeUserId: false,
        preserveOriginal: true,
        purpose,
        skipUserValidation: true,
      });
      uploadedDocuments.push({
        fileId: upload?.fileId || upload?.file?.id || null,
        name: file.name,
      });
    }
    return uploadedDocuments;
  };

  const beforeManualPaymentProofUpload = (file) => {
    if (file?.size > PAYROLL_PROOF_MAX_FILE_SIZE_BYTES) {
      message.error(`Payment proof files must be ${PAYROLL_PROOF_MAX_FILE_SIZE_MB} MB or smaller.`);
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  const saveManualPayment = async () => {
    if (!payDrawer || !selectedWorkspaceId) return;
    const acknowledgementSignerName = clean(manualPaymentForm.acknowledgementSignerName) || staffNameFor(payDrawer.profile);
    if (!acknowledgementSignerName) {
      message.error("Employee signer name is required.");
      return;
    }
    const acknowledgementSignature = capturePayrollSignatureImage();
    if (!acknowledgementSignature) {
      message.error("Employee acknowledgement signature is required.");
      return;
    }
    const proofFiles = (manualPaymentForm.proofFiles || []).map((file) => file.originFileObj || file).filter(Boolean);
    const acknowledgedAt = new Date().toISOString();
    let acknowledgementSignatureFile = null;
    let proofDocuments = [];
    try {
      setSavingManualPayment(true);
      setError("");
      acknowledgementSignatureFile = await uploadPayrollSignature(acknowledgementSignature, payDrawer);
      if (!acknowledgementSignatureFile?.fileId) {
        throw new Error("Unable to upload employee acknowledgement signature.");
      }
      proofDocuments = await uploadManualPaymentProofDocuments(proofFiles);
    } catch (nextError) {
      setSavingManualPayment(false);
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to upload payment files.");
      return;
    }
    const paymentDetails = paymentPayloadFor(payDrawer, {
      paymentType: "MANUAL",
      method: manualPaymentForm.method,
      details: clean(manualPaymentForm.details),
      paymentMethodDetails: manualPaymentMethodDetails(manualPaymentForm.method, manualPaymentForm.details),
      acknowledgementSignerName,
      acknowledgementSignature: "",
      acknowledgementSignatureFileId: acknowledgementSignatureFile.fileId,
      acknowledgementSignatureType: "STORAGE_FILE_ID",
      acknowledgedAt,
      acknowledgementAudit: {
        signerName: acknowledgementSignerName,
        signedAt: acknowledgedAt,
        signedByUserId: user?.id || user?.userId || user?.uid || "",
        signedByName: user?.name || user?.fullName || user?.displayName || "",
        signedByEmail: user?.email || "",
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        pageUrl: typeof window !== "undefined" ? window.location.href : "",
      },
      signatureDocument: acknowledgementSignatureFile,
      proofDocuments,
    });
    try {
      const paymentRecord = await workspacePayrollService.createManualPayment({
        actorId,
        workspaceId: selectedWorkspaceId,
        paymentDetails,
      });
      await markIncludedTimesheetsPaid(payDrawer, paymentRecord);
      message.success("Payment confirmed and included timesheets marked as paid.");
      closeManualPaymentDrawer();
      setPayDrawer(null);
      loadPayStaff();
      loadStaffDetails();
    } catch (nextError) {
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to mark this payment as paid.");
    } finally {
      setSavingManualPayment(false);
    }
  };

  const openPayNowDialog = async () => {
    if (!payDrawer || !selectedWorkspaceId) return;
    setPayNowDialog({
      open: true,
      row: payDrawer,
      payerPaymentMethods: [],
      loadingMethods: true,
    });
    try {
      const methods = await paymentMethodsService.outgoing.list({
        actorId,
        organizationId: selectedWorkspace?.organizationId || selectedWorkspace?.organization?.id,
        workspaceId: selectedWorkspaceId,
        size: 100,
      });
      const nextMethods = Array.isArray(methods) ? methods : [];
      setPayNowDialog((current) => ({
        ...current,
        payerPaymentMethods: nextMethods,
        loadingMethods: false,
      }));
    } catch (nextError) {
      setPayNowDialog((current) => ({ ...current, loadingMethods: false }));
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to load workspace payment methods.");
    }
  };

  const closePayNowDialog = () => {
    setPayNowDialog({
      open: false,
      row: null,
      payerPaymentMethods: [],
      loadingMethods: false,
    });
  };

  const initiatePayNow = async () => {
    const row = payNowDialog.row || payDrawer;
    if (!row || !selectedWorkspaceId) return;
    if (!selectedPayNowPayerMethod) {
      message.error("Set a default workspace payment method before Pay With OCare can start.");
      return;
    }
    if (!selectedPayNowPayeeMethod) {
      message.error("The employee needs a linked payout payment method before Pay With OCare can start.");
      return;
    }
    try {
      setInitiatingPayment(true);
      setError("");
      const phaseOnePaymentDetails = paymentPayloadFor(row, {
        paymentType: "PAY_NOW",
        paymentChannel: "OLIVEEB_PAYMENTS",
        requestedStatus: "SCHEDULED",
        paymentStatus: "SCHEDULED",
        payoutStatus: "PENDING",
        payerPaymentMethod: paymentMethodSummary(selectedPayNowPayerMethod),
        payeePaymentMethod: paymentMethodSummary(selectedPayNowPayeeMethod),
        paymentMethodDetails: {
          paymentChannel: "OLIVEEB_PAYMENTS",
          payerPaymentMethod: paymentMethodSummary(selectedPayNowPayerMethod),
          payeePaymentMethod: paymentMethodSummary(selectedPayNowPayeeMethod),
        },
        stripeAdditionalCharges: payNowChargeEstimate,
        paymentChargeDetails: {
          currentChargeAmount: payNowChargeEstimate.totalCharge,
          originalChargeAmount: payNowChargeEstimate.principalAmount,
          grossPay: payNowChargeEstimate.grossPay,
          netPay: payNowChargeEstimate.netPay,
          payeeReceivableAmount: payNowChargeEstimate.netPay,
          payerPaymentMethod: paymentMethodSummary(selectedPayNowPayerMethod),
          payeePaymentMethod: paymentMethodSummary(selectedPayNowPayeeMethod),
          paymentProcessorFeeRate: payNowChargeEstimate.paymentProcessorFeeRate,
          paymentProcessorFee: payNowChargeEstimate.paymentProcessorFee,
          paymentFeeConstant: payNowChargeEstimate.paymentFeeConstant,
          totalPaymentProcessorFee: payNowChargeEstimate.totalPaymentProcessorFee,
          oliveebProcessingFeeRate: payNowChargeEstimate.oliveebProcessingFeeRate,
          oliveebProcessingFee: payNowChargeEstimate.oliveebProcessingFee,
          currency: payNowChargeEstimate.currency,
        },
      });
      const paymentRecord = await workspacePayrollService.initiatePayment({
        actorId,
        workspaceId: selectedWorkspaceId,
        paymentDetails: {
          ...phaseOnePaymentDetails,
          status: "SCHEDULED",
          paymentStatus: "SCHEDULED",
          payoutStatus: "PENDING",
        },
      });
      await markIncludedTimesheetsPaid(row, paymentRecord);
      const paymentRecordId = paymentRecordIdFor(paymentRecord);
      let payoutResult = null;
      if (!paymentRecordId) {
        message.warning("Workspace funds were deducted and timesheets were marked paid, but no payroll payment record ID was returned for payout initiation.");
      } else {
        try {
          payoutResult = await workspacePayrollService.initiatePayrollPayout({
            actorId,
            workspaceId: selectedWorkspaceId,
            paymentId: paymentRecordId,
            payoutDetails: {
              payrollPaymentDetailsId: paymentRecordId,
              paymentId: paymentRecordId,
              chargeIds: paymentChargeIdsFor(paymentRecord),
              currency: payNowChargeEstimate.currency,
              amount: payNowChargeEstimate.netPay,
              grossPay: payNowChargeEstimate.grossPay,
              netPay: payNowChargeEstimate.netPay,
              payeeReceivableAmount: payNowChargeEstimate.netPay,
              payeeUserId: row.userId || userIdFor(row.profile || {}),
              payeeCareProfileId: row.staffId || profileIdFor(row.profile || {}),
              payeeWorkspaceId: firstText(selectedPayNowPayeeMethod.payeeWorkspaceId, selectedPayNowPayeeMethod.workspaceId) || undefined,
              payeePaymentMethod: paymentMethodSummary(selectedPayNowPayeeMethod),
              payoutMethod: paymentMethodSummary(selectedPayNowPayeeMethod),
              paymentChannel: "OLIVEEB_PAYMENTS",
              source: "WORKSPACE_PAYROLL",
            },
          });
          const payoutStatus = payoutStatusText(payoutResult || paymentRecord);
          if (paymentStatusColor(payoutStatus) === "red") {
            message.warning(payrollPayoutMessage(payoutResult || paymentRecord));
          } else {
            message.success(payrollPayoutMessage(payoutResult || paymentRecord));
          }
        } catch (payoutError) {
          message.warning("Workspace funds were deducted and timesheets were marked paid, but employee payout initiation failed.");
          setError(payoutError?.response?.data?.message || payoutError?.message || "Unable to initiate the employee payout.");
        }
      }
      closePayNowDialog();
      setPayDrawer(null);
      await loadPayStaff();
      await loadStaffDetails();
    } catch (nextError) {
      setError(nextError?.response?.data?.message || nextError?.message || "Unable to start the payment.");
    } finally {
      setInitiatingPayment(false);
    }
  };

  const changeTab = (nextTab) => {
    setActiveTab(nextTab);
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      if (nextTab === DEFAULT_PAYROLL_TAB) {
        nextParams.delete("tab");
      } else {
        nextParams.set("tab", nextTab);
      }
      return nextParams;
    });
  };

  const loadPayRateRows = useCallback(async (row) => {
    const targetUserId = row?.userId || userIdFor(row?.profile || {});
    if (!actorId || !targetUserId) {
      setPayRateRows([]);
      return;
    }
    try {
      setLoadingPayRates(true);
      const rows = await userPayrollService.listPayRates({ actorId, userId: targetUserId, workspaceId: selectedWorkspaceId });
      setPayRateRows(Array.isArray(rows) ? rows : pageContent(rows));
    } catch (nextError) {
      setPayRateRows([]);
      setError(nextError?.message || "Unable to load staff pay rates.");
    } finally {
      setLoadingPayRates(false);
    }
  }, [actorId, selectedWorkspaceId]);

  const openPayRateDrawer = (row) => {
    const currentRate = row?.payRate || null;
    setPayRateDrawer(row);
    setPayRateForm({
      amount: payRateAmount(currentRate) || row?.hourlyRate || null,
      type: currentRate?.type || "HOURLY",
      rateKind: currentRate?.rateKind || "REGULAR",
      status: "ACTIVE",
      startAt: dayjs(),
    });
    loadPayRateRows(row);
  };

  const savePayRate = async () => {
    const targetUserId = payRateDrawer?.userId || userIdFor(payRateDrawer?.profile || {});
    if (!targetUserId) {
      setError("This staff member is missing a user record.");
      return;
    }
    if (!payRateForm.amount || !payRateForm.startAt) {
      setError("Enter an amount and start date for the pay rate.");
      return;
    }
    try {
      setSavingPayRate(true);
      setError("");
      await userPayrollService.createPayRate({
        actorId,
        userId: targetUserId,
        workspaceId: selectedWorkspaceId,
        payRate: {
          amount: payRateForm.amount,
          type: payRateForm.type,
          rateKind: payRateForm.rateKind,
          status: payRateForm.status,
          startAt: payRateForm.startAt.toISOString(),
        },
      });
      const nextRows = await userPayrollService.listPayRates({ actorId, userId: targetUserId, workspaceId: selectedWorkspaceId });
      const normalizedRows = Array.isArray(nextRows) ? nextRows : pageContent(nextRows);
      const currentRate = activePayRateFor(normalizedRows, "REGULAR");
      const currentOvertimeRate = activePayRateFor(normalizedRows, "OVERTIME");
      setPayRateRows(normalizedRows);
      setPayRateDrawer((current) => current ? {
        ...current,
        payRate: currentRate || current.payRate || null,
        overtimePayRate: currentOvertimeRate || current.overtimePayRate || null,
        payRates: normalizedRows,
        hourlyRate: payRateAmount(currentRate) || current.hourlyRate,
        overtimeRate: payRateAmount(currentOvertimeRate) || current.overtimeRate || payRateAmount(currentRate) || current.hourlyRate,
      } : current);
      setPayDrawer((current) => current && (current.userId === targetUserId || userIdFor(current.profile) === targetUserId) ? {
        ...current,
        payRate: currentRate || current.payRate || null,
        overtimePayRate: currentOvertimeRate || current.overtimePayRate || null,
        payRates: normalizedRows,
        hourlyRate: payRateAmount(currentRate) || current.hourlyRate,
        overtimeRate: payRateAmount(currentOvertimeRate) || current.overtimeRate || payRateAmount(currentRate) || current.hourlyRate,
        estimatedPay: computeEstimatedPay({
          regularHours: current.regularHours,
          overtimeHours: current.overtimeHours,
          regularRate: payRateAmount(currentRate) || current.hourlyRate || 0,
          overtimeRate: payRateAmount(currentOvertimeRate) || current.overtimeRate || payRateAmount(currentRate) || current.hourlyRate || 0,
          payRateType: payRateType(currentRate || current.payRate),
        }),
      } : current);
      await loadPayStaff();
      message.success("Staff pay rate saved.");
      setPayRateDrawer(null);
    } catch (nextError) {
      setError(nextError?.message || "Unable to save staff pay rate.");
    } finally {
      setSavingPayRate(false);
    }
  };

  const updatePayrollItemForm = (kind, patch) => {
    setPayrollItemForms((current) => ({
      ...current,
      [kind]: {
        ...current[kind],
        ...patch,
      },
    }));
  };

  const resetPayrollItemForm = (kind) => {
    setPayrollItemForms((current) => ({
      ...current,
      [kind]: {
        scope: "WORKSPACE",
        userId: "",
        name: "",
        code: "",
        description: "",
        amount: null,
        amountType: "FIXED",
        status: "ACTIVE",
        taxable: true,
        deductionTaxTreatment: "POST_TAX",
        startAt: dayjs(),
        endAt: null,
      },
    }));
  };

  const openPayrollItemDrawer = (kind) => {
    resetPayrollItemForm(kind);
    setPayrollItemDrawerKind(kind);
  };

  const savePayrollItem = async (kind) => {
    const form = payrollItemForms[kind];
    if (!selectedWorkspaceId) {
      setError("Select a workspace before adding payroll items.");
      return;
    }
    if (!clean(form.name) || form.amount === null || form.amount === undefined || !form.startAt) {
      setError("Enter a name, amount, and start date.");
      return;
    }
    if (form.scope === "USER" && !form.userId) {
      setError("Select a staff member for a staff specific payroll item.");
      return;
    }
    try {
      setSavingPayrollItemKind(kind);
      setError("");
      await workspacePayrollService.createItem({
        actorId,
        workspaceId: selectedWorkspaceId,
        item: {
          kind,
          scope: form.scope,
          userId: form.scope === "USER" ? form.userId : null,
          name: clean(form.name),
          code: clean(form.code),
          description: clean(form.description),
          amount: form.amount,
          amountType: form.amountType,
          status: form.status,
          taxable: kind === "EARNING" ? form.taxable : null,
          deductionTaxTreatment: kind === "DEDUCTION" ? form.deductionTaxTreatment : null,
          startAt: form.startAt.toISOString(),
          endAt: form.endAt ? form.endAt.toISOString() : null,
        },
      });
      resetPayrollItemForm(kind);
      await loadPayrollItems();
      message.success(`${kind === "EARNING" ? "Earning" : "Deduction"} saved.`);
      setPayrollItemDrawerKind("");
    } catch (nextError) {
      setError(nextError?.message || "Unable to save payroll item.");
    } finally {
      setSavingPayrollItemKind("");
    }
  };

  const updatePayrollScheduleForm = (patch) => {
    setPayrollScheduleForm((current) => {
      const next = {
        ...current,
        ...patch,
      };
      if (Object.prototype.hasOwnProperty.call(patch, "frequency") || Object.prototype.hasOwnProperty.call(patch, "startAt")) {
        next.firstPayDate = computedFirstPayDate(next.frequency, next.startAt);
      }
      return next;
    });
  };

  const resetPayrollScheduleForm = () => {
    const startAt = dayjs();
    setPayrollScheduleForm({
      frequency: "BIWEEKLY",
      status: "ACTIVE",
      startAt,
      firstPayDate: computedFirstPayDate("BIWEEKLY", startAt),
      description: "",
    });
  };

  const savePayrollSchedule = async () => {
    if (!selectedWorkspaceId) {
      setError("Select a workspace before adding a payment schedule.");
      return;
    }
    if (!payrollScheduleForm.frequency || !payrollScheduleForm.startAt) {
      setError("Select a schedule and start date.");
      return;
    }
    try {
      setSavingPayrollSchedule(true);
      setError("");
      await workspacePayrollService.createSchedule({
        actorId,
        workspaceId: selectedWorkspaceId,
        schedule: {
          frequency: payrollScheduleForm.frequency,
          status: payrollScheduleForm.status,
          startAt: payrollScheduleForm.startAt.toISOString(),
          firstPayDate: payrollScheduleForm.firstPayDate ? payrollScheduleForm.firstPayDate.toISOString() : null,
          description: clean(payrollScheduleForm.description),
        },
      });
      resetPayrollScheduleForm();
      await loadPayrollSchedules();
      message.success("Payment schedule saved.");
      setPayrollScheduleDrawerOpen(false);
    } catch (nextError) {
      setError(nextError?.message || "Unable to save payment schedule.");
    } finally {
      setSavingPayrollSchedule(false);
    }
  };

  const openPayrollScheduleDrawer = () => {
    setPayrollScheduleDrawerOpen(true);
  };

  const updateWashingtonLiForm = (patch) => {
    setWashingtonLiForm((current) => {
      const next = { ...current, ...patch };
      const hasFundRatePatch = ["medicalAidRate", "stayAtWorkRate", "supplementalPensionRate"].some((key) =>
        Object.prototype.hasOwnProperty.call(patch, key),
      );
      if (hasFundRatePatch) {
        const medicalAid = Number(next.medicalAidRate || 0);
        const stayAtWork = Number(next.stayAtWorkRate || 0);
        const supplementalPension = Number(next.supplementalPensionRate || 0);
        next.employeeHourlyRate = Math.round(((medicalAid + stayAtWork + supplementalPension) / 2) * 100000) / 100000;
      }
      return next;
    });
  };

  const resetWashingtonLiForm = () => {
    setWashingtonLiForm({
      riskClassCode: "",
      riskClassDescription: "",
      employeeHourlyRate: null,
      medicalAidRate: null,
      stayAtWorkRate: null,
      supplementalPensionRate: null,
      status: "ACTIVE",
      startAt: dayjs(),
      endAt: null,
      notes: "",
    });
  };

  const openWashingtonLiDrawer = () => {
    resetWashingtonLiForm();
    setWashingtonLiDrawerOpen(true);
  };

  const saveWashingtonLiConfig = async () => {
    if (!selectedWorkspaceId) {
      setError("Select a workspace before adding WA L&I configuration.");
      return;
    }
    if (washingtonLiForm.employeeHourlyRate === null || washingtonLiForm.employeeHourlyRate === undefined) {
      setError("Enter the employee-share L&I rate per hour or the L&I fund rates.");
      return;
    }
    if (!washingtonLiForm.startAt) {
      setError("Select the WA L&I start date.");
      return;
    }
    try {
      setSavingWashingtonLiConfig(true);
      setError("");
      await workspacePayrollService.createWashingtonLiConfig({
        actorId,
        workspaceId: selectedWorkspaceId,
        config: {
          riskClassCode: clean(washingtonLiForm.riskClassCode),
          riskClassDescription: clean(washingtonLiForm.riskClassDescription),
          employeeHourlyRate: washingtonLiForm.employeeHourlyRate,
          medicalAidRate: washingtonLiForm.medicalAidRate,
          stayAtWorkRate: washingtonLiForm.stayAtWorkRate,
          supplementalPensionRate: washingtonLiForm.supplementalPensionRate,
          status: washingtonLiForm.status,
          startAt: washingtonLiForm.startAt.toISOString(),
          endAt: washingtonLiForm.endAt ? washingtonLiForm.endAt.toISOString() : null,
          notes: clean(washingtonLiForm.notes),
        },
      });
      resetWashingtonLiForm();
      await loadWashingtonLiConfigs();
      message.success("WA L&I configuration saved.");
      setWashingtonLiDrawerOpen(false);
    } catch (nextError) {
      setError(nextError?.message || "Unable to save WA L&I configuration.");
    } finally {
      setSavingWashingtonLiConfig(false);
    }
  };

  const staffSelector = (
    <Card size="small" style={{ borderRadius: 8, marginBottom: 14 }}>
      <Space direction="vertical" size={8} style={{ width: "100%" }}>
        <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Staff member</Text>
        <Select
          showSearch
          value={selectedStaffId || undefined}
          placeholder="Select staff"
          loading={loadingStaff}
          options={staffOptions}
          optionFilterProp="label"
          style={{ width: "100%" }}
          onChange={setSelectedStaffId}
          notFoundContent="No staff found"
        />
        {isAllStaffSelected ? (
          <Text type="secondary">Showing payroll details for all staff in this workspace.</Text>
        ) : selectedStaff ? (
          <Text type="secondary">{staffEmailFor(selectedStaff) || "No email on record"}</Text>
        ) : null}
      </Space>
    </Card>
  );

  const timesheetList = loadingDetails ? (
    <Skeleton active paragraph={{ rows: 5 }} />
  ) : approvedTimesheetsInPayWindow.length ? (
    <List
      dataSource={approvedTimesheetsInPayWindow}
      renderItem={(item) => (
        <List.Item key={item.id || `${timesheetDate(item)}-${timesheetHours(item)}`}>
          <div className="my-payments-list-row" style={{ gap: 16 }}>
            <div style={{ minWidth: 220 }}>
              <Text strong>{timesheetStaffName(item)}</Text>
              <div><Text type="secondary">{timesheetStaffEmail(item) || "No email on record"}</Text></div>
            </div>
            <Space wrap size={[10, 6]} style={{ flex: 1 }}>
              <Text>{shortDateText(timesheetDate(item))}</Text>
              <Text type="secondary" style={{ marginRight: 10 }}>{dateText(timesheetStart(item))} - {dateText(timesheetEnd(item))}</Text>
              {(timesheetPayCodes(item).length ? timesheetPayCodes(item) : ["Regular"]).map((code) => (
                <Tag color="blue" key={code}>{code}</Tag>
              ))}
              {timesheetReviewReason(item) ? <Text type="secondary">Review reason: {timesheetReviewReason(item)}</Text> : null}
              {timesheetNotes(item) ? <Text type="secondary">{timesheetNotes(item)}</Text> : null}
            </Space>
            <Text strong style={{ color: OLIVEEB_COLORS.oliveebBlue, whiteSpace: "nowrap" }}>{formatHours(timesheetHours(item))}</Text>
          </div>
        </List.Item>
      )}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No approved timesheets found for this pay range." />
  );

  const paymentList = loadingDetails ? (
    <Skeleton active paragraph={{ rows: 4 }} />
  ) : issuedPaymentsInPayWindow.length ? (
    <List
      dataSource={issuedPaymentsInPayWindow}
      renderItem={(item) => (
        <List.Item key={item.id || item.createdAt}>
          <div className="my-payments-list-row">
            <div>
              <Text strong>{paymentTitle(item)}</Text>
              <Space wrap size={[4, 4]} style={{ marginTop: 5 }}>
                <Tag color={paymentStatusColor(paymentStatusText(item))}>{readable(paymentStatusText(item), "Status")}</Tag>
                {hasPayoutStatus(item) ? (
                  <Tag color={paymentStatusColor(payoutStatusText(item))}>
                    Payout: {readable(payoutStatusText(item), "Pending")}
                  </Tag>
                ) : null}
                {payoutTypeText(item) ? <Tag color="cyan">Payout type: {readable(payoutTypeText(item), "Method")}</Tag> : null}
                {item.paymentType ? <Tag color="green">{readable(item.paymentType, "Payment")}</Tag> : null}
                {item.staffEmail ? <Tag>{item.staffEmail}</Tag> : null}
                <Tag>{dateText(item.createdAt || item.updatedAt)}</Tag>
              </Space>
            </div>
            <Space direction="vertical" size={0} align="end">
              <Text strong style={{ color: OLIVEEB_COLORS.oliveebBlue }}>{money(paymentAmount(item), item.currency)}</Text>
              <Space size={6} wrap style={{ justifyContent: "flex-end" }}>
                {canRetryPayrollPayment(item) ? (
                  <Button
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={retryingPayrollPaymentId === paymentRecordIdFor(item)}
                    onClick={() => retryPayrollPayment(item)}
                  >
                    Retry
                  </Button>
                ) : null}
                <Button
                  className="payroll-blue-button"
                  size="small"
                  icon={<FileTextOutlined />}
                  loading={loadingPayrollPaymentDetail === item.id}
                  onClick={() => openIssuedPaymentDetails(item)}
                >
                  View
                </Button>
              </Space>
            </Space>
          </div>
        </List.Item>
      )}
    />
  ) : (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="No issued payments found for this pay range."
    />
  );

  const togglePayStaff = (staffId, checked) => {
    setIncludedPayStaffIds((current) =>
      checked ? [...new Set([...current, staffId])] : current.filter((id) => id !== staffId),
    );
  };

  const payStaffList = loadingPayStaff ? (
    <Skeleton active paragraph={{ rows: 6 }} />
  ) : payStaffRows.length ? (
    <List
      className="payroll-pay-staff-list"
      dataSource={payStaffRows}
      renderItem={(row) => {
        const primaryMethod = row.paymentMethods[0] || null;
        const payrollTaxes = computedPayrollTaxLinesFor(row);
        const payrollTaxTotal = computedPayrollTaxAmountFor(row);
        return (
          <List.Item key={row.staffId || row.userId}>
            <div className="payroll-pay-staff-row">
              <Checkbox
                className="payroll-pay-staff-check"
                checked={includedPayStaffIds.includes(row.staffId)}
                onChange={(event) => togglePayStaff(row.staffId, event.target.checked)}
                aria-label={`Include ${staffNameFor(row.profile)} in payment`}
              />
              <div className="payroll-pay-staff-person">
                <Text strong>{staffNameFor(row.profile)}</Text>
                <div><Text type="secondary">{staffEmailFor(row.profile) || "No email on record"}</Text></div>
              </div>
              <div className="payroll-pay-staff-metrics">
                <span>
                  <strong>{formatHours(row.approvedHours)}</strong>
                  {row.overtimeHours > 0 ? (
                    <em>{formatHours(row.regularHours)} regular + {formatHours(row.overtimeHours)} overtime</em>
                  ) : null}
                  <small>Approved hours</small>
                </span>
                <span>
                  <strong className="payroll-payrate-value">
                    <span className="payroll-payrate-lines">
                      {payRateDisplayParts(row).map((label) => (
                        <em key={label}>{label}</em>
                      ))}
                    </span>
                    {row.staffId ? (
                      <button
                        className="payroll-payrate-edit"
                        type="button"
                        title="Edit pay rate"
                        aria-label={`Edit pay rate for ${staffNameFor(row.profile)}`}
                        onClick={() => openPayRateDrawer(row)}
                      >
                        <SyncOutlined />
                      </button>
                    ) : null}
                  </strong>
                  <small>Pay rate</small>
                </span>
                <span>
                  <strong>{payrollTaxes.length ? money(payrollTaxTotal) : "-"}</strong>
                  {payrollTaxes.length ? (
                    <em>{payrollTaxes.map((item) => `${item.name}: ${money(payrollItemAmount(item, row.grossPay))}`).join(" | ")}</em>
                  ) : null}
                  <small>Payroll taxes</small>
                </span>
                <span><strong>{hasAnyPayRate(row) ? money(row.estimatedPay) : "-"}</strong><small>Estimated pay</small></span>
                <span className={primaryMethod ? "" : "is-muted"}><strong>{primaryMethod ? methodLabel(primaryMethod) : "Not linked"}</strong><small>Payment method</small></span>
              </div>
              <Button className="payroll-blue-button" type="default" onClick={() => setPayDrawer(row)}>
                Details
              </Button>
            </div>
          </List.Item>
        );
      }}
    />
  ) : (
    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No staff found for this pay window." />
  );

  const payrollAdjustmentForm = (kind) => {
    const form = payrollItemForms[kind];
    const noun = kind === "EARNING" ? "earning" : "deduction";
    const title = kind === "EARNING" ? "Earnings" : "Deductions";
    return (
      <>
        <div className="payroll-adjustment-form">
          <div className="payroll-adjustment-group">
            <div className="payroll-adjustment-group-title">
              <strong>Who this applies to</strong>
              <small>Choose whether this is shared by the workspace or assigned to one staff member.</small>
            </div>
            <div className="payroll-adjustment-grid payroll-adjustment-grid-two">
              <label className="payroll-adjustment-scope">
                <span>Applies to</span>
                <Select
                  value={form.scope}
                  options={[
                    { value: "WORKSPACE", label: "All staff in workspace" },
                    { value: "USER", label: "One staff member" },
                  ]}
                  onChange={(value) => updatePayrollItemForm(kind, { scope: value, userId: "" })}
                />
              </label>
              {form.scope === "USER" ? (
                <label className="payroll-adjustment-staff">
                  <span>Staff member</span>
                  <Select
                    showSearch
                    value={form.userId || undefined}
                    placeholder="Select staff"
                    options={staffUserOptions}
                    optionFilterProp="label"
                    onChange={(value) => updatePayrollItemForm(kind, { userId: value })}
                  />
                </label>
              ) : (
                <div className="payroll-adjustment-scope-note">
                  <TeamOutlined />
                  <span>Applies to all staff in this workspace.</span>
                </div>
              )}
            </div>
          </div>

          <div className="payroll-adjustment-group">
            <div className="payroll-adjustment-group-title">
              <strong>{title.slice(0, -1)} details</strong>
              <small>Name the item and enter how the amount should be calculated.</small>
            </div>
            <div className="payroll-adjustment-grid payroll-adjustment-grid-item">
              <label className="payroll-adjustment-name">
                <span>Name</span>
                <Input
                  value={form.name}
                  placeholder={kind === "EARNING" ? "Bonus, stipend, holiday pay" : "Loan repayment, uniform deduction"}
                  onChange={(event) => updatePayrollItemForm(kind, { name: event.target.value })}
                />
              </label>
              <label className="payroll-adjustment-code">
                <span>Code</span>
                <Input
                  value={form.code}
                  placeholder={kind === "EARNING" ? "BONUS" : "WA_CARES"}
                  onChange={(event) => updatePayrollItemForm(kind, { code: event.target.value })}
                />
              </label>
              <label className="payroll-adjustment-amount">
                <span>Amount</span>
                <InputNumber
                  min={0}
                  precision={2}
                  value={form.amount}
                  prefix={form.amountType === "FIXED" ? "$" : undefined}
                  suffix={form.amountType === "PERCENT" ? "%" : undefined}
                  style={{ width: "100%" }}
                  onChange={(value) => updatePayrollItemForm(kind, { amount: value })}
                />
              </label>
              <label className="payroll-adjustment-amount-type">
                <span>Amount type</span>
                <Select
                  value={form.amountType}
                  options={[
                    { value: "FIXED", label: "Fixed amount" },
                    { value: "PERCENT", label: "Percent" },
                  ]}
                  onChange={(value) => updatePayrollItemForm(kind, { amountType: value })}
                />
              </label>
            </div>
          </div>

          <div className="payroll-adjustment-group">
            <div className="payroll-adjustment-group-title">
              <strong>Tax and timing</strong>
              <small>Set the active dates and how this item should be handled for payroll tax.</small>
            </div>
            <div className="payroll-adjustment-grid payroll-adjustment-grid-four">
              <label>
                <span>Status</span>
                <Select
                  value={form.status}
                  options={[
                    { value: "ACTIVE", label: "Active" },
                    { value: "INACTIVE", label: "Inactive" },
                  ]}
                  onChange={(value) => updatePayrollItemForm(kind, { status: value })}
                />
              </label>
              {kind === "EARNING" ? (
                <label>
                  <span>Taxable</span>
                  <Select
                    value={form.taxable}
                    options={[
                      { value: true, label: "Taxable" },
                      { value: false, label: "Not taxable" },
                    ]}
                    onChange={(value) => updatePayrollItemForm(kind, { taxable: value })}
                  />
                </label>
              ) : (
                <label>
                  <span>Tax timing</span>
                  <Select
                    value={form.deductionTaxTreatment}
                    options={[
                      { value: "POST_TAX", label: "After tax" },
                      { value: "PRE_TAX", label: "Before tax" },
                    ]}
                    onChange={(value) => updatePayrollItemForm(kind, { deductionTaxTreatment: value })}
                  />
                </label>
              )}
              <label>
                <span>Start</span>
                <DatePicker
                  value={form.startAt}
                  format="MM/DD/YYYY"
                  style={{ width: "100%" }}
                  onChange={(value) => updatePayrollItemForm(kind, { startAt: value })}
                />
              </label>
              <label>
                <span>End</span>
                <DatePicker
                  value={form.endAt}
                  format="MM/DD/YYYY"
                  style={{ width: "100%" }}
                  onChange={(value) => updatePayrollItemForm(kind, { endAt: value })}
                />
              </label>
            </div>
          </div>

          <div className="payroll-adjustment-group">
            <label className="payroll-adjustment-description">
              <span>Notes</span>
              <Input.TextArea
                value={form.description}
                rows={2}
                placeholder="Optional"
                onChange={(event) => updatePayrollItemForm(kind, { description: event.target.value })}
              />
            </label>
          </div>
        </div>
        <div className="payroll-payrate-actions">
          <Text type="secondary">{title} can apply to everyone in this workspace or only to one staff member.</Text>
          <Button
            className="payroll-save-payrate-button"
            type="primary"
            loading={savingPayrollItemKind === kind}
            onClick={() => savePayrollItem(kind)}
          >
            Save {noun}
          </Button>
        </div>
      </>
    );
  };

  const payrollAdjustmentPanel = (kind) => {
    const rows = payrollItems[kind] || [];
    const noun = kind === "EARNING" ? "earning" : "deduction";
    const title = kind === "EARNING" ? "Earnings" : "Deductions";
    return (
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <Card className="payroll-pay-staff-card" title={<Text strong className="payroll-section-title">{title}</Text>}>
          <div className="payroll-adjustment-list-header">
            <Text type="secondary">Manage {noun}s for everyone in this workspace or for one staff member.</Text>
            <Button className="payroll-blue-button" type="default" icon={<PlusOutlined />} onClick={() => openPayrollItemDrawer(kind)}>
              Add {noun}
            </Button>
          </div>
          {loadingPayrollItems ? (
            <Skeleton active paragraph={{ rows: 5 }} />
          ) : rows.length ? (
            <List
              className="payroll-adjustment-list"
              dataSource={rows}
              renderItem={(item) => (
                <List.Item key={item.id || `${item.name}-${item.createdAt}`}>
                  <div className="payroll-adjustment-row">
                    <div className="payroll-adjustment-row-main">
                      <Text strong>{item.name}</Text>
                      {item.code ? <Tag color="blue">{item.code}</Tag> : null}
                      <small>{item.description || "No notes"}</small>
                    </div>
                    <div className="payroll-adjustment-row-metrics">
                      <span>
                        <strong>{item.amountType === "PERCENT" ? `${Number(item.amount || 0)}%` : money(item.amount)}</strong>
                        <small>Amount</small>
                      </span>
                      <span>
                        <strong>{item.scope === "USER" ? item.userEmail || "Staff specific" : "Workspace"}</strong>
                        <small>Applies to</small>
                      </span>
                      <span>
                        <strong>
                          {kind === "EARNING"
                            ? item.taxable === false ? "Not taxable" : "Taxable"
                            : payrollDeductionTreatmentLabel(item)}
                        </strong>
                        <small>{kind === "EARNING" ? "Taxable" : "Tax timing"}</small>
                      </span>
                      <span>
                        <strong>{shortDateText(item.startAt)}{item.endAt ? ` - ${shortDateText(item.endAt)}` : ""}</strong>
                        <small>Dates</small>
                      </span>
                      <span>
                        <strong>{readable(item.status, "Status")}</strong>
                        <small>Status</small>
                      </span>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No ${noun}s added yet.`} />
          )}
        </Card>
      </Space>
    );
  };

  const activeWashingtonLiDescription = activeWashingtonLiConfig ? (
    <div className="payroll-active-schedule-details">
      <span>The newest active WA L&I setup controls workers' compensation withholding for this workspace.</span>
      <span className="payroll-active-schedule-date-line">
        Employee-share L&I rate {money(activeWashingtonLiConfig.employeeHourlyRate || 0)} / hr
        {activeWashingtonLiConfig.riskClassCode ? ` | Risk class ${activeWashingtonLiConfig.riskClassCode}` : ""}
      </span>
      <span>
        {scheduleStartLabel(activeWashingtonLiConfig.startAt)} {shortDateText(activeWashingtonLiConfig.startAt)}
        {activeWashingtonLiConfig.endAt ? ` | Ends ${shortDateText(activeWashingtonLiConfig.endAt)}` : ""}
      </span>
    </div>
  ) : (
    "Add the employee-share hourly L&I rate from the AFH's Washington L&I rate notice."
  );

  const washingtonLiPanel = (
    <Space direction="vertical" size={14} style={{ width: "100%" }}>
      <Card className="payroll-pay-window-card" title={<Text strong className="payroll-section-title">WA L&I Workers' Compensation</Text>}>
        <div className="payroll-schedule-summary">
          <Alert
            type={activeWashingtonLiConfig ? "success" : "warning"}
            showIcon
            message={activeWashingtonLiConfig ? "WA L&I is configured" : "WA L&I is not configured"}
            description={activeWashingtonLiDescription}
          />
          <Space className="payroll-schedule-actions" size={8}>
            <Button
              className="payroll-info-icon-button"
              icon={<InfoCircleOutlined />}
              onClick={() => setWashingtonLiInfoOpen(true)}
              title="About WA L&I"
              aria-label="About WA L&I"
            />
            <Button className="payroll-blue-button payroll-schedule-change-trigger" type="default" onClick={openWashingtonLiDrawer}>
              {activeWashingtonLiConfig ? "Change WA L&I" : "Add WA L&I"}
            </Button>
          </Space>
        </div>
      </Card>

      <Card className="payroll-pay-staff-card" title={<Text strong className="payroll-section-title">WA L&I History</Text>}>
        {loadingWashingtonLiConfigs ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : washingtonLiConfigs.length ? (
          <List
            className="payroll-schedule-list"
            dataSource={washingtonLiConfigs}
            renderItem={(config) => {
              const status = String(config.status || "").toUpperCase();
              const detailValues = [
                { label: "Risk class", value: config.riskClassCode || "Not set" },
                { label: "Description", value: config.riskClassDescription || "Not set" },
                { label: "Employee-share rate", value: `${money(config.employeeHourlyRate || 0)} / hr` },
                { label: "Medical Aid rate", value: config.medicalAidRate ?? "Not set" },
                { label: "Stay at Work rate", value: config.stayAtWorkRate ?? "Not set" },
                { label: "Supplemental Pension rate", value: config.supplementalPensionRate ?? "Not set" },
                { label: "Status", value: readable(status, "Status") },
                { label: "Starts", value: shortDateText(config.startAt) },
                { label: "Ends", value: config.endAt ? shortDateText(config.endAt) : "No end date" },
                { label: "Notes", value: config.notes || "No notes" },
              ];
              return (
                <List.Item key={config.id || `${config.riskClassCode}-${config.startAt}`}>
                  <div className={`payroll-schedule-row payroll-li-row${status === "ACTIVE" ? " is-active" : " is-inactive"}`}>
                    <Space className="payroll-schedule-row-dates" size={6} wrap>
                      <Tag color="blue">{scheduleStartLabel(config.startAt)} {shortDateText(config.startAt)}</Tag>
                      {config.endAt ? <Tag color="default">Ends {shortDateText(config.endAt)}</Tag> : null}
                      <Tag color={status === "ACTIVE" ? "green" : "default"}>
                        {status === "ACTIVE" ? "Active" : "Inactive"}
                      </Tag>
                    </Space>
                    <div className="payroll-schedule-row-main">
                      <Text strong>{money(config.employeeHourlyRate || 0)} / hr employee-share L&I</Text>
                      <div className="payroll-li-values">
                        {detailValues.map((item) => (
                          <span key={item.label}>
                            <small>{item.label}</small>
                            <strong>{item.value}</strong>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </List.Item>
              );
            }}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No WA L&I configuration added yet." />
        )}
      </Card>
    </Space>
  );

  const payrollScheduleButtonLabel = activePayrollSchedule ? "Change Pay Schedule" : "Add Pay Schedule";
  const activePayrollScheduleDescription = activePayrollSchedule ? (
    <div className="payroll-active-schedule-details">
      <span>The newest active schedule controls payroll timing for this workspace.</span>
      <span className="payroll-active-schedule-date-line">
        {scheduleStartLabel(activePayrollSchedule.startAt)} {shortDateText(activePayrollSchedule.startAt)}
        {activePayrollSchedule.firstPayDate ? ` | Next pay date ${shortDateText(activePayrollSchedule.firstPayDate)}` : ""}
      </span>
      {activePayrollSchedule.description ? <span>{activePayrollSchedule.description}</span> : null}
    </div>
  ) : (
    "Set how often this workspace pays staff. Only one payment schedule can be active at a time."
  );
  const payrollScheduleFormContent = (
    <div className="payroll-schedule-form">
      <div className="payroll-schedule-form-row payroll-schedule-form-row-two">
        <label>
          <span>Schedule</span>
          <Select
            value={payrollScheduleForm.frequency}
            options={payrollScheduleOptions}
            onChange={(value) => updatePayrollScheduleForm({ frequency: value })}
          />
        </label>
        <label>
          <span>Status</span>
          <Select
            value={payrollScheduleForm.status}
            options={[
              { value: "ACTIVE", label: "Active" },
              { value: "INACTIVE", label: "Inactive" },
            ]}
            onChange={(value) => updatePayrollScheduleForm({ status: value })}
          />
        </label>
      </div>
      <div className="payroll-schedule-form-row">
        <label>
          <span>Starts</span>
          <DatePicker
            value={payrollScheduleForm.startAt}
            format="MM/DD/YYYY"
            style={{ width: "100%" }}
            onChange={(value) => updatePayrollScheduleForm({ startAt: value })}
          />
        </label>
      </div>
      <div className="payroll-schedule-form-row">
        <label>
          <span>Next pay date</span>
          <DatePicker
            value={payrollScheduleForm.firstPayDate}
            format="MM/DD/YYYY"
            disabled
            style={{ width: "100%" }}
          />
          <small>Calculated from the start date and schedule.</small>
        </label>
      </div>
      <label className="payroll-schedule-notes">
        <span>Notes</span>
        <Input.TextArea
          value={payrollScheduleForm.description}
          rows={2}
          placeholder="Optional schedule notes"
          onChange={(event) => updatePayrollScheduleForm({ description: event.target.value })}
        />
      </label>
      <div className="payroll-schedule-action">
        <Button
          className={`payroll-save-payrate-button${activePayrollSchedule ? " payroll-change-schedule-button" : ""}`}
          type="primary"
          block
          loading={savingPayrollSchedule}
          onClick={savePayrollSchedule}
        >
          {payrollScheduleButtonLabel}
        </Button>
      </div>
    </div>
  );
  const payrollSchedulePanel = (
    <Space direction="vertical" size={14} style={{ width: "100%" }}>
      <Card className="payroll-pay-window-card" title={<Text strong className="payroll-section-title">Payment Schedule</Text>}>
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <div className="payroll-schedule-summary">
            <Alert
              type={activePayrollSchedule ? "success" : "info"}
              showIcon
              message={activePayrollSchedule ? `${readable(activePayrollSchedule.frequency, "Schedule")} is active` : "No active payment schedule"}
              description={activePayrollScheduleDescription}
            />
            <Button
              className={`payroll-blue-button payroll-schedule-change-trigger${activePayrollSchedule ? " payroll-change-schedule-button" : ""}`}
              type="default"
              onClick={openPayrollScheduleDrawer}
            >
              {payrollScheduleButtonLabel}
            </Button>
          </div>
        </Space>
      </Card>

      <Card className="payroll-pay-staff-card" title={<Text strong className="payroll-section-title">Schedule History</Text>}>
        {loadingPayrollSchedules ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : payrollSchedules.length ? (
          <List
            className="payroll-schedule-list"
            dataSource={payrollSchedules}
            renderItem={(schedule) => (
              <List.Item key={schedule.id || `${schedule.frequency}-${schedule.startAt}`}>
                <div className={`payroll-schedule-row${schedule.status === "ACTIVE" ? " is-active" : " is-inactive"}`}>
                  <Space className="payroll-schedule-row-dates" size={6} wrap>
                    <Tag color="blue">{scheduleStartLabel(schedule.startAt)} {shortDateText(schedule.startAt)}</Tag>
                    {schedule.firstPayDate ? <Tag color="cyan">Next pay {shortDateText(schedule.firstPayDate)}</Tag> : null}
                  </Space>
                  <div className="payroll-schedule-row-main">
                    <Text strong>{readable(schedule.frequency, "Schedule")}</Text>
                    <small>{schedule.description || "No notes"}</small>
                  </div>
                  <Space size={6} wrap>
                    <Tag color={schedule.status === "ACTIVE" ? "green" : "default"}>
                      {schedule.status === "ACTIVE" ? "Active" : "Inactive"}
                    </Tag>
                  </Space>
                </div>
              </List.Item>
            )}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No payment schedules added yet." />
        )}
      </Card>
    </Space>
  );
  const paystubPanel = (
    <Space direction="vertical" size={14} style={{ width: "100%" }}>
      <Card
        className="payroll-pay-window-card"
        title={<Text strong className="payroll-section-title"><FileTextOutlined /> Paysturbs</Text>}
      >
        <div className="payroll-paystub-controls">
          <label>
            <span>Staff member</span>
            <Select
              showSearch
              value={paystubStaffUserId || undefined}
              placeholder="Select staff"
              options={staffUserOptions}
              optionFilterProp="label"
              onChange={setPaystubStaffUserId}
            />
          </label>
          <label>
            <span>Pay period range</span>
            <RangePicker
              value={payWindow}
              allowClear={false}
              onChange={(nextRange) => setPayWindow(nextRange || [dayjs().startOf("month"), dayjs().endOf("month")])}
            />
          </label>
        </div>
      </Card>

      <Card className="payroll-pay-staff-card" title={<Text strong className="payroll-section-title">Issued Paystubs</Text>}>
        {loadingDetails ? (
          <Skeleton active paragraph={{ rows: 5 }} />
        ) : paystubPaymentRows.length ? (
          <List
            className="payroll-pay-staff-list"
            dataSource={paystubPaymentRows}
            renderItem={(payment) => {
              const period = payrollPaymentPeriodFor(payment);
              const paystubRow = payrollPaymentToPaystubRow(payment);
              const isSelected = payment.id === selectedPaystubPayment?.id;
              return (
                <List.Item key={payment.id || payment.createdAt}>
                  <div className={`payroll-pay-staff-row payroll-paystub-record-row${isSelected ? " is-selected" : ""}`}>
                    <div className="payroll-pay-staff-person">
                      <Text strong>{firstText(payment.staffName, payment.userEmail, "Staff member")}</Text>
                      <div><Text type="secondary">{firstText(payment.staffEmail, payment.userEmail, "No email on record")}</Text></div>
                    </div>
                    <div className="payroll-pay-staff-metrics">
                      <span>
                        <strong>{shortDateText(period.start)} - {shortDateText(period.end)}</strong>
                        <small>Pay period</small>
                      </span>
                      <span>
                        <strong>{shortDateText(period.payDate)}</strong>
                        <small>Pay date</small>
                      </span>
                      <span>
                        <strong>{formatHours(paystubRow.approvedHours)}</strong>
                        <small>Approved hours</small>
                      </span>
                      <span>
                        <strong>{money(paystubRow.estimatedPay, payment.currency)}</strong>
                        <small>Net pay</small>
                      </span>
                    </div>
                    <Space wrap>
                      <Tag color={paymentStatusColor(paymentStatusText(payment))}>{readable(paymentStatusText(payment), "Status")}</Tag>
                      <Button
                        className="payroll-blue-button"
                        type={isSelected ? "primary" : "default"}
                        icon={<FileTextOutlined />}
                        loading={loadingPayrollPaymentDetail === payment.id}
                        onClick={() => openPaystubDetails(payment)}
                      >
                        Details
                      </Button>
                    </Space>
                  </div>
                </List.Item>
              );
            }}
          />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No issued payroll payment records found." />
        )}
      </Card>

      <Drawer
        className="payroll-staff-drawer payroll-paystub-drawer"
        extra={
          <Button
            className="payroll-blue-button"
            icon={<PrinterOutlined />}
            disabled={!selectedPaystubRow || loadingDetails}
            onClick={printPaystub}
          >
            Print Paystub
          </Button>
        }
        onClose={() => setPaystubDrawerOpen(false)}
        open={paystubDrawerOpen}
        title={<Text strong className="payroll-section-title">Paystub Details</Text>}
        width={980}
      >
        {!paystubPaymentRows.length ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No issued payroll payment records found." />
        ) : !selectedPaystubPeriod ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select an issued payroll payment to review a paystub." />
        ) : (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <div className="payroll-paystub-summary">
              <span>
                <small>Staff member</small>
                <strong>{staffNameFor(selectedPaystubRow.profile)}</strong>
                <em>{staffEmailFor(selectedPaystubRow.profile) || "No email on record"}</em>
              </span>
              <span>
                <small>Pay period</small>
                <strong>{shortDateText(selectedPaystubPeriod.start)} - {shortDateText(selectedPaystubPeriod.end)}</strong>
                <em>Pay date {shortDateText(selectedPaystubPeriod.payDate)}</em>
              </span>
              <span>
                <small>Approved hours</small>
                <strong>{formatHours(paystubApprovedHours)}</strong>
                <em>{paystubTimesheetsForSelected.length} approved timesheet{paystubTimesheetsForSelected.length === 1 ? "" : "s"}</em>
              </span>
            </div>
            {loadingDetails ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : selectedPaystubRow ? (
              <PaystubDetailsCard
                payment={selectedPaystubPayment}
                row={selectedPaystubRow}
                period={{
                  start: shortDateText(selectedPaystubPeriod.start),
                  end: shortDateText(selectedPaystubPeriod.end),
                  payDate: shortDateText(selectedPaystubPeriod.payDate),
                }}
                employer={selectedPaystubEmployer}
                ytdTotals={selectedPaystubYtdTotals}
                staffName={staffNameFor(selectedPaystubRow.profile)}
                staffEmail={staffEmailFor(selectedPaystubRow.profile)}
                staffPhone={phoneFor(selectedPaystubRow.profile)}
                employeeAddress={paystubEmployeeAddressFor(selectedPaystubRow)}
                employerName={paystubEmployerNameFor(selectedPaystubPayment, { employer: selectedPaystubEmployer })}
                employerAddress={paystubEmployerAddressFor(selectedPaystubPayment, { employer: selectedPaystubEmployer })}
                employerContact={[selectedPaystubEmployer.email, selectedPaystubEmployer.phone].map(clean).filter(Boolean).join(" | ")}
                money={money}
                formatHours={formatHours}
                payRateDisplayParts={payRateDisplayParts}
                computedPayrollTaxLinesFor={computedPayrollTaxLinesFor}
                computedPayrollTaxAmountFor={computedPayrollTaxAmountFor}
                hasAnyPayRate={hasAnyPayRate}
                regularPayAmountFor={regularPayAmountFor}
                overtimePayAmountFor={overtimePayAmountFor}
                payrollItemAmount={payrollItemAmount}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No approved timesheets found for this staff member and pay period." />
            )}
          </Space>
        )}
      </Drawer>
    </Space>
  );

  return (
    <CareShell className="dashboard-content project-page payroll-page my-payments-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon="cash-banknote"
          title="Payroll"
          subtitle="Manage staff payroll readiness, approved hours, issued payments, and payment methods."
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <button className="mini-btn primary" type="button" onClick={refresh} disabled={loadingStaff || loadingDetails}>
          <ReloadOutlined /> Refresh
        </button>
      </div>

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 14 }} /> : null}

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <Card style={workspaceStyles.softCard}>
            <Statistic title="Staff" value={staff.length} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={workspaceStyles.softCard}>
            <Statistic title="Approved Hours" value={formatHours(approvedHours)} prefix={<ClockCircleOutlined />} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={workspaceStyles.softCard}>
            <Statistic title="Issued Total" value={money(issuedTotal, issuedPayments[0]?.currency || "USD")} prefix={<DollarOutlined />} />
          </Card>
        </Col>
        <Col xs={24} md={6}>
          <Card style={workspaceStyles.softCard}>
            <div className="payroll-schedule-stat">
              <small>Current Pay Schedule</small>
              <strong>{activePayrollSchedule ? `${readable(activePayrollSchedule.frequency, "Schedule")} active` : "Not set"}</strong>
              <span className="payroll-active-schedule-date-line">
                {activePayrollSchedule
                  ? `${scheduleStartLabel(activePayrollSchedule.startAt)} ${shortDateText(activePayrollSchedule.startAt)}${
                      activePayrollSchedule.firstPayDate ? ` | Next pay date ${shortDateText(activePayrollSchedule.firstPayDate)}` : ""
                    }`
                  : "Add a schedule to control payroll timing."}
              </span>
            </div>
          </Card>
        </Col>
      </Row>

      <Tabs
        className="my-payments-tabs"
        activeKey={activeTab}
        onChange={changeTab}
        items={[
          { key: "overview", label: "Overview" },
          { key: "approved-timesheets", label: "Approved Timesheets" },
          { key: "issued-payments", label: "Issued Payments" },
          { key: "pay-staff", label: "Pay Staff" },
          { key: "payment-schedules", label: "Payment Schedules" },
          { key: "wa-lni", label: "WA L&I" },
          { key: "paysturbs", label: "Paysturbs" },
          { key: "earnings", label: "Earnings" },
          { key: "deductions", label: "Deductions" },
          { key: "tax-remittances", label: "Tax Remittances" },
          {
            key: "attention",
            label: (
              <span className="payroll-attention-tab-label">
                Attention
                {payrollAttentionItems.length ? <Badge count={payrollAttentionItems.length} size="small" /> : null}
              </span>
            ),
          },
        ]}
      />

      {activeTab === "overview" ? (
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <Card title={<Text strong className="payroll-section-title">OCare Payroll</Text>}>
            {!selectedWorkspaceId ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a care workspace to manage payroll." />
            ) : (
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <Alert
                  type="info"
                  showIcon
                  message="Payroll for adult family home care teams"
                  description="OCare Payroll helps this workspace prepare staff payments from approved timesheets, pay schedules, staff pay rates, deductions, taxes, and payment records."
                />
                <Row gutter={[12, 12]}>
                  <Col xs={24} md={8}>
                    <Card size="small" className="payroll-drawer-section-card">
                      <Text strong className="payroll-section-title">Prepare payroll</Text>
                      <p className="payroll-overview-copy">
                        Review approved hours, apply regular and overtime rates, and calculate earnings, deductions, and payroll taxes for each pay window.
                      </p>
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card size="small" className="payroll-drawer-section-card">
                      <Text strong className="payroll-section-title">Pay and record</Text>
                      <p className="payroll-overview-copy">
                        Mark outside payments as paid or use Pay With OCare to charge the workspace and send net pay to the employee's linked payout method.
                      </p>
                    </Card>
                  </Col>
                  <Col xs={24} md={8}>
                    <Card size="small" className="payroll-drawer-section-card">
                      <Text strong className="payroll-section-title">Keep paystubs</Text>
                      <p className="payroll-overview-copy">
                        Issued payroll payment records create paystub details, year-to-date totals, proof documents, and payment history for audit-ready payroll records.
                      </p>
                    </Card>
                  </Col>
                </Row>
              </Space>
            )}
          </Card>
        </Space>
      ) : null}

      {activeTab === "tax-remittances" ? (
        <Row gutter={[14, 14]}>
          <Col xs={24} lg={7}>
            <Card
              className="payroll-tax-remittance-list-card"
              title={<Text strong className="payroll-section-title">Taxes to remit</Text>}
            >
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                {taxRemittanceRows.map((tax) => (
                  <button
                    className={`payroll-tax-remittance-option${selectedTaxRemittance?.code === tax.code ? " is-active" : ""}`}
                    key={tax.code}
                    type="button"
                    onClick={() => setSelectedTaxRemittanceCode(tax.code)}
                  >
                    <span>
                      <strong>{tax.label}</strong>
                      <small>{tax.agency}</small>
                    </span>
                    <em>
                      <strong>{money(tax.outstanding)}</strong>
                      <small>Next {shortDateText(tax.nextExpectedRemitDate)}</small>
                    </em>
                  </button>
                ))}
              </Space>
            </Card>
          </Col>
          <Col xs={24} lg={17}>
            <Space direction="vertical" size={14} style={{ width: "100%" }}>
              <Card
                title={<Text strong className="payroll-section-title">{selectedTaxRemittance?.label || "Tax remittance"}</Text>}
                extra={(
                  <Button
                    className="payroll-blue-button"
                    disabled={selectedTaxRemittance?.isAllRemittances}
                    onClick={openTaxRemittanceDrawer}
                  >
                    Record a Remittance
                  </Button>
                )}
              >
                {selectedTaxRemittance ? (
                  <Space direction="vertical" size={14} style={{ width: "100%" }}>
                    <Alert
                      type={selectedTaxRemittance.outstanding > 0 ? "warning" : "success"}
                      showIcon
                      message={`${selectedTaxRemittance.agency} remittance tracking`}
                      description={selectedTaxRemittance.due}
                    />
                    <div className="payroll-tax-remittance-summary">
                      <span>
                        <small>Current accumulated</small>
                        <strong>{money(selectedTaxRemittance.accumulated)}</strong>
                      </span>
                      <span>
                        <small>Previously remitted</small>
                        <strong>{money(selectedTaxRemittance.remitted)}</strong>
                      </span>
                      <span className={selectedTaxRemittance.outstanding > 0 ? "is-due" : ""}>
                        <small>Remaining to remit</small>
                        <strong>{money(selectedTaxRemittance.outstanding)}</strong>
                      </span>
                      <span>
                        <small>Payroll records</small>
                        <strong>{selectedTaxRemittance.paymentCount}</strong>
                      </span>
                      <span>
                        <small>Next expected remit</small>
                        <strong>{shortDateText(selectedTaxRemittance.nextExpectedRemitDate)}</strong>
                      </span>
                    </div>
                  </Space>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a tax to review remittance details." />
                )}
              </Card>

              <Card
                loading={loadingTaxRemittances}
                title={<Text strong className="payroll-section-title">Previous remittances</Text>}
                extra={(
                  <RangePicker
                    allowClear
                    className="payroll-tax-remittance-range"
                    format="MM/DD/YYYY"
                    value={taxRemittanceHistoryRange}
                    onChange={(value) => setTaxRemittanceHistoryRange(value || [])}
                  />
                )}
              >
                {filteredTaxRemittanceHistory.length ? (
                  <List
                    dataSource={filteredTaxRemittanceHistory}
                    renderItem={(record) => (
                      <List.Item>
                        <div className="payroll-tax-remittance-history-row">
                          <span>
                            <strong>{money(taxRemittanceAmountForRecord(record), record.currency || "USD")}</strong>
                            <small>
                              {selectedTaxRemittance?.isAllRemittances
                                ? `${taxRemittanceNameForRecord(record)} | ${dateText(record.remittedAt || record.paymentDate || record.createdAt)}`
                                : dateText(record.remittedAt || record.paymentDate || record.createdAt)}
                            </small>
                          </span>
                          <span>
                            <small>Method</small>
                            <strong>{readable(record.paymentMethod, "Payment method")}</strong>
                          </span>
                          <span>
                            <small>Reference</small>
                            <strong>{record.reference || record.confirmationNumber || "Not recorded"}</strong>
                          </span>
                          <span>
                            <small>Notes</small>
                            <strong>{record.notes || "No notes"}</strong>
                          </span>
                        </div>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={selectedTaxRemittanceHistory.length ? "No remittances found in this date range." : "No remittances recorded for this tax yet."} />
                )}
              </Card>
            </Space>
          </Col>
        </Row>
      ) : null}

      {activeTab === "attention" ? (
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <Card title={<Text strong className="payroll-section-title">Payroll Attention</Text>}>
            <Alert
              type={payrollAttentionItems.length ? "warning" : "success"}
              showIcon
              message={payrollAttentionItems.length ? `${payrollAttentionItems.length} setup item${payrollAttentionItems.length === 1 ? "" : "s"} need attention` : "Payroll setup looks ready"}
              description={
                payrollAttentionItems.length
                  ? "Complete these setup items before running payroll so pay periods, taxes, deductions, and Washington payroll calculations are handled correctly."
                  : "The main payroll configuration items are active for this workspace."
              }
            />
          </Card>
          <Card title={<Text strong className="payroll-section-title">Missing configuration</Text>}>
            {payrollAttentionItems.length ? (
              <List
                dataSource={payrollAttentionItems}
                renderItem={(item) => (
                  <List.Item>
                    <div className="payroll-attention-row">
                      <div>
                        <Space size={8} wrap>
                          <Tag color="orange">Needs setup</Tag>
                          <Text strong>{item.title}</Text>
                        </Space>
                        <p>{item.description}</p>
                      </div>
                      <Button className="payroll-blue-button" onClick={() => changeTab(item.tab)}>
                        {item.actionLabel}
                      </Button>
                    </div>
                  </List.Item>
                )}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No missing payroll configuration found." />
            )}
          </Card>
        </Space>
      ) : null}

      {activeTab === "approved-timesheets" ? (
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          {staffSelector}
          <Card className="payroll-pay-window-card" size="small">
            <Row gutter={[12, 12]} align="middle">
              <Col xs={24} lg={9}>
                <Text strong className="payroll-section-title">Pay range</Text>
                <Select
                  allowClear
                  placeholder={activePayrollPeriodOptions.length ? "Select pay period" : "No payment schedule periods yet"}
                  value={selectedActivePayrollPeriodValue}
                  options={activePayrollPeriodOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  onChange={(value) => {
                    const option = activePayrollPeriodOptions.find((item) => item.value === value);
                    if (option) setPayWindow([option.start, option.end]);
                  }}
                  style={{ width: "100%", marginTop: 8 }}
                />
              </Col>
              <Col xs={24} lg={9}>
                <Text strong className="payroll-section-title">Custom dates</Text>
                <RangePicker
                  value={payWindow}
                  format="MM/DD/YYYY"
                  onChange={(nextRange) => setPayWindow(nextRange || [dayjs().startOf("month"), dayjs().endOf("month")])}
                  style={{ width: "100%", marginTop: 8 }}
                />
                <Text type="secondary" style={{ display: "block", marginTop: 6 }}>
                  {selectedPayWindowPeriod?.frequency
                    ? `${readable(selectedPayWindowPeriod.frequency, "Schedule")} schedule | Pay date ${shortDateText(selectedPayWindowPeriod.payDate)}`
                    : `Selected dates | Pay date ${shortDateText(selectedPayWindowPeriod?.payDate)}`}
                </Text>
              </Col>
              <Col xs={12} lg={3}>
                <Statistic title="Approved hours" value={formatHours(approvedHours)} />
              </Col>
              <Col xs={12} lg={3}>
                <Statistic title="Entries" value={approvedTimesheetsInPayWindow.length} />
              </Col>
            </Row>
          </Card>
          <Card title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Approved Timesheets</Text>}>
            {timesheetList}
          </Card>
        </Space>
      ) : null}

      {activeTab === "issued-payments" ? (
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          {staffSelector}
          <Card className="payroll-pay-window-card" size="small">
            <Row gutter={[12, 12]} align="middle">
              <Col xs={24} lg={9}>
                <Text strong className="payroll-section-title">Pay range</Text>
                <Select
                  allowClear
                  placeholder={activePayrollPeriodOptions.length ? "Select pay period" : "No payment schedule periods yet"}
                  value={selectedActivePayrollPeriodValue}
                  options={activePayrollPeriodOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  onChange={(value) => {
                    const option = activePayrollPeriodOptions.find((item) => item.value === value);
                    if (option) setPayWindow([option.start, option.end]);
                  }}
                  style={{ width: "100%", marginTop: 8 }}
                />
              </Col>
              <Col xs={24} lg={9}>
                <Text strong className="payroll-section-title">Custom dates</Text>
                <RangePicker
                  value={payWindow}
                  format="MM/DD/YYYY"
                  onChange={(nextRange) => setPayWindow(nextRange || [dayjs().startOf("month"), dayjs().endOf("month")])}
                  style={{ width: "100%", marginTop: 8 }}
                />
                <Text type="secondary" style={{ display: "block", marginTop: 6 }}>
                  {selectedPayWindowPeriod?.frequency
                    ? `${readable(selectedPayWindowPeriod.frequency, "Schedule")} schedule | Pay date ${shortDateText(selectedPayWindowPeriod.payDate)}`
                    : `Selected dates | Pay date ${shortDateText(selectedPayWindowPeriod?.payDate)}`}
                </Text>
              </Col>
              <Col xs={12} lg={3}>
                <Statistic title="Issued total" value={money(issuedPayWindowTotal, issuedPaymentsInPayWindow[0]?.currency || "USD")} />
              </Col>
              <Col xs={12} lg={3}>
                <Statistic title="Records" value={issuedPaymentsInPayWindow.length} />
              </Col>
            </Row>
          </Card>
          <Card title={<Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>Issued Payments</Text>}>
            {paymentList}
          </Card>
        </Space>
      ) : null}

      {activeTab === "pay-staff" ? (
        <Space direction="vertical" size={14} style={{ width: "100%" }}>
          <Card className="payroll-pay-window-card" size="small">
            <Row gutter={[12, 12]} align="middle">
              <Col xs={24} lg={12}>
                <Text strong className="payroll-section-title">Pay window</Text>
                <RangePicker
                  value={payWindow}
                  format="MM/DD/YYYY"
                  onChange={(nextRange) => setPayWindow(nextRange || [dayjs().startOf("month"), dayjs().endOf("month")])}
                  style={{ width: "100%", marginTop: 8 }}
                />
                <Text type="secondary" style={{ display: "block", marginTop: 6 }}>
                  {selectedPayWindowPeriod?.frequency
                    ? `${readable(selectedPayWindowPeriod.frequency, "Schedule")} schedule | Pay date ${shortDateText(selectedPayWindowPeriod.payDate)}`
                    : `Selected dates | Pay date ${shortDateText(selectedPayWindowPeriod?.payDate)}`}
                </Text>
              </Col>
              <Col xs={12} lg={4}>
                <Statistic title="Staff selected" value={includedPayStaffIds.length} />
              </Col>
              <Col xs={12} lg={4}>
                <Statistic title="Estimated total" value={money(includedPayTotal)} />
              </Col>
              <Col xs={24} lg={4}>
                <Button className="payroll-blue-button" block onClick={loadPayStaff} loading={loadingPayStaff}>
                  Refresh
                </Button>
              </Col>
            </Row>
          </Card>
          <Card className="payroll-pay-staff-card" title={<Text strong className="payroll-section-title">Pay Staff</Text>}>
            {payStaffList}
          </Card>
        </Space>
      ) : null}

      {activeTab === "payment-schedules" ? payrollSchedulePanel : null}

      {activeTab === "wa-lni" ? washingtonLiPanel : null}

      {activeTab === "paysturbs" ? paystubPanel : null}

      {activeTab === "earnings" ? payrollAdjustmentPanel("EARNING") : null}

      {activeTab === "deductions" ? payrollAdjustmentPanel("DEDUCTION") : null}

      {issuedPaymentDrawer ? (() => {
        const record = issuedPaymentDrawer;
        const details = objectValue(record.paymentDetails);
        const employee = objectValue(details.employee);
        const employer = objectValue(details.employer);
        const payPeriod = objectValue(details.payPeriod || record.payPeriod);
        const hours = objectValue(details.hours);
        const payRates = objectValue(details.payRates);
        const totals = objectValue(details.totals);
        const earnings = arrayValue(details.earnings);
        const deductions = arrayValue(details.deductions);
        const timesheets = arrayValue(details.timesheets);
        const ytdTotals = payrollPaymentYtdTotalsFor(record);
        const paymentDocuments = issuedPaymentDocuments;
        const viewablePaymentDocuments = paymentDocuments.filter((document) => payrollDocumentResolvedUrl(document));
        const signatureFileId = firstText(
          record.acknowledgementSignatureFileId,
          details.acknowledgementSignatureFileId,
          details.signatureDocument?.fileId,
          details.signature?.fileId,
        );
        const currency = record.currency || details.currency || "USD";
        const grossPay = record.grossPay ?? totals.grossPay ?? totals.basePay ?? paymentAmount(record);
        const totalEarnings = record.totalEarnings ?? totals.totalEarnings ?? grossPay;
        const totalDeductions = record.totalDeductions ?? totals.totalDeductions;
        const netPay = record.netPay ?? totals.netPay ?? paymentAmount(record);
        const periodStart = firstText(payPeriod.periodStart, payPeriod.start, payPeriod.startsAt, record.periodStart);
        const periodEnd = firstText(payPeriod.periodEnd, payPeriod.end, payPeriod.endsAt, record.periodEnd);
        const actualPayDate = payrollPaymentActualPayDateFor(record);
        const staffName = firstText(employee.name, record.staffName, record.userName, paymentTitle(record));
        const staffEmail = firstText(employee.email, record.staffEmail, record.userEmail);
        const employerName = firstText(employer.name, record.workspaceName, selectedWorkspace?.name);
        const recordStatus = paymentRecordStatusText(record);
        const payoutStatus = payoutStatusText(record);
        const payoutReference = payoutReferenceText(record);
        const rateSummary = firstText(
          payRates.label,
          [
            payRates.regular !== undefined && payRates.regular !== null ? `Regular - ${money(payRates.regular, currency)} / hr` : "",
            payRates.overtime !== undefined && payRates.overtime !== null ? `Overtime - ${money(payRates.overtime, currency)} / hr` : "",
          ].filter(Boolean).join(" | "),
        );
        return (
          <Drawer
            className="payroll-staff-drawer"
            title={
              <div className="payroll-staff-drawer-title">
                <span className="round-icon blue"><FileTextOutlined /></span>
                <div>
                  <strong>{staffName}</strong>
                  <small>{staffEmail || "No email on record"}</small>
                </div>
                <Tag color="blue">
                  {shortDateText(periodStart)} - {shortDateText(periodEnd)}
                </Tag>
              </div>
            }
            open={Boolean(issuedPaymentDrawer)}
            width={1040}
            onClose={() => {
              setIssuedPaymentDrawer(null);
              setPayrollDocumentDialog(null);
            }}
            destroyOnClose
          >
            <Space className="payroll-staff-drawer-body" direction="vertical" size={10} style={{ width: "100%" }}>
              <div className="payroll-payment-status-strip">
                <span>
                  <small>Payment status</small>
                  <Tag color={paymentStatusColor(recordStatus)}>{readable(recordStatus, "Status")}</Tag>
                </span>
                <span>
                  <small>Payout status</small>
                  <Tag color={paymentStatusColor(payoutStatus)}>{readable(payoutStatus, "Not started")}</Tag>
                </span>
                <span>
                  <small>Updated</small>
                  <strong>{dateText(record.updatedAt || record.createdAt)}</strong>
                </span>
              </div>
              <Card className="payroll-paystub-card" size="small">
                <div className="payroll-paystub-party-grid">
                  <section>
                    <Text type="secondary">Employer</Text>
                    <h3>{employerName || "Organization"}</h3>
                    <p>{employer.address || "No organization address on record"}</p>
                    <p>
                      {[employer.email, employer.phone]
                        .map(clean)
                        .filter(Boolean)
                        .join(" | ") || "No contact on record"}
                    </p>
                  </section>
                  <section>
                    <Text type="secondary">Employee</Text>
                    <h3>{staffName}</h3>
                    <p>{employee.address || "No address on record"}</p>
                    <p>
                      {[staffEmail, employee.phone]
                        .map(clean)
                        .filter(Boolean)
                        .join(" | ") || "No contact on record"}
                    </p>
                  </section>
                </div>
                <div className="payroll-paystub-meta">
                  <span><small>Approved hours</small><strong>{formatHours(hours.approved)}</strong></span>
                  <span><small>Regular</small><strong>{formatHours(hours.regular)}</strong></span>
                  <span><small>Overtime</small><strong>{formatHours(hours.overtime)}</strong></span>
                  <span><small>Pay rate</small><strong>{rateSummary || "Not set"}</strong></span>
                  <span><small>Payroll taxes</small><strong>{money(deductions.filter((item) => item.computed || isStatutoryPayrollTax(item)).reduce((sum, item) => sum + Number(item.amount || 0), 0), currency)}</strong></span>
                  <span className="payroll-paystub-net"><small>Net pay</small><strong>{money(netPay, currency)}</strong></span>
                </div>

                <div className="payroll-paystub-columns">
                  <div className="payroll-paystub-table">
                    <div className="payroll-paystub-table-title">Earnings</div>
                    {earnings.length ? earnings.map((item, index) => (
                      <div className="payroll-paystub-line" key={item.id || item.name || index}>
                        <span>{item.name || item.description || "Earning"}</span>
                        <em>
                          {item.hours !== undefined && item.hours !== null ? `${formatHours(item.hours)} ` : ""}
                          {item.rate !== undefined && item.rate !== null ? `x ${money(item.rate, currency)}` : ""}
                        </em>
                        <strong>{money(item.amount, currency)}</strong>
                      </div>
                    )) : (
                      <div className="payroll-paystub-line">
                        <span>Gross pay</span>
                        <em />
                        <strong>{money(grossPay, currency)}</strong>
                      </div>
                    )}
                    <div className="payroll-paystub-line is-total">
                      <span>Total earnings</span>
                      <em />
                      <strong>{money(totalEarnings, currency)}</strong>
                    </div>
                    <div className="payroll-paystub-line is-total">
                      <span>Gross pay</span>
                      <em />
                      <strong>{money(grossPay, currency)}</strong>
                    </div>
                  </div>

                  <div className="payroll-paystub-table">
                    <div className="payroll-paystub-table-title">Taxes and deductions</div>
                    {deductions.length ? deductions.map((item, index) => (
                      <div className="payroll-paystub-line" key={item.id || item.name || index}>
                        <span>{item.name || item.description || "Deduction"}</span>
                        <em />
                        <strong>{money(item.amount, currency)}</strong>
                      </div>
                    )) : (
                      <div className="payroll-paystub-line">
                        <span>No deductions on record</span>
                        <em />
                        <strong>{money(0, currency)}</strong>
                      </div>
                    )}
                    <div className="payroll-paystub-line is-total">
                      <span>Total deductions and taxes</span>
                      <em />
                      <strong>{money(totalDeductions, currency)}</strong>
                    </div>
                  </div>
                </div>
                <div className="payroll-paystub-bottom-summary">
                  <span>
                    <small>Gross pay</small>
                    <strong>{money(grossPay, currency)}</strong>
                  </span>
                  <span>
                    <small>Net pay</small>
                    <strong>{money(netPay, currency)}</strong>
                  </span>
                </div>
                {Object.keys(ytdTotals).length ? (
                  <div className="payroll-paystub-meta payroll-paystub-ytd-summary">
                    <span><small>YTD gross pay</small><strong>{money(ytdTotals.grossPay, currency)}</strong></span>
                    <span><small>YTD net pay</small><strong>{money(ytdTotals.netPay, currency)}</strong></span>
                    <span><small>YTD deductions</small><strong>{money(ytdTotals.totalDeductions, currency)}</strong></span>
                    <span><small>YTD hours</small><strong>{formatHours(ytdTotals.approvedHours)}</strong></span>
                  </div>
                ) : null}
              </Card>

              <Card className="payroll-drawer-section-card" size="small" title={<Text strong className="payroll-section-title">Payment details</Text>}>
                <div className="payroll-paynow-method-grid">
                  <span><small>Status</small><strong>{readable(recordStatus, "Status")}</strong><em>{dateText(record.confirmedAt || record.createdAt)}</em></span>
                  <span><small>Pay date</small><strong>{shortDateText(actualPayDate || record.createdAt)}</strong><em>{actualPayDate ? "Actual payment date" : "Recorded date"}</em></span>
                  <span><small>Payout status</small><strong>{readable(payoutStatus, "Not started")}</strong><em>{payoutReference || "No payout reference"}</em></span>
                  <span><small>Payment type</small><strong>{readable(record.paymentType, "Payment")}</strong><em>{record.paymentReference || "No reference on record"}</em></span>
                  <span><small>Payment method</small><strong>{record.paymentMethod || details.method || "Not recorded"}</strong><em>{details.details || "No extra details"}</em></span>
                  <span><small>Documents</small><strong>{viewablePaymentDocuments.length} document{viewablePaymentDocuments.length === 1 ? "" : "s"}</strong><em>{signatureFileId ? "Signature attached" : "No signature file"}</em></span>
                </div>
                <div className="payroll-document-thumbnail-strip">
                  {viewablePaymentDocuments.length ? viewablePaymentDocuments.map((document, index) => {
                    const documentUrl = payrollDocumentResolvedUrl(document);
                    const documentName = payrollDocumentName(document, document.fallbackName || `Document ${index + 1}`);
                    const isImage = isPayrollImageDocument(document, documentUrl);
                    return (
                      <button
                        type="button"
                        className="payroll-document-thumbnail"
                        key={payrollDocumentFileId(document) || documentUrl || `${documentName}-${index}`}
                        aria-disabled={!documentUrl}
                        disabled={!documentUrl}
                        aria-label={`View ${documentName}`}
                        onClick={() => {
                          if (!documentUrl) return;
                          setPayrollDocumentDialog({
                            document,
                            documentName,
                            documentUrl,
                            isImage,
                          });
                        }}
                      >
                        <span className="payroll-document-thumbnail-preview">
                          {documentUrl && isImage ? (
                            <img src={documentUrl} alt={documentName} />
                          ) : (
                            <FileTextOutlined />
                          )}
                        </span>
                        <strong>{document.kind || "Document"}</strong>
                        <em>{documentName}</em>
                        {documentUrl ? <small>View document</small> : null}
                      </button>
                    );
                  }) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No documents saved with this payment." />
                  )}
                </div>
              </Card>

              <Card className="payroll-drawer-section-card" size="small" title={<Text strong className="payroll-section-title">Approved timesheets</Text>}>
                {timesheets.length ? (
                  <List
                    className="payroll-drawer-timesheet-list"
                    dataSource={timesheets}
                    renderItem={(item, index) => (
                      <List.Item key={item.id || index}>
                        <div className="payroll-drawer-timesheet-row">
                          <Space wrap size={[10, 6]} style={{ flex: 1 }}>
                            <Text>{shortDateText(item.date || item.start)}</Text>
                            <Text type="secondary">{dateText(item.start)} - {dateText(item.end)}</Text>
                            {arrayValue(item.payCodes).length ? arrayValue(item.payCodes).map((code) => (
                              <Tag color="blue" key={code}>{readable(code, code)}</Tag>
                            )) : <Tag color="blue">Regular</Tag>}
                            {item.reviewReason ? <Text type="secondary">Review reason: {item.reviewReason}</Text> : null}
                          </Space>
                          <Text strong style={{ color: OLIVEEB_COLORS.oliveebBlue }}>{formatHours(item.hours)}</Text>
                        </div>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No approved timesheets saved with this payment." />
                )}
              </Card>
            </Space>
          </Drawer>
        );
      })() : null}

      <Modal
        title={payrollDocumentDialog?.documentName || "Payment document"}
        open={Boolean(payrollDocumentDialog)}
        onCancel={() => setPayrollDocumentDialog(null)}
        footer={null}
        width={900}
        destroyOnClose
      >
        {payrollDocumentDialog ? (
          <div className="payroll-document-dialog-body">
            <div className="payroll-document-dialog-preview">
              {payrollDocumentDialog.documentUrl ? (
                payrollDocumentDialog.isImage ? (
                  <img
                    src={payrollDocumentDialog.documentUrl}
                    alt={payrollDocumentDialog.documentName || "Payment document"}
                  />
                ) : (
                  <iframe
                    src={payrollDocumentDialog.documentUrl}
                    title={payrollDocumentDialog.documentName || "Payment document"}
                  />
                )
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>

      <Drawer
        className="payroll-staff-drawer"
        title={
          <div className="payroll-staff-drawer-title">
            <span className="round-icon blue"><DollarOutlined /></span>
            <div>
              <strong>Record a Remittance</strong>
              <small>{selectedTaxRemittance?.label || "Payroll tax"}</small>
            </div>
          </div>
        }
        open={taxRemittanceDrawerOpen}
        width={560}
        onClose={() => setTaxRemittanceDrawerOpen(false)}
        destroyOnClose
      >
        <Space className="payroll-staff-drawer-body" direction="vertical" size={12} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message={selectedTaxRemittance?.agency || "Tax agency"}
            description={`Current remaining amount: ${money(selectedTaxRemittance?.outstanding || 0)}`}
          />
          <Card className="payroll-drawer-section-card" size="small" title={<Text strong className="payroll-section-title">Remittance details</Text>}>
            <div className="payroll-remittance-form-grid">
              <label>
                <span>Amount</span>
                <InputNumber
                  min={0}
                  precision={2}
                  prefix="$"
                  value={selectedTaxRemittance?.outstanding || 0}
                  disabled
                  style={{ width: "100%" }}
                />
              </label>
              <label>
                <span>Remitted date</span>
                <DatePicker
                  format="MM/DD/YYYY"
                  value={taxRemittanceForm.remittedAt}
                  onChange={(value) => setTaxRemittanceForm((current) => ({ ...current, remittedAt: value || dayjs() }))}
                  style={{ width: "100%" }}
                />
              </label>
              <label>
                <span>Payment method</span>
                <Select
                  value={taxRemittanceForm.paymentMethod}
                  options={[
                    { value: "EFT", label: "EFT" },
                    { value: "CHECK", label: "Check" },
                    { value: "WIRE", label: "Wire" },
                    { value: "OTHER", label: "Other" },
                  ]}
                  onChange={(value) => setTaxRemittanceForm((current) => ({ ...current, paymentMethod: value }))}
                />
              </label>
              <label>
                <span>Reference</span>
                <Input
                  value={taxRemittanceForm.reference}
                  onChange={(event) => setTaxRemittanceForm((current) => ({ ...current, reference: event.target.value }))}
                  placeholder="Confirmation or check number"
                />
              </label>
              <label className="is-wide">
                <span>Notes</span>
                <Input.TextArea
                  rows={4}
                  value={taxRemittanceForm.notes}
                  onChange={(event) => setTaxRemittanceForm((current) => ({ ...current, notes: event.target.value }))}
                  placeholder="Optional notes"
                />
              </label>
              <label className="is-wide">
                <span>Documents</span>
                <Upload
                  beforeUpload={beforeManualPaymentProofUpload}
                  fileList={taxRemittanceForm.proofFiles}
                  multiple
                  onChange={({ fileList }) => setTaxRemittanceForm((current) => ({ ...current, proofFiles: fileList }))}
                >
                  <Button icon={<UploadOutlined />}>Attach documents</Button>
                </Upload>
              </label>
            </div>
          </Card>
          <Button
            className="payroll-green-action-button"
            loading={savingTaxRemittance}
            onClick={submitTaxRemittance}
            type="primary"
            block
          >
            Save Remittance
          </Button>
        </Space>
      </Drawer>

      <Drawer
        className="payroll-staff-drawer"
        title={
          <div className="payroll-staff-drawer-title">
            <span className="round-icon blue"><i className="ti ti-calendar-dollar" /></span>
            <div>
              <strong>{payrollScheduleButtonLabel}</strong>
              <small>{selectedWorkspace?.name || "Current workspace"}</small>
            </div>
          </div>
        }
        open={payrollScheduleDrawerOpen}
        width={560}
        onClose={() => setPayrollScheduleDrawerOpen(false)}
        destroyOnClose
      >
        <Space className="payroll-staff-drawer-body" direction="vertical" size={10} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="Only one active schedule"
            description="When you save a new active schedule, the previous active schedule becomes inactive."
          />
          <Card className="payroll-drawer-section-card" size="small" title={<Text strong className="payroll-section-title">Schedule details</Text>}>
            {payrollScheduleFormContent}
          </Card>
        </Space>
      </Drawer>

      <Modal
        title="About WA L&I"
        open={washingtonLiInfoOpen}
        onCancel={() => setWashingtonLiInfoOpen(false)}
        footer={[
          <Button key="close" className="payroll-blue-button" onClick={() => setWashingtonLiInfoOpen(false)}>
            Got it
          </Button>,
        ]}
        width={760}
      >
        <Row gutter={[14, 14]}>
          <Col xs={24} md={8}>
            <Card size="small" className="payroll-drawer-section-card">
              <Text strong className="payroll-section-title">What this is</Text>
              <p className="payroll-overview-copy">
                Washington L&I workers' compensation rates help OCare calculate the employee-share payroll deduction for staff working in Washington.
              </p>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" className="payroll-drawer-section-card">
              <Text strong className="payroll-section-title">Where rates come from</Text>
              <p className="payroll-overview-copy">
                Use the workspace's Washington L&I rate notice. Enter the risk class and the Medical Aid, Stay at Work, and Supplemental Pension rates shown there.
              </p>
            </Card>
          </Col>
          <Col xs={24} md={8}>
            <Card size="small" className="payroll-drawer-section-card">
              <Text strong className="payroll-section-title">How OCare uses it</Text>
              <p className="payroll-overview-copy">
                The active setup is used during payroll to calculate WA Workers' Compensation Tax from the employee-share hourly rate and approved hours.
              </p>
            </Card>
          </Col>
        </Row>
      </Modal>

      <Drawer
        className="payroll-staff-drawer"
        title={
          <div className="payroll-staff-drawer-title">
            <span className="round-icon blue"><i className="ti ti-shield-dollar" /></span>
            <div>
              <strong>{activeWashingtonLiConfig ? "Change WA L&I" : "Add WA L&I"}</strong>
              <small>{selectedWorkspace?.name || "Current workspace"}</small>
            </div>
          </div>
        }
        open={washingtonLiDrawerOpen}
        width={620}
        onClose={() => setWashingtonLiDrawerOpen(false)}
        destroyOnClose
      >
        <Space className="payroll-staff-drawer-body" direction="vertical" size={10} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="Configure workspace WA L&I"
            description="Enter the employee-share hourly workers' compensation rate from the employer's Washington L&I rate notice. A new active setup makes the previous active setup inactive."
          />
          <Card className="payroll-drawer-section-card" size="small" title={<Text strong className="payroll-section-title">WA L&I details</Text>}>
            <div className="payroll-schedule-form">
              <div className="payroll-schedule-form-row payroll-schedule-form-row-two">
                <label>
                  <span>Risk class code</span>
                  <Input
                    value={washingtonLiForm.riskClassCode}
                    placeholder="Optional"
                    onChange={(event) => updateWashingtonLiForm({ riskClassCode: event.target.value })}
                  />
                </label>
                <label>
                  <span>Status</span>
                  <Select
                    value={washingtonLiForm.status}
                    options={[
                      { value: "ACTIVE", label: "Active" },
                      { value: "INACTIVE", label: "Inactive" },
                    ]}
                    onChange={(value) => updateWashingtonLiForm({ status: value })}
                  />
                </label>
              </div>
              <div className="payroll-schedule-form-row">
                <label>
                  <span>Risk class description</span>
                  <Input
                    value={washingtonLiForm.riskClassDescription}
                    placeholder="Optional description from L&I notice"
                    onChange={(event) => updateWashingtonLiForm({ riskClassDescription: event.target.value })}
                  />
                </label>
              </div>
              <div className="payroll-schedule-form-row payroll-schedule-form-row-two">
                <label>
                  <span>Medical Aid rate</span>
                  <InputNumber
                    min={0}
                    precision={5}
                    value={washingtonLiForm.medicalAidRate}
                    style={{ width: "100%" }}
                    onChange={(value) => updateWashingtonLiForm({ medicalAidRate: value })}
                  />
                </label>
                <label>
                  <span>Stay at Work rate</span>
                  <InputNumber
                    min={0}
                    precision={5}
                    value={washingtonLiForm.stayAtWorkRate}
                    style={{ width: "100%" }}
                    onChange={(value) => updateWashingtonLiForm({ stayAtWorkRate: value })}
                  />
                </label>
              </div>
              <div className="payroll-schedule-form-row payroll-schedule-form-row-two">
                <label>
                  <span>Supplemental Pension rate</span>
                  <InputNumber
                    min={0}
                    precision={5}
                    value={washingtonLiForm.supplementalPensionRate}
                    style={{ width: "100%" }}
                    onChange={(value) => updateWashingtonLiForm({ supplementalPensionRate: value })}
                  />
                </label>
                <label>
                  <span>Employee-share L&I rate per hour</span>
                  <InputNumber
                    min={0}
                    precision={5}
                    value={washingtonLiForm.employeeHourlyRate}
                    prefix="$"
                    style={{ width: "100%" }}
                    onChange={(value) => updateWashingtonLiForm({ employeeHourlyRate: value })}
                  />
                  <small>Uses half of Medical Aid + Stay at Work + Supplemental Pension when those values are entered.</small>
                </label>
              </div>
              <div className="payroll-schedule-form-row payroll-schedule-form-row-two">
                <label>
                  <span>Starts</span>
                  <DatePicker
                    value={washingtonLiForm.startAt}
                    format="MM/DD/YYYY"
                    style={{ width: "100%" }}
                    onChange={(value) => updateWashingtonLiForm({ startAt: value })}
                  />
                </label>
                <label>
                  <span>Ends</span>
                  <DatePicker
                    value={washingtonLiForm.endAt}
                    format="MM/DD/YYYY"
                    style={{ width: "100%" }}
                    onChange={(value) => updateWashingtonLiForm({ endAt: value })}
                  />
                </label>
              </div>
              <label className="payroll-schedule-notes">
                <span>Notes</span>
                <Input.TextArea
                  value={washingtonLiForm.notes}
                  rows={2}
                  placeholder="Optional notes from the L&I rate notice"
                  onChange={(event) => updateWashingtonLiForm({ notes: event.target.value })}
                />
              </label>
              <div className="payroll-schedule-action">
                <Button
                  className="payroll-save-payrate-button"
                  type="primary"
                  block
                  loading={savingWashingtonLiConfig}
                  onClick={saveWashingtonLiConfig}
                >
                  Save WA L&I
                </Button>
              </div>
            </div>
          </Card>
        </Space>
      </Drawer>

      <Drawer
        className="payroll-staff-drawer"
        title={
          <div className="payroll-staff-drawer-title">
            <span className="round-icon blue"><i className={payrollItemDrawerKind === "DEDUCTION" ? "ti ti-receipt-refund" : "ti ti-cash-plus"} /></span>
            <div>
              <strong>Add {payrollItemDrawerKind === "DEDUCTION" ? "Deduction" : "Earning"}</strong>
              <small>{selectedWorkspace?.name || "Current workspace"}</small>
            </div>
          </div>
        }
        open={Boolean(payrollItemDrawerKind)}
        width={680}
        onClose={() => setPayrollItemDrawerKind("")}
        destroyOnClose
      >
        {payrollItemDrawerKind ? (
          <Space className="payroll-staff-drawer-body" direction="vertical" size={10} style={{ width: "100%" }}>
            <Alert
              type="info"
              showIcon
              message={payrollItemDrawerKind === "DEDUCTION" ? "Add payroll deduction" : "Add payroll earning"}
              description="This item can apply to the whole workspace or only one staff member."
            />
            <Card
              className="payroll-drawer-section-card"
              size="small"
              title={<Text strong className="payroll-section-title">{payrollItemDrawerKind === "DEDUCTION" ? "Deduction details" : "Earning details"}</Text>}
            >
              {payrollAdjustmentForm(payrollItemDrawerKind)}
            </Card>
          </Space>
        ) : null}
      </Drawer>

      <Drawer
        className="payroll-staff-drawer"
        title={
          payDrawer ? (
            <div className="payroll-staff-drawer-title">
              <span className="round-icon blue"><i className="ti ti-cash" /></span>
              <div>
                <strong>{staffNameFor(payDrawer.profile)}</strong>
                <small>{staffEmailFor(payDrawer.profile) || "No email on record"}</small>
              </div>
              <Tag color="blue">
                {shortDateText(payDrawerPeriod.start)} - {shortDateText(payDrawerPeriod.end)}
              </Tag>
            </div>
          ) : "Staff payment details"
        }
        open={Boolean(payDrawer)}
        width={1040}
        onClose={() => setPayDrawer(null)}
        destroyOnClose
      >
        {payDrawer ? (
          <Space className="payroll-staff-drawer-body" direction="vertical" size={10} style={{ width: "100%" }}>
            <Card className="payroll-drawer-section-card payroll-payment-action-card" size="small" title={<Text strong className="payroll-section-title">Payment actions</Text>}>
              <div className="payroll-payment-actions">
                <div>
                  <Text strong>Pay this employee</Text>
                  <p>Pay With OCare charges the workspace payment method and sends funds to the employee's linked default payout method.</p>
                </div>
                <Space wrap>
                  <Button
                    className="payroll-blue-button"
                    type="primary"
                    icon={<SendOutlined />}
                    loading={initiatingPayment}
                    disabled={!hasAnyPayRate(payDrawer)}
                    onClick={openPayNowDialog}
                  >
                    Pay With OCare
                  </Button>
                  <Button
                    className="payroll-save-payrate-button"
                    type="primary"
                    disabled={!hasAnyPayRate(payDrawer)}
                    onClick={openManualPaymentDrawer}
                  >
                    Pay Outside OCare
                  </Button>
                </Space>
              </div>
            </Card>
            <Card className="payroll-paystub-card" size="small">
              <div className="payroll-paystub-party-grid">
                <section>
                  <Text type="secondary">Employer</Text>
                  <h3>{workspaceNameFor(paystubEmployer)}</h3>
                  <p>
                    {loadingWorkspaceOrganization
                      ? "Loading organization address..."
                      : workspaceAddressFor(paystubEmployer) || "No organization address on record"}
                  </p>
                  <p>
                    {[workspaceEmailFor(paystubEmployer), workspacePhoneFor(paystubEmployer)]
                      .map(clean)
                      .filter(Boolean)
                      .join(" | ") || "No contact on record"}
                  </p>
                </section>
                <section>
                  <Text type="secondary">Employee</Text>
                  <h3>{staffNameFor(payDrawer.profile)}</h3>
                  <p>{employeePaystubAddressFor(payDrawer.profile) || "No address on record"}</p>
                  <p>
                    {[staffEmailFor(payDrawer.profile), phoneFor(payDrawer.profile)]
                      .map(clean)
                      .filter(Boolean)
                      .join(" | ") || "No contact on record"}
                  </p>
                </section>
              </div>
              <Alert
                type={payDrawer.washingtonTaxEvaluation?.isWashington ? "success" : "warning"}
                showIcon
                style={{ marginTop: 12, marginBottom: 12 }}
                message={`Temporary WA payroll tax state check: ${payDrawer.washingtonTaxEvaluation?.isWashington ? "Washington matched" : "Washington not matched"}`}
                description={
                  <div className="payroll-wa-check-details">
                    <div className="payroll-wa-check-copy">
                      <div>
                        <strong>Matched:</strong>{" "}
                        {payDrawer.washingtonTaxEvaluation?.matched
                          ? `${payDrawer.washingtonTaxEvaluation.matched.label}: ${payDrawer.washingtonTaxEvaluation.matched.value}`
                          : "No WA state/address match found"}
                      </div>
                      <div>
                        <strong>Computed WA tax lines:</strong> {payDrawer.washingtonTaxEvaluation?.computedLineCount || 0}
                      </div>
                      <div>
                        <strong>Checked values:</strong>{" "}
                        {(payDrawer.washingtonTaxEvaluation?.candidates || [])
                          .map((item) => `${item.label}: ${item.value}`)
                          .join(" | ") || "No state/address values found"}
                      </div>
                    </div>
                    <div className="payroll-wa-check-payrate">
                      <small>Pay rate</small>
                      <strong className="payroll-payrate-value">
                        <span className="payroll-payrate-lines">
                          {payRateDisplayParts(payDrawer).map((label) => (
                            <em key={label}>{label}</em>
                          ))}
                        </span>
                        {payDrawer.staffId ? (
                          <button
                            className="payroll-payrate-edit"
                            type="button"
                            title="Edit pay rate"
                            aria-label={`Edit pay rate for ${staffNameFor(payDrawer.profile)}`}
                            onClick={() => openPayRateDrawer(payDrawer)}
                          >
                            <SyncOutlined />
                          </button>
                        ) : null}
                      </strong>
                    </div>
                  </div>
                }
              />
              <div className="payroll-paystub-meta">
                <span><small>Approved hours</small><strong>{formatHours(payDrawer.approvedHours)}</strong></span>
                <span><small>Regular</small><strong>{formatHours(payDrawer.regularHours)}</strong></span>
                <span><small>Overtime</small><strong>{formatHours(payDrawer.overtimeHours)}</strong></span>
                <span><small>Payroll taxes</small><strong>{money(computedPayrollTaxAmountFor(payDrawer))}</strong></span>
                <span className="payroll-paystub-net">
                  <small>Net pay</small>
                  <strong>{hasAnyPayRate(payDrawer) ? money(payDrawer.estimatedPay) : "-"}</strong>
                </span>
              </div>

              <div className="payroll-paystub-columns">
                <div className="payroll-paystub-table">
                  <div className="payroll-paystub-table-title">Earnings</div>
                  <div className="payroll-paystub-line">
                    <span>Regular pay</span>
                    <em>
                      {payDrawer.payRateType === "SALARY"
                        ? "Salary"
                        : `${formatHours(payDrawer.regularHours)} x ${money(payDrawer.hourlyRate)}`}
                    </em>
                    <strong>{money(regularPayAmountFor(payDrawer))}</strong>
                  </div>
                  {payDrawer.overtimeHours > 0 ? (
                    <div className="payroll-paystub-line">
                      <span>Overtime pay</span>
                      <em>{formatHours(payDrawer.overtimeHours)} x {money(payDrawer.overtimeRate)}</em>
                      <strong>{money(payDrawer.overtimeHours * payDrawer.overtimeRate)}</strong>
                    </div>
                  ) : null}
                  {payDrawer.activeEarnings?.map((item) => (
                    <div className="payroll-paystub-line" key={item.id || item.name}>
                      <span>{item.name}</span>
                      <em>{item.scope === "USER" ? "Staff only" : "All staff"} | {item.taxable === false ? "Not taxable" : "Taxable"}</em>
                      <strong>{money(payrollItemAmount(item, payDrawer.basePay))}</strong>
                    </div>
                  ))}
                  <div className="payroll-paystub-line is-total">
                    <span>Gross pay</span>
                    <em />
                    <strong>{money(payDrawer.grossPay)}</strong>
                  </div>
                </div>

                <div className="payroll-paystub-table">
                  <div className="payroll-paystub-table-title">Taxes and deductions</div>
                  {payDrawer.activeDeductions?.map((item) => (
                    <div className="payroll-paystub-line" key={item.id || item.name}>
                      <span>{item.name}</span>
                      <em />
                      <strong>{money(payrollItemAmount(item, payDrawer.grossPay))}</strong>
                    </div>
                  ))}
                  {computedPayrollTaxLinesFor(payDrawer).map((item) => (
                    <div className="payroll-paystub-line" key={item.id || item.code || item.name}>
                      <span>{item.name}</span>
                      <em />
                      <strong>{money(payrollItemAmount(item, payDrawer.grossPay))}</strong>
                    </div>
                  ))}
                  <div className="payroll-paystub-line">
                    <span>Federal income tax</span>
                    <em />
                    <strong>{money(payDrawer.federalTax?.amount || 0)}</strong>
                  </div>
                  <div className="payroll-paystub-line is-taxable">
                    <span>Taxable pay</span>
                    <em />
                    <strong>{money(payDrawer.taxablePay)}</strong>
                  </div>
                  <div className="payroll-paystub-line is-total">
                    <span>Total deductions and taxes</span>
                    <em />
                    <strong>{money((payDrawer.preTaxDeductionsAmount || 0) + (payDrawer.postTaxDeductionsAmount || 0) + computedPayrollTaxAmountFor(payDrawer) + (payDrawer.federalTax?.amount || 0))}</strong>
                  </div>
                </div>
              </div>
              <div className="payroll-paystub-bottom-summary">
                <span>
                  <small>Gross pay</small>
                  <strong>{money(payDrawer.grossPay)}</strong>
                </span>
                <span>
                  <small>Net pay</small>
                  <strong>{hasAnyPayRate(payDrawer) ? money(payDrawer.estimatedPay) : "-"}</strong>
                </span>
              </div>
            </Card>
            <Card className="payroll-drawer-section-card" size="small" title={<Text strong className="payroll-section-title">Payment method</Text>}>
              {payDrawer.paymentMethods.length ? (
                <List
                  className="payroll-drawer-method-list"
                  dataSource={payDrawer.paymentMethods}
                  renderItem={(method) => (
                    <List.Item key={methodId(method)}>
                      <div className="payroll-drawer-method-row">
                        <BankOutlined />
                        <div>
                          <Text strong>{methodLabel(method)}</Text>
                          <div>
                            <Tag color="blue">Incoming</Tag>
                            <Tag color="green">{readable(method.status, "Status")}</Tag>
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No incoming payment method found." />
              )}
            </Card>
            <Card className="payroll-drawer-section-card" size="small" title={<Text strong className="payroll-section-title">Staff address</Text>}>
              <div className="payroll-drawer-address">
                <i className="ti ti-map-pin" />
                <Text>{addressFor(payDrawer.profile) || "No address on record."}</Text>
              </div>
            </Card>
            <Card className="payroll-drawer-section-card" size="small" title={<Text strong className="payroll-section-title">Approved timesheets</Text>}>
              {payDrawerTimesheets.length ? (
                <List
                  className="payroll-drawer-timesheet-list"
                  dataSource={payDrawerTimesheets}
                  renderItem={(item) => (
                    <List.Item key={item.id || `${timesheetDate(item)}-${timesheetHours(item)}`}>
                      <div className="payroll-drawer-timesheet-row">
                        <Space wrap size={[10, 6]} style={{ flex: 1 }}>
                          <Text>{shortDateText(timesheetDate(item))}</Text>
                          <Text type="secondary">{dateText(timesheetStart(item))} - {dateText(timesheetEnd(item))}</Text>
                          {(timesheetPayCodes(item).length ? timesheetPayCodes(item) : ["Regular"]).map((code) => (
                            <Tag color="blue" key={code}>{code}</Tag>
                          ))}
                        </Space>
                        <Text strong style={{ color: OLIVEEB_COLORS.oliveebBlue }}>{formatHours(timesheetHours(item))}</Text>
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No approved timesheets in the current pay schedule period." />
              )}
            </Card>
          </Space>
        ) : null}
      </Drawer>

      <Modal
        title="Confirm Pay With OCare"
        open={payNowDialog.open}
        onCancel={closePayNowDialog}
        onOk={initiatePayNow}
        okText="Confirm Pay With OCare"
        cancelText="Cancel"
        confirmLoading={initiatingPayment}
        okButtonProps={{
          disabled: payNowDialog.loadingMethods || !selectedPayNowPayerMethod || !selectedPayNowPayeeMethod,
        }}
        width={720}
      >
        {payNowDialog.row ? (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <Alert
              type="info"
              showIcon
              message="Oliveeb Payments"
              description="The workspace payment method will be charged and the employee's linked payout method will receive the net payroll payment."
            />
            <div className="payroll-paynow-summary">
              <span>
                <small>Employee</small>
                <strong>{staffNameFor(payNowDialog.row.profile)}</strong>
                <em>{staffEmailFor(payNowDialog.row.profile) || "No email on record"}</em>
              </span>
              <span>
                <small>Net pay</small>
                <strong>{money(payNowDialog.row.estimatedPay || 0)}</strong>
                <em>{formatHours(payNowDialog.row.approvedHours || 0)} approved hours</em>
              </span>
            </div>
            <Card size="small" className="payroll-drawer-section-card" title={<Text strong className="payroll-section-title">Payment methods</Text>}>
              <div className="payroll-paynow-method-grid">
                <span>
                  <small>Workspace default payment method</small>
                  <strong>{payNowDialog.loadingMethods ? "Loading..." : selectedPayNowPayerMethod ? methodLabel(selectedPayNowPayerMethod) : "No default method set"}</strong>
                  <em>{selectedPayNowPayerMethod ? "This default method will be charged." : "Set a default workspace payment method before using Pay With OCare."}</em>
                </span>
                <span className={selectedPayNowPayeeMethod ? "" : "is-error"}>
                  <small>Employee payout method</small>
                  <strong>{selectedPayNowPayeeMethod ? methodLabel(selectedPayNowPayeeMethod) : "Not linked"}</strong>
                  <em>{selectedPayNowPayeeMethod ? "Default incoming payout method" : "Employee must link a payout method first."}</em>
                </span>
              </div>
            </Card>
            <Card size="small" className="payroll-drawer-section-card" title={<Text strong className="payroll-section-title">Stripe and payment charges</Text>}>
              <div className="payroll-paynow-charge-lines">
                <span><small>Net pay charged</small><strong>{money(payNowChargeEstimate.principalAmount)}</strong></span>
                <span><small>Processor percentage fee</small><strong>{money(payNowChargeEstimate.paymentProcessorFee)}</strong><em>{payNowChargeEstimate.paymentProcessorFeeRate}%</em></span>
                <span><small>Processor fixed fee</small><strong>{money(payNowChargeEstimate.paymentFeeConstant)}</strong></span>
                <span><small>Total processor charges</small><strong>{money(payNowChargeEstimate.totalPaymentProcessorFee)}</strong></span>
                <span><small>Oliveeb processing fee</small><strong>{money(payNowChargeEstimate.oliveebProcessingFee)}</strong><em>{payNowChargeEstimate.oliveebProcessingFeeRate}%</em></span>
                <span className="is-total"><small>Total charged to workspace</small><strong>{money(payNowChargeEstimate.totalCharge)}</strong></span>
                <span className="is-total"><small>Employee receives</small><strong>{money(payNowChargeEstimate.netPay)}</strong></span>
              </div>
            </Card>
          </Space>
        ) : null}
      </Modal>

      <Drawer
        className="payroll-staff-drawer payroll-manual-payment-drawer"
        title={
          <div className="payroll-staff-drawer-title">
            <span className="round-icon blue"><BankOutlined /></span>
            <div>
              <strong>Pay Outside OCare</strong>
              <small>{payDrawer ? `${staffNameFor(payDrawer.profile)} - ${hasAnyPayRate(payDrawer) ? money(payDrawer.estimatedPay) : "-"}` : "Manual payroll payment"}</small>
            </div>
          </div>
        }
        open={manualPaymentDrawerOpen}
        width={620}
        afterOpenChange={(open) => {
          if (open) window.setTimeout(preparePayrollSignatureCanvas, 0);
        }}
        onClose={closeManualPaymentDrawer}
        destroyOnClose
      >
        <Space className="payroll-staff-drawer-body" direction="vertical" size={12} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="Record a manual payment"
            description="Use this when the employee has already been paid outside OCare, such as cash, cheque, wire, or another method."
          />
          <Card className="payroll-drawer-section-card" size="small" title={<Text strong className="payroll-section-title">Payment details</Text>}>
            <div className="payroll-manual-payment-form">
              <label>
                <FieldInfoLabel info="Choose how the employee was paid outside OCare. This is used for the payroll payment record.">
                  Payment method
                </FieldInfoLabel>
                <Select
                  value={manualPaymentForm.method}
                  options={MANUAL_PAYROLL_PAYMENT_METHODS}
                  onChange={(value) => updateManualPaymentForm({ method: value })}
                />
              </label>
              <label className="payroll-manual-payment-wide">
                <FieldInfoLabel info="Add the payment reference or note, such as cheque number, wire confirmation, cash note, Zelle note, or other details that help verify the payment.">
                  Details
                </FieldInfoLabel>
                <Input.TextArea
                  rows={4}
                  value={manualPaymentForm.details}
                  placeholder="Bank, Zelle, cheque number, wire reference, cash notes, confirmation number, or other payment details"
                  onChange={(event) => updateManualPaymentForm({ details: event.target.value })}
                />
              </label>
              <label className="payroll-manual-payment-wide">
                <FieldInfoLabel info="Attach optional evidence for the payment, such as cheque image, wire receipt, signed cash receipt, or other confirmation document.">
                  Proof documents
                </FieldInfoLabel>
                <Upload
                  multiple
                  beforeUpload={beforeManualPaymentProofUpload}
                  fileList={manualPaymentForm.proofFiles}
                  onChange={({ fileList }) => updateManualPaymentForm({ proofFiles: fileList })}
                >
                  <Button icon={<UploadOutlined />}>Upload proof</Button>
                </Upload>
              </label>
              <label className="payroll-manual-payment-wide">
                <FieldInfoLabel info="Enter the employee name that matches the person drawing the acknowledgement signature below.">
                  Employee signer name
                </FieldInfoLabel>
                <Input
                  value={manualPaymentForm.acknowledgementSignerName}
                  placeholder="Employee legal name"
                  onChange={(event) => updateManualPaymentForm({ acknowledgementSignerName: event.target.value })}
                />
              </label>
              <div className="payroll-manual-payment-wide ncp-signature-pad-block payroll-signature-pad-block">
                <div className="ncp-signature-pad-head">
                  <FieldInfoLabel info="The employee draws their signature to acknowledge this manual payment record. The signature image and audit details are saved with the payment.">
                    Employee acknowledgement signature
                  </FieldInfoLabel>
                  <button className="mini-btn" type="button" onClick={clearPayrollSignatureInk}>
                    <i className="ti ti-eraser" /> Clear
                  </button>
                </div>
                <div className="ncp-signature-pad-shell">
                  <canvas
                    aria-label="Draw employee acknowledgement signature"
                    className="ncp-signature-pad"
                    onPointerDown={startPayrollSignatureStroke}
                    onPointerLeave={endPayrollSignatureStroke}
                    onPointerMove={movePayrollSignatureStroke}
                    onPointerUp={endPayrollSignatureStroke}
                    ref={payrollSignatureCanvasRef}
                  />
                  <span className="ncp-signature-pad-line" />
                </div>
                <small className="payroll-signature-note">
                  The employee signs here to acknowledge the manual payment record.
                </small>
              </div>
            </div>
          </Card>
          <div className="payroll-manual-payment-actions">
            <Button onClick={closeManualPaymentDrawer}>Cancel</Button>
            <Button className="payroll-save-payrate-button" type="primary" loading={savingManualPayment} onClick={saveManualPayment}>
              Confirm Pay
            </Button>
          </div>
        </Space>
      </Drawer>

      <Drawer
        className="payroll-staff-drawer payroll-payrate-drawer"
        title={
          payRateDrawer ? (
            <div className="payroll-staff-drawer-title">
              <span className="round-icon blue"><SyncOutlined /></span>
              <div>
                <strong>Edit pay rate</strong>
                <small>{staffNameFor(payRateDrawer.profile)} - {staffEmailFor(payRateDrawer.profile) || "No email on record"}</small>
              </div>
            </div>
          ) : "Edit pay rate"
        }
        open={Boolean(payRateDrawer)}
        width={560}
        onClose={() => setPayRateDrawer(null)}
        destroyOnClose
      >
        {payRateDrawer ? (
          <Space className="payroll-staff-drawer-body" direction="vertical" size={16} style={{ width: "100%" }}>
            <Card className="payroll-drawer-summary-card payroll-payrate-current-card" size="small">
              <div>
                <Text type="secondary">Current pay rates</Text>
                <strong>{payRateDisplay(payRateDrawer)}</strong>
                <small>{payRateDrawer.payRate?.startAt ? `Started ${dateText(payRateDrawer.payRate.startAt)}` : "Add a rate to start payroll calculations."}</small>
              </div>
            </Card>

            <Card className="payroll-drawer-section-card" size="small" title={<Text strong className="payroll-section-title">New pay rate</Text>}>
              <div className="payroll-payrate-form">
                <label>
                  <span>Amount</span>
                  <InputNumber
                    min={0}
                    precision={2}
                    value={payRateForm.amount}
                    prefix="$"
                    style={{ width: "100%" }}
                    onChange={(value) => setPayRateForm((current) => ({ ...current, amount: value }))}
                  />
                </label>
                <label>
                  <span>Type</span>
                  <Select
                    value={payRateForm.type}
                    options={[
                      { value: "HOURLY", label: "Hourly" },
                      { value: "SALARY", label: "Salary" },
                    ]}
                    onChange={(value) => setPayRateForm((current) => ({ ...current, type: value }))}
                  />
                </label>
                <label>
                  <span>Rate for</span>
                  <Select
                    value={payRateForm.rateKind}
                    options={[
                      { value: "REGULAR", label: "Regular" },
                      { value: "OVERTIME", label: "Overtime" },
                    ]}
                    onChange={(value) => setPayRateForm((current) => ({ ...current, rateKind: value }))}
                  />
                </label>
                <label>
                  <span>Status</span>
                  <Select
                    value={payRateForm.status}
                    options={[
                      { value: "ACTIVE", label: "Active" },
                      { value: "INACTIVE", label: "Inactive" },
                    ]}
                    onChange={(value) => setPayRateForm((current) => ({ ...current, status: value }))}
                  />
                </label>
                <label>
                  <span>Start</span>
                  <DatePicker
                    showTime
                    value={payRateForm.startAt}
                    format="MM/DD/YYYY hh:mm A"
                    style={{ width: "100%" }}
                    onChange={(value) => setPayRateForm((current) => ({ ...current, startAt: value }))}
                  />
                </label>
              </div>
              <div className="payroll-payrate-actions">
                <Text type="secondary">Saving an active rate turns the previous active rate for the same rate type off.</Text>
                <Button className="payroll-save-payrate-button" type="primary" loading={savingPayRate} onClick={savePayRate}>
                  Save pay rate
                </Button>
              </div>
            </Card>

            <Card className="payroll-drawer-section-card" size="small" title={<Text strong className="payroll-section-title">Previous rates</Text>}>
              {loadingPayRates ? (
                <Skeleton active paragraph={{ rows: 4 }} />
              ) : payRateRows.length ? (
                <List
                  className="payroll-payrate-history-list"
                  dataSource={payRateRows}
                  renderItem={(rate) => {
                    const isCurrent = activePayRateFor(payRateRows, payRateKind(rate))?.id === rate.id;
                    return (
                      <List.Item key={rate.id || `${rate.amount}-${rate.startAt}`}>
                        <div className="payroll-payrate-history-row">
                          <div>
                            <Text strong>{payRateLabel(rate)}</Text>
                            <small>Started {dateText(rate.startAt)}</small>
                          </div>
                          <Space size={6} wrap>
                            <Tag color={payRateKind(rate) === "OVERTIME" ? "orange" : "green"}>{readable(payRateKind(rate), "Regular")}</Tag>
                            <Tag color={String(rate.type).toUpperCase() === "SALARY" ? "purple" : "blue"}>{readable(rate.type, "Hourly")}</Tag>
                            <Tag color={String(rate.status).toUpperCase() === "ACTIVE" ? "green" : "default"}>{readable(rate.status, "Status")}</Tag>
                            {isCurrent ? <Tag color="gold">Current</Tag> : null}
                          </Space>
                        </div>
                      </List.Item>
                    );
                  }}
                />
              ) : (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No pay rates recorded yet." />
              )}
            </Card>
          </Space>
        ) : null}
      </Drawer>
    </CareShell>
  );
}
